import { useState, useEffect, useRef } from "react";

// ─── Google Font ───────────────────────────────────────────────────────────────
const FontStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    ::-webkit-scrollbar { width: 4px; height: 4px; }
    ::-webkit-scrollbar-track { background: #080c14; }
    ::-webkit-scrollbar-thumb { background: #1e3058; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: #2a4070; }
    .acc-body { max-height: 0; overflow: hidden; transition: max-height 0.38s cubic-bezier(0.4,0,0.2,1); }
    .acc-body.open { max-height: 1200px; }
    .card-hover { transition: border-color 0.2s, box-shadow 0.2s; }
    .card-hover:hover { border-color: #2a5298 !important; box-shadow: 0 0 24px rgba(79,156,249,0.08); }
    .tag-chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; letter-spacing:0.3px; }
    .btn-primary { background: linear-gradient(135deg,#3b74d4,#4f9cf9); color:#fff; border:none; border-radius:10px; padding:10px 22px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; transition:opacity 0.2s,transform 0.1s; }
    .btn-primary:hover { opacity:0.88; transform:translateY(-1px); }
    .btn-primary:disabled { background:#1a2444; color:#3a4a6a; cursor:not-allowed; transform:none; }
    .glow-dot { width:8px; height:8px; border-radius:50%; background:#4f9cf9; box-shadow:0 0 10px #4f9cf9; display:inline-block; }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    .pulse { animation: pulse 2s infinite; }
    @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
    .fade-in { animation: fadeIn 0.3s ease forwards; }
    @keyframes lockShake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-6px)} 40%,80%{transform:translateX(6px)} }
    .lock-shake { animation: lockShake 0.4s ease; }
    @keyframes unlockPop { 0%{transform:scale(1)} 50%{transform:scale(1.15)} 100%{transform:scale(1)} }
    .unlock-pop { animation: unlockPop 0.4s cubic-bezier(0.34,1.56,0.64,1); }
    .pw-input { background:#0a1020; border:1.5px solid #1a2a44; border-radius:10px; color:#e8f0fe; padding:11px 16px; font-size:14px; font-family:inherit; outline:none; width:100%; transition:border-color 0.2s,box-shadow 0.2s; letter-spacing:2px; box-sizing:border-box; }
    .pw-input:focus { border-color:#4f9cf9; box-shadow:0 0 0 3px rgba(79,156,249,0.15); }
    .pw-input.error { border-color:#ef4444; box-shadow:0 0 0 3px rgba(239,68,68,0.15); }
    .step-item { display:flex; gap:10px; align-items:flex-start; padding:10px 14px; background:#080c14; border-radius:10px; border:1px solid #0f1a2a; margin-bottom:8px; }
    .submit-area { background:#0a1020; border:1.5px solid #1a2a44; border-radius:10px; padding:12px 14px; width:100%; font-size:12px; font-family:inherit; color:#c8d8f0; resize:none; outline:none; transition:border-color 0.2s; line-height:1.7; box-sizing:border-box; }
    .submit-area:focus { border-color:#4f9cf9; box-shadow:0 0 0 2px rgba(79,156,249,0.12); }
    .step-guide { max-height:0; overflow:hidden; transition:max-height 0.4s cubic-bezier(0.4,0,0.2,1); }
    .step-guide.open { max-height:1200px; }
  `}</style>
);

// ─── FULL CURRICULUM DATA ──────────────────────────────────────────────────────
const buildDay = (day, week, title, emoji, tags, theory, resources, exercises) => ({
  day, week, title, emoji, tags,
  theory: theory.map((s, i) => ({ ...s, id: `d${day}s${i}` })),
  resources,
  exercises,
});

const T = (title, content) => ({ title, content });
const R = (cat, cn, items) => ({ cat, cn, items });  // cn = china, items has {label,url,desc}
const E = (no, title, desc, steps=[], submitHint='') => ({ no, title, desc, steps, submitHint });

const CURRICULUM = [
  // ─── WEEK 1 ─────────────────────────────────────────────────────────────────
  buildDay(1,1,"AI全局认知 + ChatGPT入门","🧠",["大模型","Prompt","认知框架"],[
    T("大语言模型工作原理",`大语言模型（LLM）基于Transformer架构，通过自回归方式预测下一个Token。训练过程分三阶段：

① 预训练（Pre-training）：在万亿级Token文本上学习语言规律，建立世界知识
② 监督微调（SFT）：用人工标注的对话数据微调，使模型能遵循指令
③ RLHF（人类反馈强化学习）：用人类偏好排序数据进一步对齐，提升安全性和有用性

关键参数理解：
• 上下文窗口（Context Window）：模型一次能处理的最大Token数，GPT-4o为128K，Claude 3.5为200K
• 温度（Temperature）：0=确定性输出，1=高创意随机，建议创意任务0.7-0.9，事实任务0.1-0.3
• Top-P采样：从概率累积达到P%的词汇集合中采样，控制输出多样性`),
    T("Prompt五要素框架",`优质Prompt = 角色(Role) + 背景(Context) + 任务(Task) + 格式(Format) + 约束(Constraint)

【角色设定的心理学原理】
角色设定激活模型在训练数据中对应领域的"专家模式"，相当于告诉模型用哪个子空间生成输出。

【实战模板】
"你是[角色]。[背景信息]。请[具体任务]，以[格式要求]输出，注意[约束条件]。"

【质量对比】
❌ 低质量："帮我写文案"
✅ 高质量："你是专注美妆电商的资深文案师，我们是面向25-35岁女性的国货护肤品牌。请为主打玻尿酸原液的新品写一条小红书首图标题，要有冲击力，30字以内，不能用'绝了'等夸张词汇。"

效果差距：高质量Prompt的转化率通常比低质量高200-500%`),
    T("思维链（Chain-of-Thought）原理",`CoT（思维链）是让模型逐步推理而非直接输出答案的技术，由Google Brain 2022年提出，在复杂推理任务上准确率提升达45%。

【触发方式】
基础版："请一步一步思考..."
进阶版：提供2-3个思考示例，让模型模仿推理结构
零样本版："Let's think step by step"（英文效果更稳定）

【适用场景】
• 数学推导、逻辑分析：必须用CoT
• 多步骤决策、方案制定：强烈推荐
• 简单翻译、格式转换：不需要，会浪费Token

【Self-Consistency升级版】
对同一问题生成多个推理路径，取多数投票结果，准确率再提升15-25%`),
    T("国内AI工具生态全景",`2024年国内大模型生态已相当完善，无需翻墙即可完成大部分AI工作：

【文本大模型】
• 豆包（字节）：免费额度大，中文理解强，适合日常使用
• 通义千问（阿里）：支持超长文档，API价格低，开发者友好  
• 文心一言（百度）：国内最早商用，垂直场景（法律/医疗）较强
• Kimi（月之暗面）：128K超长上下文，适合长文档分析
• 智谱ChatGLM：学术背景强，代码能力较好

【选择原则】
日常写作→豆包；长文档→Kimi；代码→通义/智谱；创意→Claude（需梯子）`),
  ],[
    R("国内资源",true,[
      {label:"豆包AI（字节出品）",url:"https://www.doubao.com",desc:"最推荐新手首选，无需梯子，免费额度大"},
      {label:"Kimi（超长上下文）",url:"https://kimi.moonshot.cn",desc:"128K上下文，适合长文档分析处理"},
      {label:"通义千问（阿里云）",url:"https://tongyi.aliyun.com",desc:"开发者必备，API价格极实惠"},
      {label:"文心一言（百度）",url:"https://yiyan.baidu.com",desc:"国产最早商用大模型，垂直场景强"},
      {label:"智谱AI·GLM（清华系）",url:"https://chatglm.cn",desc:"学术背景强，代码推理能力好"},
      {label:"讯飞星火（科大讯飞）",url:"https://xinghuo.xfyun.cn",desc:"语音+文字双强，教育场景优势"},
      {label:"AI工具集导航（ai-bot.cn）",url:"https://ai-bot.cn",desc:"收录500+AI工具，按场景分类"},
      {label:"提示词中文指南",url:"https://www.promptingguide.ai/zh",desc:"最全中文Prompt工程学习站"},
      {label:"B站AI大模型教程合集",url:"https://www.bilibili.com/search?keyword=大模型+Prompt+教程",desc:"视频教程，直观易学"},
    ]),
    R("国际资源",false,[
      {label:"OpenAI Prompt Engineering官方指南",url:"https://platform.openai.com/docs/guides/prompt-engineering",desc:"官方权威，必读"},
      {label:"Anthropic Prompt Library",url:"https://docs.anthropic.com/en/prompt-library/library",desc:"高质量Prompt案例库"},
      {label:"Learn Prompting（英文社区教程）",url:"https://learnprompting.org",desc:"系统化Prompt学习路径"},
    ]),
  ],[
    E(1,"账号矩阵搭建","注册豆包+Kimi+通义千问三个账号，用同一个问题「请分析电商直播带货的5个核心成功要素」分别测试，对比三个平台的回答质量、深度和风格差异，制作对比表格。"),
    E(2,"五要素Prompt改写练习","收集3条你认为写得很差的Prompt（可以是自己之前写的或网上找的），用五要素框架逐一改写，分别测试改写前后的输出质量，量化分析改善程度（1-10分自评）。"),
    E(3,"CoT推理实战","选择一个真实业务决策场景（如：是否要开通直播带货/是否要换工作），用CoT Prompt让AI逐步分析利弊，要求AI在每步推理后给出置信度分数，最终给出建议。"),
    E(4,"角色Prompt实验室","测试同一任务在5种不同角色设定下的输出差异：① 不设角色 ② 通用助手 ③ 资深行业专家 ④ 批评者 ⑤ 苏格拉底式导师。记录每种角色的输出特点，总结角色设定的规律。"),
    E(5,"商业场景综合任务","模拟：你是某国产护肤品的内容运营，用AI完成一套完整的内容策划：受众分析→内容方向→3个选题→1篇完整文案→发布时间建议，全程记录每步用的Prompt和修改过程。"),
  ]),

  buildDay(2,1,"Midjourney图像生成入门","🎨",["Midjourney","AI绘图","商业图像"],[
    T("扩散模型工作原理",`Midjourney和Stable Diffusion都基于扩散模型（Diffusion Model）工作原理：

【前向过程（加噪）】
原始图像 → 逐步添加高斯噪声 → 最终变为纯随机噪声（共T=1000步）

【反向过程（去噪）】
纯随机噪声 → UNet神经网络预测并减去噪声 → 逐步还原图像
每步去噪都受到CLIP文本编码的引导（你输入的Prompt）

【CLIP Text Encoder的作用】
CLIP将你的文字Prompt映射为768维语义向量，通过Cross-Attention机制注入每步去噪过程，引导图像朝你想要的方向生成。

这就是为什么Prompt的语义越精准，生成的图像就越符合预期。`),
    T("MJ核心参数体系",`【版本参数】--v 6.1（当前最新，质量最高）
【比例参数】--ar 16:9（横版）/ 9:16（竖版）/ 1:1（方图）
【质量参数】--q 1（标准）/ 2（高质量，耗时×2）
【风格参数】--style raw（减少MJ美化，更忠实Prompt）
【随机种子】--seed 12345（固定结果可复现）
【混沌度】--chaos 0-100（越高结果越多样，默认0）
【风格化】--stylize 0-1000（越高越有MJ审美偏好，默认100）
【排除元素】--no text,watermark,logo

【高级参数】
--iw 0.5（图片权重，与参考图URL联用）
--tile（生成可无缝拼接的贴图素材）
--sref [URL]（风格参考，v6新功能，锁定视觉风格）
--cref [URL]（角色参考，保持同一角色外观一致）`),
    T("商业图像Prompt公式",`【通用公式】
主体描述 + 场景/背景 + 光线 + 风格 + 构图 + 技术参数

【电商产品图模板】
"[产品名称], [材质质感], white clean background, professional product photography, studio lighting, soft shadows, 8K resolution, commercial quality --ar 1:1 --v 6.1"

【营销人物图模板】
"Young [国籍] [性别], [场景], [情绪], natural light, candid lifestyle photography, [品牌色调] color palette --ar 4:5 --v 6.1"

【品牌视觉模板】
"[场景/概念], minimal design aesthetic, [主色系] color scheme, clean composition, [风格关键词], high-end brand visual --ar 16:9 --style raw --v 6.1"

【用ChatGPT优化Prompt的技巧】
"请将这个中文图像描述转化为Midjourney专业英文Prompt，保留所有视觉细节，补充光线/材质/构图术语：[你的需求]"`),
    T("权重控制与多概念融合",`【权重语法（v6支持）】
(element::weight)  → 例：(photorealistic::2)(illustration::0.5)
多概念融合：horse::2 wings::1 → 马的特征是翅膀的两倍权重

【负向权重】
--no bad anatomy, blurry, watermark, low quality
(unwanted element::-1)  → 负权重排除特定元素

【图片参考（Image Prompting）】
[image_url] [text_description] --iw 0.5
iw值范围0-3：越高图片影响越大，越低文字影响越大

【风格参考新功能】
--sref [style_image_url] --sw 100
sw值（Style Weight）控制风格迁移强度：0-1000

商业应用：用品牌历史图像作为sref，批量生成风格一致的新物料`),
  ],[
    R("国内资源",true,[
      {label:"即梦AI（国内最佳MJ替代）",url:"https://jimeng.jianying.com",desc:"字节出品，每日免费额度，中文友好"},
      {label:"LiblibAI模型社区",url:"https://www.liblib.art",desc:"国内最大SD模型库，大量免费资源"},
      {label:"吐司AI（在线生图）",url:"https://tusi.cn",desc:"无需GPU，浏览器直接用Stable Diffusion"},
      {label:"无界AI（免费在线生图）",url:"https://www.wujieai.com",desc:"免费额度充足，支持中文Prompt"},
      {label:"触站Huashi6（工作流社区）",url:"https://www.huashi6.com",desc:"ComfyUI工作流分享，中文讲解"},
      {label:"B站：秋叶SD整合包",url:"https://www.bilibili.com/search?keyword=秋叶SD整合包",desc:"国内最流行的SD一键安装方案"},
      {label:"B站：MJ关键词大全",url:"https://www.bilibili.com/search?keyword=midjourney+prompt大全",desc:"搜索关键词获取风格词汇表"},
    ]),
    R("国际资源",false,[
      {label:"Midjourney官方文档",url:"https://docs.midjourney.com",desc:"参数权威参考，必须收藏"},
      {label:"Civitai.com（全球最大SD社区）",url:"https://civitai.com",desc:"顶级模型+Prompt案例库"},
      {label:"PromptHero（顶级Prompt案例）",url:"https://prompthero.com",desc:"按效果浏览高质量Prompt"},
      {label:"MidLibrary（风格分类Prompt库）",url:"https://www.midlibrary.io",desc:"按艺术风格分类，查找灵感必备"},
    ]),
  ],[
    E(1,"参数矩阵实验","固定同一Prompt，系统测试--chaos(0/30/70)×--stylize(0/250/750)的9种组合，共生成9张图，制作参数效果对照表，总结每个参数对视觉风格的具体影响规律。"),
    E(2,"电商产品图专项","为一款真实或虚拟产品（如：北欧风陶瓷马克杯）生成5种不同场景的产品图：白底棚拍/咖啡馆桌面/木质书架/厨房台面/户外野餐，分析哪种场景转化效果最好。"),
    E(3,"--sref风格锁定测试","找一个品牌的官方视觉（如：MUJI/苹果/三顿半），用--sref引用其图片风格，为该品牌风格的新产品生成3张视觉素材，评估风格一致性（1-10分），记录哪些风格元素被成功迁移。"),
    E(4,"ChatGPT+MJ联动工作流","设计一个两步工作流：Step1：用ChatGPT将中文创意需求转化为专业MJ Prompt（要求GPT补充光线/材质/构图词汇）；Step2：在MJ中测试，将ChatGPT的Prompt和你自己直接写的Prompt做质量对比。"),
    E(5,"商业提案视觉包制作","模拟：你是品牌设计顾问，为一个新茶饮品牌制作视觉提案包：Logo意向图×2、门店氛围图×2、产品图×2，共6张图，整理为提案格式（含图片+风格说明），评估可商用程度。"),
  ]),

  buildDay(3,1,"即梦AI + LiblibAI模型平台","🌟",["即梦AI","LiblibAI","国产AIGC"],[
    T("LoRA微调技术原理",`LoRA（Low-Rank Adaptation）是2021年微软提出的参数高效微调技术，是SD生态最核心的模型技术：

【数学原理】
预训练权重矩阵W₀不变，旁路添加低秩分解矩阵：ΔW = BA
其中 B∈R^{d×r}，A∈R^{r×k}，秩r远小于d和k

【参数量对比】
全量微调：需要更新所有权重（如：7B参数 = 28GB内存）
LoRA微调：只训练BA两个小矩阵（r=8时参数量减少99.7%）
文件大小：一个LoRA文件通常只有10-200MB，而完整模型5-20GB

【实际效果】
同等计算资源下，LoRA可以训练：
• 特定风格（水墨/像素/油画）
• 特定人物（品牌代言人/IP角色）
• 特定产品（使产品在任意场景都保持外观一致）`),
    T("LiblibAI平台深度解析",`LiblibAI（哩布哩布）是国内最大的Stable Diffusion模型社区：

【模型分类理解】
• Checkpoint（基础模型）：决定整体风格基调，相当于"画风基因"
  - 写实类：Realistic Vision，Deliberate，ChilloutMix
  - 动漫类：CounterfeitXL，AnythingXL
  - 国风类：GuoFeng，InkPainting
• LoRA：风格增强插件，叠加在基础模型上
• VAE：决定色彩饱和度和细节清晰度
• Embedding（Textual Inversion）：通过特殊token记忆新概念

【下载与使用流程】
1. LiblibAI搜索模型 → 查看示例图和使用说明
2. 下载Checkpoint → 放入ComfyUI/WebUI的models/checkpoints/
3. 下载LoRA → 放入models/loras/
4. 在工作流中加载对应模型 → 调整权重（建议LoRA权重0.5-0.8）`),
    T("即梦AI功能全景与核心参数",`即梦AI是字节跳动出品的一体化AIGC平台：

【文生图核心参数】
• 生成比例：9:16（小红书/抖音）/ 1:1（微博）/ 3:4（通用竖版）
• 精细度：标准/高精细（高精细消耗2倍积分但细节更丰富）
• 风格预设：写实/动漫/插画/3D/水彩/油画（选对风格事半功倍）

【图生图关键参数】
• 参考强度：0.3（弱参考，更多AI创意）→ 0.8（强参考，保留原图结构）
• 最佳范围：0.5-0.65 适合「保留构图+改变风格」

【文生视频参数】
• 运镜：固定/推近/拉远/左移/右移/旋转
• 时长：5秒/10秒（10秒消耗更多积分）
• 画质：流畅（720p）/ 高清（1080p）

【负向提示词常用词】
低质量,模糊,变形,水印,文字,错误比例,分裂手指`),
    T("中文Prompt与英文Prompt的差异对比",`国内AI图像平台与MJ在Prompt语言上有本质差异：

【即梦AI】
✅ 原生支持中文，语义理解准确
✅ 中文风格描述词效果好（如"新中式/ins风/奶奶灰"）
❌ 专业摄影/技术词汇中文效果略弱于英文
建议：主体描述用中文+风格参数用英文混合

【MJ/SD+CLIP英文端】
✅ 英文摄影术语精准（bokeh/golden ratio/rule of thirds）
✅ 风格艺术家引用准确（Monet style/Bauhaus design）
❌ 中文直接输入效果不稳定

【最优实践】
中文内容需求 → ChatGPT翻译优化为专业英文Prompt → 
在MJ使用英文版 / 在即梦使用中英混合版
两个版本分别测试，选最优结果使用`),
  ],[
    R("国内资源",true,[
      {label:"即梦AI官网",url:"https://jimeng.jianying.com",desc:"每日免费额度，强烈推荐"},
      {label:"LiblibAI模型下载",url:"https://www.liblib.art",desc:"国内最大SD模型社区"},
      {label:"吐司AI（在线SD）",url:"https://tusi.cn",desc:"在线生图，不需要GPU"},
      {label:"造梦日记（AI绘画）",url:"https://zmrj.art",desc:"国内在线AI绘画，操作简单"},
      {label:"触站Huashi6（工作流）",url:"https://www.huashi6.com",desc:"ComfyUI工作流分享社区"},
      {label:"B站：LoRA训练教程",url:"https://www.bilibili.com/search?keyword=LoRA训练+SD",desc:"从零开始训练LoRA视频教程"},
      {label:"B站：LiblibAI使用教程",url:"https://www.bilibili.com/search?keyword=liblib+模型使用教程",desc:"搜索最新实战教程"},
    ]),
    R("国际资源",false,[
      {label:"Civitai（全球LoRA模型库）",url:"https://civitai.com",desc:"含详细LoRA使用说明和示例图"},
      {label:"SD WebUI GitHub文档",url:"https://github.com/AUTOMATIC1111/stable-diffusion-webui",desc:"WebUI官方文档，参数权威参考"},
      {label:"Hugging Face模型库",url:"https://huggingface.co/models?pipeline_tag=text-to-image",desc:"全球最大AI模型开放库"},
    ]),
  ],[
    E(1,"模型风格对比实验","在LiblibAI下载3个不同风格的Checkpoint（写实/动漫/国风各1个），用同一个人物描述Prompt分别测试，对比三个基础模型的风格特征，制作风格对比图表，分析适用商业场景。"),
    E(2,"LoRA权重调优","找1个人物LoRA+1个风格LoRA，在同一基础模型上叠加，测试以下权重组合的出图效果：(0.3+0.7)/(0.5+0.5)/(0.7+0.3)/(0.8+0.2)，分析权重比例如何影响最终风格，找到最佳平衡点。"),
    E(3,"即梦AI商业物料生产","为一个虚拟茶饮品牌生产一套营销物料：① 品牌主视觉（1:1）× 2张 ② 产品封面图（3:4）× 3张 ③ 活动背景图（16:9）× 1张。全程仅用即梦AI，记录总用时和消耗积分，计算商业成本效益。"),
    E(4,"图生图风格迁移链","选一张真实的产品照片 → 用即梦图生图以0.55参考强度生成3个不同风格版本（写实/插画/3D）→ 评估哪种风格最适合电商场景，并说明理由。模拟实际设计提案流程。"),
    E(5,"Prompt工程优化记录","为同一个场景（如：夜晚的北京胡同里的咖啡馆）创作5个递进优化版本的Prompt，每次增加1-2个新的描述维度（光线→材质→构图→氛围→技术参数），对比5个版本的生成质量提升曲线。"),
  ]),

  buildDay(4,1,"AI视频生成工具全景","🎬",["可灵AI","AI视频","文生视频"],[
    T("AI视频生成的技术路线",`当前主流AI视频生成技术分为三大路线：

【Diffusion-based视频模型】
代表：Sora（OpenAI）、可灵（快手）、即梦（字节）
原理：将视频扩散模型从图像扩展到时间维度，同时学习空间和时序的去噪过程
优势：高质量、自然运动；劣势：计算成本高、时长较短

【DiT（Diffusion Transformer）架构】
Sora采用此架构，将视频帧序列视为Patch序列，用Transformer处理时空关系
优势：理论上可生成任意时长视频，时序一致性更好

【当前能力边界】
• 最长生成：通常5-60秒，长视频仍有时序漂移问题
• 主体一致性：同一人物在长视频中外观容易改变
• 物理规律：水流/碰撞等物理效果仍不够真实
• 文字渲染：视频中生成准确文字是当前挑战`),
    T("文生视频vs图生视频工作流选择",`【文生视频（T2V）适用场景】
✅ 需要全新创建的场景素材（风景/抽象/概念）
✅ 测试不同视觉方向的快速原型
✅ 没有现成素材的新项目
❌ 需要精确控制主体外观的场景

【图生视频（I2V）适用场景】
✅ 已有精确产品图/人物图，需要动态化
✅ 电商产品展示（保证产品外观100%准确）
✅ 品牌代言人内容（保证人物外观一致）
✅ 制作"让图片动起来"的短视频效果

【最优工作流（商业级）】
AI图像（精确控制外观）→ 图生视频（添加运动）→ 视频后期（配音/字幕/音效）→ 多平台分发

实际数据：使用图生视频的电商视频，产品外观准确率比文生视频高约60%`),
    T("可灵AI参数体系与运镜控制",`【可灵核心模式】
① 标准模式：平衡质量与速度，适合大量测试
② 高性能模式：最高质量，适合最终输出
③ 首尾帧模式：上传起始帧+结束帧，AI自动补间（制作变身/转场效果）

【运镜指令词汇表】
推进：camera slowly zoom in / slowly push in
拉远：slowly pull back / camera zoom out
横移：camera pan left/right
升降：camera crane up/down
环绕：camera orbit around / 360 degree rotation
手持感：handheld camera movement / shaky cam effect

【文生视频Prompt结构】
主体描述 + 主体动作 + 场景环境 + 运镜指令 + 风格基调

示例：「一位身穿白色礼服的女性在玫瑰花丛中缓慢转身，玫瑰花瓣随风飘落，镜头缓慢环绕她周围一圈，梦幻唯美的电影风格，暖色调」`),
    T("AI视频商业应用场景与成本分析",`【高价值应用场景】
① 电商产品展示：静态图→动态展示，替代部分传统拍摄
  成本对比：传统拍摄约500-5000元/条 vs AI生成约10-50元/条
  适用：标品展示、颜色切换、多角度展示

② 短视频B-Roll素材：生成转场素材、场景填充、概念演绎
  适用：知识博主配图视频、企业宣传片场景填充

③ 数字营销广告：快速生成多个创意版本进行A/B测试
  适用：信息流广告素材测试，日出图量需求大的场景

④ 企业培训内容：场景演示、案例还原
  适用：内部培训、安全教育、流程演示

【当前局限与应对】
主体一致性差 → 使用首尾帧模式/图生视频
物理效果不真实 → 只用于场景和氛围素材，不依赖物理准确性
生成较慢 → 批量提交任务，等待时做其他工作`),
  ],[
    R("国内资源",true,[
      {label:"可灵AI（快手，国内最强）",url:"https://klingai.com",desc:"图生视频效果最佳，推荐优先使用"},
      {label:"即梦AI视频",url:"https://jimeng.jianying.com",desc:"图文视频一体，积分通用"},
      {label:"剪映专业版",url:"https://www.capcut.cn",desc:"AI视频后期全家桶，字幕/特效/配音"},
      {label:"腾讯智影（数字人+配音）",url:"https://zenvideo.qq.com",desc:"数字人+语音克隆，适合企业培训视频"},
      {label:"一帧秒创（文章转视频）",url:"https://aigc.yizhentv.com",desc:"文章一键转视频，运营效率神器"},
      {label:"万兴播爆（AI数字人）",url:"https://virbo.wondershare.cn",desc:"国内数字人视频工具，支持中文"},
      {label:"B站：AI视频制作教程",url:"https://www.bilibili.com/search?keyword=AI视频+可灵+剪映",desc:"从生成到发布完整流程"},
    ]),
    R("国际资源",false,[
      {label:"Runway Gen-3（商业最成熟）",url:"https://runwayml.com",desc:"商业AI视频首选，功能最全面"},
      {label:"Pika Labs（简单易用）",url:"https://pika.art",desc:"入门友好，有免费额度"},
      {label:"Luma Dream Machine",url:"https://lumalabs.ai/dream-machine",desc:"物理真实感较好，适合产品展示"},
      {label:"Sora（OpenAI，需排队）",url:"https://sora.com",desc:"最高质量，ChatGPT Plus用户可用"},
    ]),
  ],[
    E(1,"文生视频运镜实验","用同一场景描述（如：城市夜景街道）生成5个不同运镜版本（固定/推近/拉远/左移/环绕），对比运镜对观感的影响，总结哪种运镜最适合哪类内容场景（电商/品牌/故事性内容）。"),
    E(2,"图生视频产品展示","选一款真实产品图（可自拍或网上找），用可灵图生视频模式生成该产品的展示视频，测试3种不同的运动指令，选出最能展示产品质感的版本，分析选择理由。"),
    E(3,"首尾帧变身效果","用首尾帧模式创作一个「变身」或「转场」效果：起始帧（普通状态）→ 结束帧（目标状态），如：日出/日落/四季变换/从素颜到精致妆容，测试AI补间动画的流畅程度。"),
    E(4,"多平台规格适配","将同一段AI视频素材（横版16:9），通过剪映的「智能裁剪」功能分别输出为：抖音竖版(9:16)、微博方形(1:1)、YouTube横版(16:9)三个版本，对比智能裁剪的构图质量，记录需要手动调整的帧。"),
    E(5,"完整短视频制作模拟","模拟制作一条15秒的产品种草短视频：① 可灵生成3个产品动态镜头 ② 即梦生成1张文案背景图 ③ 剪映剪辑合并+AI配音+自动字幕 ④ 导出成品。记录完整制作耗时，对比传统拍摄成本。"),
  ]),

  buildDay(5,1,"第1周综合实战","🏅",["综合实战","内容创作","工作流"],[
    T("AI内容创作的系统思维",`完整的AI内容创作不是「用一个工具」，而是「工具链协同」：

【全栈内容创作工作流】
需求拆解（LLM）→ 文案生成（LLM）→ 视觉生成（AI图像）→ 动态化（AI视频）→ 后期合成（剪映）→ 数据复盘（分析工具）

每个环节AI辅助的比例和方式不同，核心是：
• LLM处理所有「文字→文字」的任务
• AI图像处理所有「文字/图片→图片」的任务
• AI视频处理「图片/文字→视频」的任务
• 人工聚焦在：创意决策、质量把关、策略规划`),
    T("内容复用的最大化策略",`同一创意，在不同平台以不同形态呈现，是降低内容生产成本的核心策略：

【一稿多发方法论】
核心内容（深度洞察）→ 长文（公众号/知乎）→ 摘要（微博/Twitter）→ 视觉卡片（小红书/Instagram）→ 短视频（抖音/视频号）→ 播客话题（喜马拉雅）

AI在各层的价值：
• 长文→摘要：10秒完成，准确率95%+
• 内容→多平台风格适配：节省70%改写时间
• 文案→图文脚本：结构化转换

【内容日历规划】
用AI生成30天的内容日历，包含：选题/格式/平台/发布时间，实现内容生产计划化、系统化`),
    T("质量评估框架",`如何判断AI生成内容是否达到可用标准？

【五维评估模型】
① 准确性：事实信息无误，数据可验证
② 相关性：紧扣主题，无跑题或废话
③ 可用性：可以不经大幅修改直接使用
④ 风格匹配：符合品牌调性和平台风格
⑤ 差异化：不是网上已有内容的简单堆砌

【AI内容的常见缺陷】
• 过于通用：缺乏具体数据和案例，像教科书
• 用词套路：「深度赋能」「持续发力」等空话
• 结构单调：总-分-总的固定套路
• 缺乏情感：逻辑严密但读起来没有「人味」

【改善策略】
在Prompt中提供：真实数据+具体案例+品牌独特视角+目标用户的真实痛点`),
    T("第1周核心技能总结与自测",`本周你应该已经掌握：

【必须能独立完成的任务】
✅ 用五要素Prompt写出能用的文案（准确率>70%）
✅ 在即梦/MJ中生成符合要求的图像素材
✅ 在可灵/即梦中生成一条基础产品展示视频
✅ 理解LLM/扩散模型/LoRA的基本工作原理

【自测问题】
1. CLIP是什么？在AI图像生成中起什么作用？
2. --sref和--cref参数有什么区别？
3. 为什么图生视频比文生视频更适合电商场景？
4. CoT提示词为什么能提升推理准确率？

【第2周预告】
将进入更专业的工具层：
• 扣子Bot（AI产品搭建）
• Dify（企业级AI应用）
• ComfyUI（专业图像工作流）
• 客服机器人配置实战`),
  ],[
    R("国内资源",true,[
      {label:"剪映专业版（后期合成）",url:"https://www.capcut.cn",desc:"AI字幕/配音/剪辑，内容创作必备"},
      {label:"飞书多维表格（内容管理）",url:"https://www.feishu.cn",desc:"内容日历规划，支持AI辅助"},
      {label:"小红书创作学院",url:"https://creator.xiaohongshu.com",desc:"平台内容规律和创作趋势"},
      {label:"稿定AI（批量内容设计）",url:"https://www.gaoding.com",desc:"AI设计+批量生成，电商运营必备"},
      {label:"创客贴（一键排版）",url:"https://www.chuangkit.com",desc:"AI海报/图文一键生成，操作简单"},
      {label:"新榜（内容运营数据）",url:"https://www.newrank.cn",desc:"公众号/短视频数据分析平台"},
      {label:"B站：AI内容创作工作流",url:"https://www.bilibili.com/search?keyword=AI内容创作+工作流+小红书",desc:"实战工作流视频教程"},
    ]),
    R("国际资源",false,[
      {label:"Buffer（多平台发布管理）",url:"https://buffer.com",desc:"内容排期和数据分析工具"},
      {label:"Canva AI（快速设计）",url:"https://www.canva.com",desc:"图文排版，AI功能完善"},
    ]),
  ],[
    E(1,"一稿多发完整实验","选定主题（任选：新能源汽车/国货护肤/健身饮食），产出同一核心洞察的5种形态：① 1000字深度文章（公众号）② 200字简版（微博）③ 标题+9图文案（小红书）④ 15秒视频脚本 ⑤ 100字英文版（国际平台）。全程记录AI参与比例。"),
    E(2,"品牌内容包交付","为一个虚拟品牌（自定义名称+品类）完整交付本周产出的所有物料：文案×3篇+主视觉图×3张+产品视频素材×1段，整理为品牌内容包文件夹，模拟向客户交付的格式。"),
    E(3,"工具链效率评估","记录本周每天使用每个AI工具的实际耗时，制作效率报告表：工具名称/任务类型/平均耗时/质量评分（1-10）/付费成本/ROI评估，总结出你个人最高效的工具组合。"),
    E(4,"AI vs 人工对比测试","选一个真实内容需求（如：为一款咖啡机写5条小红书评论），分别用纯人工和AI辅助各完成一次，记录两者的时间、质量差异，分析AI擅长的部分和人工仍有优势的部分。"),
    E(5,"第1周学习成果汇报","制作一份第1周学习复盘报告（PPT或Markdown文档）：学会的工具×7个，产出的内容×10件，最大收获×3点，待解决困惑×3个，第2周重点学习计划。这将是你作品集的第一页。"),
  ]),

  // ─── WEEK 2 ─────────────────────────────────────────────────────────────────
  buildDay(6,2,"扣子(Coze) AI Bot搭建","🤖",["扣子","AI Bot","知识库"],[
    T("扣子平台架构与工作原理",`扣子（Coze.cn）是字节跳动出品的AI Bot搭建平台，底层基于大语言模型+知识检索+工具调用的三层架构：

【核心架构层】
① 模型层：底层LLM（GPT-4o/Doubao等），负责理解和生成
② 记忆层：短期记忆（对话历史）+ 长期记忆（知识库）
③ 工具层：插件（搜索/天气/数据库）和工作流

【人设提示词的底层机制】
人设Prompt被注入为System Message，在每次对话前置处理，相当于给LLM设定了一个角色"锚点"，引导所有后续回复都在这个框架下生成。

【知识库检索原理（RAG基础）】
用户问题 → 向量化 → 在知识库向量数据库中做相似度搜索 → 取Top-K相关片段 → 拼接到Prompt → LLM生成基于知识的回答`),
    T("知识库构建最佳实践",`知识库的质量直接决定Bot回答的准确率：

【文档准备原则】
• 格式统一：推荐TXT/Markdown（比PDF提取更准确）
• 结构清晰：Q&A格式效果最佳（问题+答案配对）
• 长度适中：每个知识点200-400字，过长影响检索精度
• 定期更新：设立季度更新机制，避免过期信息

【分片策略】
推荐分片长度：300-500字（平衡语义完整性和检索精度）
重叠长度：50字（确保语义不因切割丢失）
相似度阈值：0.7（低于此分数的检索结果不使用）

【常见质量问题】
❌ FAQ太短：「Q: 退款多久？A: 7天」→ 缺少处理流程细节
✅ FAQ完整：「退款申请条件→申请流程→时效说明→特殊情况处理」`),
    T("插件与工作流进阶配置",`扣子的插件系统可以让Bot调用外部能力：

【常用插件场景】
• 联网搜索：让Bot能回答实时信息（新闻/天气/股价）
• 图像理解：用户上传图片，Bot分析内容
• 表单&记录：将用户信息自动保存到表格
• 发送通知：触发飞书/微信消息推送

【工作流 vs 简单Bot的区别】
简单Bot：用户问→AI直接回答（单轮或多轮对话）
工作流Bot：用户输入 → 触发多步自动化 → 调用多个工具 → 组合结果输出

【电商客服Bot的工作流设计】
用户问题 → 意图识别（退款/咨询/投诉）→ 路由到对应子流程 → 查询知识库 → 检索订单数据（如有API）→ 生成回答 → 记录对话日志`),
    T("Bot质量测试与迭代方法",`搭建Bot后，系统测试是提升质量的关键：

【测试用例设计原则】
覆盖五类问题：
① 标准问题（知识库已有答案）→ 测准确率
② 变体问题（同一问题不同说法）→ 测泛化能力
③ 边界问题（知识库没有的）→ 测拒绝能力
④ 刁难问题（测试Bot的立场稳定性）
⑤ 多轮问题（上下文依赖的连续对话）

【评估指标】
• 准确率：回答正确的比例（目标>85%）
• 拒绝率：知识库外的问题能否正确拒答
• 风格一致性：是否符合品牌调性
• 响应长度：是否简洁（不啰嗦/不截断）

【迭代优化循环】
测试→发现问题→定位原因（人设/知识库/分片）→修改→再测试`),
  ],[
    R("国内资源",true,[
      {label:"扣子官网（国内版）",url:"https://www.coze.cn",desc:"字节旗下，国内直连，强烈推荐"},
      {label:"扣子开放平台文档",url:"https://www.coze.cn/docs",desc:"官方API和插件开发文档"},
      {label:"FastGPT（国产RAG平台）",url:"https://fastgpt.in",desc:"知识库问答，国内团队开发，可私有化"},
      {label:"阿里云百炼（企业RAG）",url:"https://bailian.console.aliyun.com",desc:"阿里云AI应用平台，支持私有知识库"},
      {label:"飞书开放平台（Bot接入）",url:"https://open.feishu.cn",desc:"将Bot发布到飞书工作场景"},
      {label:"微信公众平台（Bot接入）",url:"https://mp.weixin.qq.com",desc:"将AI客服接入微信公众号"},
      {label:"B站：扣子Coze实战教程",url:"https://www.bilibili.com/search?keyword=扣子Coze+机器人教程",desc:"最新实战视频教程"},
      {label:"B站：RAG知识库搭建",url:"https://www.bilibili.com/search?keyword=RAG+知识库+AI客服",desc:"知识库检索增强教程"},
    ]),
    R("国际资源",false,[
      {label:"Coze国际版（英文）",url:"https://www.coze.com",desc:"功能与国内版基本同步，支持OpenAI模型"},
      {label:"Botpress（开源Bot框架）",url:"https://botpress.com",desc:"对比学习，了解企业级Bot架构"},
    ]),
  ],[
    E(1,"客服Bot从零搭建","为一个虚拟电商品牌（运动服装）搭建完整客服Bot：① 撰写500字以上的人设Prompt（含角色/能力/风格/边界）② 准备20条FAQ（覆盖售前/售中/售后）③ 上传知识库 ④ 完成20轮测试对话，记录准确率。"),
    E(2,"意图识别与多路由设计","在现有Bot基础上配置意图识别工作流：将用户问题分类为退款/咨询/投诉/其他4种意图，针对每种意图设计不同的回复风格和处理逻辑，测试分类准确率（用20个测试用例）。"),
    E(3,"边界测试与漏洞修复","对你搭建的Bot进行10种越界测试（询问竞品/要求做无关任务/情感操纵等），记录Bot的反应，针对每个漏洞改进人设Prompt，输出「Bot安全测试报告」。"),
    E(4,"多平台发布测试","将Bot发布到至少2个渠道（网页+飞书/微信），比较在不同平台的体验差异，分析哪个平台最适合你设计的Bot场景，说明理由。"),
    E(5,"商业价值量化分析","调研1-2个真实企业的客服成本（可查公开报道/招聘数据估算），结合你搭建的Bot能力，计算：如果替代50%的人工客服，1年可节省多少成本？输出一份500字的商业可行性分析。"),
  ]),

  buildDay(7,2,"Dify平台：知识库 + 工作流","⚙️",["Dify","RAG","工作流"],[
    T("RAG技术深度原理",`RAG（Retrieval Augmented Generation，检索增强生成）是当前企业级AI应用的核心技术架构：

【向量化（Embedding）原理】
文本 → Embedding模型（如text-embedding-3-large）→ 高维向量（通常1536维）
这个向量捕获了文本的语义信息，语义相似的文本在向量空间中距离更近

【检索过程（ANN近似近邻搜索）】
用户问题向量化 → 在向量数据库（Chroma/Milvus/Pinecone）中做余弦相似度搜索 → 返回最相似的Top-K个文本块

【为什么RAG比「全部塞入Prompt」更好？】
① 知识库可以超大（百万字），而上下文窗口有限（最大200K）
② 检索确保每次只注入最相关的内容，减少噪音
③ 知识库可以独立更新，不需要重新训练模型

【关键质量瓶颈】
检索质量 > 生成质量。如果检索到的内容不对，再好的LLM也会答错。`),
    T("Dify工作流节点体系",`Dify的工作流是可视化AI任务编排器，核心节点类型：

【数据处理类】
• LLM节点：调用大语言模型生成文本
• 知识检索节点：在知识库中语义搜索
• 变量提取节点：用LLM从文本中提取结构化字段（JSON格式输出）

【控制流类】
• 条件分支（IF/ELSE）：根据变量值走不同路径
• 迭代（Iterator）：对列表中每个元素重复处理
• 并行（Parallel）：多个节点同时执行

【集成类】
• HTTP请求节点：调用任意外部REST API
• 代码节点：运行Python/JavaScript处理数据
• 模板转换：格式化文本输出

【工作流设计原则】
单节点单职责：每个LLM节点只做一件事，避免一个节点承担多个任务（降低错误率）`),
    T("Dify vs 扣子的深度对比",`两个平台各有优势，职场中都有大量需求：

【扣子（适合）】
• 快速搭建消费级Bot（个人/小团队）
• 发布到微信/抖音/飞书等现有平台
• 有丰富的商业插件（不需要自己对接API）
• 学习曲线：1天入门

【Dify（适合）】
• 企业级AI应用（数据私有化要求）
• 复杂工作流（多步骤/条件分支/代码处理）
• 开源自部署（数据不出企业服务器）
• 需要与现有系统API深度集成
• 学习曲线：3-7天入门

【职场需求分布】
招聘数据显示：提到Dify的岗位主要在中型科技企业和AI创业公司，通常要求「0代码搭建AI应用」能力。提到扣子的岗位多为运营/产品方向，门槛更低但竞争也更激烈。`),
    T("混合检索与高级RAG策略",`基础RAG只用向量相似度，高级RAG综合多种检索策略：

【混合检索（Hybrid Search）】
向量检索（语义匹配）+ 关键词检索（精确匹配）→ 结果融合排序
优势：解决向量检索对专有名词/数字/缩写匹配不准的问题
Dify设置：检索模式选「混合检索」，权重建议向量0.7+关键词0.3

【重排序（ReRank）】
第一轮：快速检索候选集（Top-20）
第二轮：交叉编码器对候选集重新打分（更精准但更慢）
→ 最终取Top-5作为LLM的参考
效果：准确率通常提升10-20%，但增加300-500ms延迟

【HyDE（假设文档嵌入）】
先让LLM生成一个「假设答案」，用这个假设答案去检索（而非用原始问题），往往能找到更相关的文档。适合专业领域问答场景。`),
  ],[
    R("国内资源",true,[
      {label:"Dify官方文档（中文）",url:"https://docs.dify.ai/zh-hans",desc:"最权威，建议收藏"},
      {label:"FastGPT（国产RAG平台）",url:"https://fastgpt.in",desc:"开源可私有化，与Dify对比学习"},
      {label:"阿里云百炼（企业RAG）",url:"https://bailian.console.aliyun.com",desc:"阿里云企业AI应用平台"},
      {label:"百度千帆大模型平台",url:"https://qianfan.cloud.baidu.com",desc:"知识库问答解决方案，有完整文档"},
      {label:"智谱AI开放平台",url:"https://open.bigmodel.cn",desc:"提供中文Embedding模型，RAG性价比高"},
      {label:"通义嵌入模型（中文向量）",url:"https://help.aliyun.com/zh/dashscope/developer-reference/text-embedding-api",desc:"中文RAG的优质Embedding模型"},
      {label:"B站：Dify完整教程",url:"https://www.bilibili.com/search?keyword=Dify+RAG+教程",desc:"最新实战视频"},
      {label:"B站：FastGPT知识库教程",url:"https://www.bilibili.com/search?keyword=FastGPT+知识库",desc:"国产RAG平台实战"},
    ]),
    R("国际资源",false,[
      {label:"Dify GitHub（开源代码）",url:"https://github.com/langgenius/dify",desc:"了解底层实现，Star最多的AI应用框架"},
      {label:"LangChain文档（RAG原理）",url:"https://python.langchain.com/docs/tutorials/rag",desc:"深入理解RAG技术原理"},
      {label:"Pinecone向量数据库文档",url:"https://docs.pinecone.io",desc:"最流行的托管向量数据库"},
      {label:"Weaviate向量数据库",url:"https://weaviate.io/developers/weaviate",desc:"开源向量数据库，支持混合检索"},
    ]),
  ],[
    E(1,"RAG知识库精细调优","在Dify创建知识库，上传同一文档分别测试：分片大小(200/500/1000字)×相似度阈值(0.5/0.7/0.9)的3×3=9种配置，每种配置问10个标准问题，记录准确率，找出最优配置组合。"),
    E(2,"混合检索vs纯向量检索","使用同一知识库，分别开启「向量检索」和「混合检索」模式，设计包含：普通问题/含专有名词的问题/含数字的问题/模糊问题 各5条的测试集，对比两种模式的表现差异，得出结论。"),
    E(3,"复杂工作流设计","构建一个「内容审核+优化」工作流：用户提交文章草稿 → 第一个LLM节点检测违规内容（输出JSON格式：{violations:[],severity:''}} ） → IF有违规：生成修改建议；ELSE：进行SEO优化 → 最终输出。"),
    E(4,"HTTP节点外部API集成","在Dify工作流中添加HTTP节点，接入一个公开API（如：聚合数据的天气API/汇率API），实现：用户问「今天北京天气怎么样」→ 工作流自动调用API获取实时天气 → LLM整合生成自然语言回答。"),
    E(5,"企业知识库方案设计","为一家100人规模的电商公司设计完整的Dify知识库方案：① 知识库分类体系（产品/运营/客服/培训4个库）② 每个库的文档标准 ③ RAG配置建议 ④ 权限控制方案 ⑤ 更新维护流程。输出Word方案文档。"),
  ]),

  // ─── WEEK 2 CONT. ────────────────────────────────────────────────────────────
  buildDay(8,2,"ComfyUI安装与基础工作流","⚡",["ComfyUI","SD","节点工作流"],[
    T("ComfyUI节点图架构原理",`ComfyUI基于节点图（Node Graph）思想，将图像生成管线拆解为独立功能节点：

【核心管线节点】
Load Checkpoint（加载模型）→ CLIP Text Encode（编码Prompt）→ Empty Latent Image（创建潜空间画布）→ KSampler（扩散采样）→ VAE Decode（解码为像素）→ Save Image（保存）

【与WebUI的本质区别】
WebUI：封装好的一键式界面，适合新手但定制性低
ComfyUI：直接暴露完整Diffusion Pipeline，每个参数可精确控制，适合专业工作流

【数据流理解】
每个节点有输入口（左侧）和输出口（右侧），不同颜色代表不同数据类型：
• 紫色：Model模型权重
• 黄色：Conditioning文本条件
• 蓝色：Latent潜变量
• 红色：Image像素图像`),
    T("KSampler核心参数详解",`KSampler是ComfyUI最核心的节点，控制扩散采样过程：

【Steps（采样步数）】
20步：质量与速度的最佳平衡点（DPM++ 2M Karras）
15步：快速测试用，质量略低
30-40步：追求极致质量时使用，但提升有限

【CFG Scale（引导强度）】
范围1-20，推荐6-9：
• <5：提示词跟随性弱，图像更「随机」
• 6-9：最佳平衡，提示词有效但不过度
• >12：容易产生过饱和伪影（颜色过浓/轮廓过硬）

【采样器选择】
DPM++ 2M Karras：20步内高质量，推荐日常使用
Euler Ancestral：风格更多样，适合创意探索
DDIM：确定性强（固定seed结果完全相同），适合A/B测试
LMS Karras：细节丰富，适合写实人像

【Denoise Strength（去噪强度）】
仅在img2img/inpainting中使用：
0.3-0.5：轻微修改，保留原图结构
0.6-0.8：中等修改，改变风格保留内容
0.9-1.0：几乎重新生成`),
    T("VAE的作用与选型",`VAE（变分自编码器）是Diffusion管线的「图像编解码器」：

【功能】
编码：像素图 → 潜空间（通常8倍降采样，512×512→64×64 Latent）
解码：潜空间 → 像素图（扩散完成后还原）

【VAE对图像质量的影响】
不匹配的VAE会导致：色彩偏差（偏蓝/偏灰）、细节模糊、边缘不清晰

【主流VAE选型】
SD1.5系列：vae-ft-mse-840000-ema-pruned（最通用）
SDXL系列：sdxl_vae.safetensors（官方标配）
特殊风格：AnimagineXL VAE（动漫色彩更鲜明）

【Baked VAE说明】
部分模型将VAE权重打包进Checkpoint，称为「Baked VAE」，Load Checkpoint后无需额外加载VAE节点，直接可用。`),
    T("批量生产工作流设计",`ComfyUI的核心商业价值在于批量自动化处理：

【批量出图工作流要素】
1. Batch Size节点：一次提交生成N张（GPU显存允许时）
2. 随机Seed节点：每张图使用不同随机种子
3. 图像命名规范：使用时间戳+序号自动命名
4. 批量Prompt节点（WAS节点包）：读取txt文件中的多个Prompt，批量处理

【图像质量自动筛选】
配合图像评分模型（如CLIP-IQA）自动过滤低质量图像，只保留分数>0.7的输出，实现「生成50张→自动筛选出15张高质量图」的工作流。

【商业应用场景】
电商：1小时生成500张产品不同场景图
内容：批量生成文章配图（每篇3张，50篇=150张）
广告测试：同一产品生成20个不同风格版本进行A/B测试`),
  ],[
    R("国内资源",true,[
      {label:"秋叶ComfyUI整合包（一键安装）",url:"https://www.bilibili.com/video/BV1Ew411776J",desc:"国内最佳入门方案，含完整Python环境"},
      {label:"LiblibAI工作流社区",url:"https://www.liblib.art",desc:"下载现成ComfyUI工作流，直接复用"},
      {label:"触站Huashi6（工作流分享）",url:"https://www.huashi6.com",desc:"中文ComfyUI工作流教程与资源"},
      {label:"吐司AI（在线ComfyUI）",url:"https://tusi.cn",desc:"无需安装GPU，在线跑ComfyUI工作流"},
      {label:"B站：ComfyUI完整入门合集",url:"https://www.bilibili.com/search?keyword=ComfyUI+入门+完整教程",desc:"最新中文视频教程"},
    ]),
    R("国际资源",false,[
      {label:"ComfyUI GitHub官方",url:"https://github.com/comfyanonymous/ComfyUI",desc:"最新版本与文档"},
      {label:"ComfyUI Manager",url:"https://github.com/ltdrdata/ComfyUI-Manager",desc:"节点/模型一键管理"},
    ]),
  ],[
    E(1,"环境搭建与首图生成","使用秋叶整合包安装ComfyUI（或使用在线版），运行默认工作流成功出图，然后逐一右键每个节点查看说明，手绘一张完整的「节点数据流图」（标注每个连线的数据类型和流向方向）。"),
    E(2,"KSampler参数矩阵实验","固定Prompt和Seed，测试Steps(10/20/30)×CFG(5/7/12)的3×3=9种参数组合，生成9张对比图，制作「参数效果矩阵表」，标注每种组合的质量特点，得出你的最优参数组合推荐。"),
    E(3,"VAE替换对比测试","下载2-3个不同VAE文件，在相同Checkpoint+相同Prompt下分别测试，对比色彩饱和度、边缘清晰度、皮肤质感三个维度的差异，总结不同VAE适用的视觉风格场景。"),
    E(4,"批量出图工作流","搭建一个支持批量处理的工作流：使用Batch Size=4，配合随机Seed节点，一次提交生成16张不同Seed的图像，使用WAS节点的图像评分功能，筛选出质量最高的4张，记录筛选准确率。"),
    E(5,"电商白底图工作流","模拟业务场景：客户提供5张不同产品的真实照片，需要生成白底展示图。搭建完整工作流：加载产品图→背景去除→白底合成→质量优化，测试5个产品的成功率，记录失败案例的原因。"),
  ]),

  buildDay(9,2,"ComfyUI进阶：LoRA应用","🎛️",["LoRA","ControlNet","批量出图"],[
    T("LoRA训练原理与参数空间",`LoRA的完整数学原理：

【权重矩阵分解】
预训练权重：W₀（d×k维，例如768×3072）
LoRA增量：ΔW = B·A
  B：d×r矩阵（r远小于d，通常r=4,8,16,32）
  A：r×k矩阵

前向传播：h = W₀x + ΔWx = W₀x + BAx
缩放因子：α/r（控制LoRA实际影响强度）

【秩r的选择影响】
r=4：极轻量，适合简单风格迁移
r=8：标准配置，质量与体积平衡
r=16：更高保真度，适合人物特征保留
r=32+：接近全量微调效果，文件更大

【LoRA权重（strength）参数】
0.3-0.5：轻微风格影响，背景模型特征明显
0.6-0.8：中等影响，LoRA特征清晰但不过度
0.9-1.0：强影响，LoRA特征主导，可能牺牲多样性
>1.0：过度激活，可能导致图像崩溃`),
    T("ControlNet工作原理与类型",`ControlNet是为Diffusion模型添加空间条件控制的框架（2023年斯坦福提出）：

【工作机制】
额外的条件控制信号（骨骼图/边缘图/深度图）→ 可训练的控制网络 → 注入UNet编码器的每一层 → 精确控制空间布局

【主要ControlNet类型与适用场景】
Canny Edge（Canny边缘检测）：
  → 保持线稿/产品轮廓，改变风格
  → 适合：产品图风格迁移、线稿上色

OpenPose（人体骨骼）：
  → 控制人体姿势和动作
  → 适合：时装展示、动作类内容

Depth（深度图）：
  → 保持三维空间结构和透视关系
  → 适合：室内设计、建筑渲染

IP-Adapter（图像参考）：
  → 基于参考图的风格/内容迁移
  → 适合：品牌视觉一致性、IP风格应用`),
    T("多LoRA叠加与冲突处理",`商业工作流中经常需要叠加多个LoRA：

【叠加原则】
• 功能性LoRA（如：人物外观）+ 风格LoRA = 常见组合
• 总权重建议不超过1.5，否则图像易崩溃
• 同类型LoRA叠加（两个风格LoRA）冲突概率高，建议只用一个

【推荐叠加策略】
基础模型（写实Checkpoint）× 1
+ 人物LoRA（0.7）：固定角色外观
+ 风格LoRA（0.4）：添加特定画风
+ 细节优化LoRA（0.3）：提升皮肤/光线质量
总权重：1.4，在安全范围内

【LoRA Block Weight（分层控制）】
高级技巧：不同层的LoRA权重可以单独控制
INPUT层（布局/结构控制）+ OUTPUT层（风格/质感控制）
实现：「保留人物外观但改变背景风格」的精细控制`),
    T("IP-Adapter与风格克隆应用",`IP-Adapter是2023年腾讯研究院提出的图像提示适配器：

【工作原理】
参考图像 → CLIP图像编码器 → 图像特征向量 → 通过解耦交叉注意力注入UNet
与文本Prompt并行工作，实现「图文双重引导」

【商业价值】
品牌一致性：上传品牌设计图 → 批量生成风格一致的新物料
电商场景：上传产品图 → IP-Adapter保持产品特征 → 在任意背景/场景中复现
IP角色：上传角色参考图 → 在不同场景中保持外观一致

【强度参数（scale）选择】
0.3-0.5：轻度风格参考，Prompt仍有较大影响力
0.6-0.8：中度，图像风格明显向参考图靠拢
0.9-1.0：强度参考，几乎完全按参考图风格生成

【IP-Adapter + ControlNet 组合】
同时使用IP-Adapter（控制风格）+ OpenPose（控制姿势）
实现：固定品牌风格+精确姿势控制，适合时装/广告内容`),
  ],[
    R("国内资源",true,[
      {label:"LiblibAI LoRA模型库",url:"https://www.liblib.art",desc:"国内最大LoRA模型下载，按风格分类"},
      {label:"哩布哩布AI社区",url:"https://www.liblibai.com",desc:"LoRA模型分享与工作流讨论"},
      {label:"吐司AI（LoRA在线测试）",url:"https://tusi.cn",desc:"在线测试LoRA效果，免GPU"},
      {label:"B站：LoRA训练完整教程",url:"https://www.bilibili.com/search?keyword=LoRA训练+ComfyUI+完整",desc:"国内最新LoRA训练视频"},
      {label:"秋叶：SD LoRA训练工具箱",url:"https://www.bilibili.com/search?keyword=秋叶+LoRA+训练工具箱",desc:"B站搜索秋叶训练教程"},
    ]),
    R("国际资源",false,[
      {label:"Civitai LoRA模型库",url:"https://civitai.com",desc:"全球最大AI模型社区"},
      {label:"Kohya_ss（LoRA训练框架）",url:"https://github.com/bmaltais/kohya_ss",desc:"主流LoRA训练工具"},
    ]),
  ],[
    E(1,"LoRA权重调优实验","下载1个人物LoRA+1个画风LoRA，在相同基础模型上分别测试：(0.3+0.7)/(0.5+0.5)/(0.7+0.3)/(0.8+0.2)/(1.0+0.4)五种权重组合，记录每种组合的视觉特征变化，找出最佳平衡点并解释原因。"),
    E(2,"ControlNet姿势控制实战","安装OpenPose ControlNet节点，从真实人物照片中提取骨骼图，生成：① 完全相同姿势的动漫版本 ② 相同姿势但改变服装场景的写实版本，对比ControlNet对姿势控制的精确度。"),
    E(3,"品牌图像批量生产流水线","为一个品牌设计ComfyUI批量生产工作流：品牌风格LoRA+产品描述Prompt→批量生成20张产品场景图（4种场景×5种构图），使用图像评分节点自动筛选质量>0.7的图，统计可用率。"),
    E(4,"IP-Adapter风格克隆","收集一个你喜欢的品牌的5张视觉图作为参考，用IP-Adapter提取品牌视觉风格，为另一个完全不同的产品生成符合该品牌风格的图像×3张，邀请3位朋友评估风格相似度（1-10分）。"),
    E(5,"商业提案：AI视觉工作室方案","模拟：某电商品牌每月需要200张产品图，原来外包费用2万/月，询问你能否用AI降低成本。设计完整方案：工作流架构+所用模型+预计日产出量+质量标准+报价建议。输出1000字方案文档。"),
  ]),

  buildDay(10,2,"电商客服话术库设计","💬",["话术库","意图识别","客服AI"],[
    T("电商客服场景的AI化架构",`电商客服AI化的完整技术架构：

【五层服务架构】
1. 意图识别层：NLU模型识别用户意图（退款/咨询/投诉/闲聊）
2. 对话管理层：维护对话状态，追踪上下文
3. 知识检索层：RAG从知识库检索相关FAQ和政策
4. 生成层：LLM基于检索结果生成回复
5. 后处理层：合规检查、情感分析、人工转接判断

【客服场景分类（MECE原则）】
售前：产品咨询/价格/库存/促销规则/比较推荐
售中：下单引导/支付问题/优惠券使用/赠品确认
物流：发货查询/物流异常/预计到达/拦截修改
售后：退换货申请/维权处理/差评解释/赔付谈判
情感：投诉安抚/情绪处理/升级预警`),
    T("Few-Shot提示词设计",`Few-Shot Prompting通过提供具体示例引导模型的回复风格：

【结构模板】
系统人设（角色+风格）
---示例开始---
用户：[典型问题A]
助手：[标准回复A]
用户：[典型问题B]
助手：[标准回复B]
---示例结束---
用户：{实际问题}

【示例选择原则】
① 覆盖边界情况（无理取闹/专业问题/多层诉求）
② 展示正确的情感处理方式（共情→解决→追问）
③ 包含不应该做的示例（负面示例同样重要）
④ 长度控制：每个示例50-150字，太长会稀释关键模式

【Few-Shot vs Zero-Shot效果对比】
研究数据：在客服场景中，3-5个Few-Shot示例可将回复相关性提升30-50%，情感适配度提升40%+`),
    T("RAG知识库在客服场景的优化",`客服场景的RAG知识库有其特殊性：

【数据结构设计】
标准格式：Q（用户问题）+ A（标准回复）+ 标签（场景类别+情感类型）

超级FAQ格式（推荐）：
{
  "intent": "退款申请",
  "trigger_keywords": ["退款","退货","不想要了"],
  "standard_questions": ["怎么退款","能退吗","申请退货"],
  "policy": "7天无理由退款，...",
  "standard_reply": "您好！非常理解您的需求...",
  "special_cases": ["超过7天的处理方案", "非质量问题退款"],
  "escalation_trigger": ["投诉","举报","曝光","律师"]
}

【客服知识库分层】
L1：通用FAQ（80%问题）→ 自动回复
L2：政策文档（10%问题）→ 检索后回复
L3：案例库（8%问题）→ 相似案例参考
L4：人工（2%问题）→ 升级处理`),
    T("高难度客服场景的AI应对策略",`最难处理的5类客服场景及AI应对设计：

【场景1：情绪激动/语言攻击】
策略：先情感共情→不与对方对抗→转移到解决方案
触发升级：出现"骂人词汇"→立即提醒人工接管

【场景2：无理取闹/超规则退款】
策略：清晰引用政策→提供替代方案→不做无授权承诺
示例回复结构：「理解您的心情 + 我们的政策是X + 我能为您做的是Y + 如您不满意可以Z」

【场景3：差评/投诉威胁】
策略：不慌张、不道歉（道歉=承认责任）、快速升级人工
触发词：「投诉到工商」「在平台曝光」「找律师」→ 立即标记高优先级

【场景4：价格异议/竞品比较】
策略：不攻击竞品→强调本品价值→提供可让步的方案（优惠券/赠品）

【场景5：跨平台纠纷】
策略：收集截图证据→核实事实→走正式仲裁流程→AI不独立判决`),
  ],[
    R("国内资源",true,[
      {label:"扣子（Coze）客服Bot搭建",url:"https://www.coze.cn",desc:"快速搭建知识库客服机器人，无需代码"},
      {label:"FastGPT客服场景模板",url:"https://fastgpt.in",desc:"开源可私有化，客服Bot专项能力强"},
      {label:"阿里云智能客服解决方案",url:"https://www.aliyun.com/product/ai/chatbot",desc:"企业级NLP客服，支持私有化部署"},
      {label:"Dify客服Bot模板",url:"https://cloud.dify.ai/explore/apps",desc:"现成客服模板，可直接修改上线"},
      {label:"B站：AI智能客服系统搭建",url:"https://www.bilibili.com/search?keyword=AI智能客服+知识库+搭建",desc:"从零到上线实战视频"},
      {label:"腾讯云智能对话（腾讯客服AI）",url:"https://cloud.tencent.com/product/tbp",desc:"腾讯对话机器人平台，有免费额度"},
    ]),
    R("国际资源",false,[
      {label:"OpenAI Cookbook：客服Bot",url:"https://cookbook.openai.com",desc:"官方客服Bot示例代码"},
      {label:"LangChain客服Agent教程",url:"https://python.langchain.com/docs/use_cases/question_answering",desc:"RAG客服全套方案"},
    ]),
  ],[
    E(1,"话术库系统设计","为一个虚拟运动品牌设计完整客服话术库：① 意图分类树（3级分类，共15个叶节点）② 每个意图配标准FAQ×2条 ③ 设计触发关键词列表 ④ 设计升级触发词列表 ⑤ 设计回复风格指南。输出完整Excel文档（含5个Sheet）。"),
    E(2,"Few-Shot Prompt构建与测试","选取5个最难的客服场景（如：超7天要求退款/差评威胁/使用辱骂语言），为每个场景设计含3个示例的Few-Shot Prompt，分别在豆包和通义上测试，对比输出质量，量化改善幅度。"),
    E(3,"RAG客服知识库搭建","在Dify或FastGPT中搭建客服知识库：上传50条以上的FAQ文档，配置混合检索模式，测试20个标准问题的准确率，记录失败案例，分析失败原因（知识库缺失/检索失败/LLM幻觉）并分别修复。"),
    E(4,"高难度场景压力测试","模拟10种极端客服场景对你的Bot进行压力测试：语言攻击/无理索赔/套话术漏洞/假冒官方/情绪操纵等，记录Bot的每次反应，给出「危险度」评估（1-5级），针对3级以上危险场景设计改进方案。"),
    E(5,"AI客服商业价值报告","收集1-2个真实的AI客服落地案例（搜索36氪/虎嗅/阿里云案例），结合你搭建的系统，撰写800字的「AI客服部署可行性分析报告」，包含：现状痛点/方案设计/预期ROI/风险提示/实施建议。"),
  ]),

  buildDay(11,2,"知识数据整理与标准化","📝",["数据工程","向量数据库","知识标注"],[
    T("非结构化文档处理管线",`企业知识通常分散在不同格式的文档中，AI化的第一步是建立统一处理管线：

【文档解析技术栈】
PDF解析：PyMuPDF（精度高）/ pdfplumber（表格友好）
Word文档：python-docx
HTML：BeautifulSoup + lxml
图片中的文字（OCR）：PaddleOCR（中文最佳）/ Tesseract（英文）
PPT：python-pptx

【处理流水线】
原始文档 → 格式解析 → 内容清洗（去页眉页脚/水印/广告）→ 结构识别（标题/正文/表格/图片说明）→ 语义分块 → 元数据标注 → 向量化存储

【分块策略对比】
固定长度分块：简单但可能切断语义，适合连续文本
段落分块：保留自然段落，适合结构化文档
语义分块：用小模型判断语义边界，质量最好但速度慢
推荐配置：chunk_size=512 tokens，overlap=64 tokens`),
    T("向量数据库核心概念",`向量数据库是RAG的核心基础设施：

【工作原理】
文本 → Embedding模型 → 向量（768-3072维）→ 存入向量数据库（建立ANN索引）→ 查询时计算余弦相似度返回最近邻

【主流产品对比】
Chroma：Python原生，轻量嵌入式，适合原型和小规模
Milvus：企业级，支持亿级向量，高并发，开源可私有化
Pinecone：托管服务，免运维，按用量付费，美国公司
Weaviate：混合检索强，GraphQL接口，开源
Faiss（Meta）：最快的向量检索库，但需要自行构建上层

【中文Embedding模型推荐】
智谱Embedding-2：综合最佳，官方API直接调用
BCEmbedding（百度）：开源，中文RAG评测第一
text-embedding-ada-002（OpenAI）：英文最强，中文一般`),
    T("LLM辅助标注工作流",`利用AI大幅提升知识库标注效率：

【传统标注 vs AI辅助标注】
传统：人工阅读→判断分类→填写元数据 → 效率：50-100条/天
AI辅助：LLM批量预标注→人工抽样验证（10-20%）→ 效率：500-1000条/天

【AI预标注Prompt设计】
"你是知识管理专家。对以下文档片段进行结构化标注，以JSON格式输出：
{
  'topic_tags': ['标签1','标签2'],  // 最多3个主题标签
  'summary': '一句话摘要（≤50字）',
  'qa_pairs': [{'q':'问题','a':'答案'}],  // 提取2-3个QA对
  'importance': 1-5,  // 重要性评分
  'staleness_risk': 'high/medium/low'  // 信息时效性风险
}
文档：{text}"

【质量控制】
对10-20%的AI标注结果进行人工核验，计算准确率，当准确率<85%时优化Prompt`),
    T("知识库质量评估体系",`知识库质量直接决定RAG应用的最终效果：

【六维质量评估框架】
① 覆盖率：能回答多少比例的预期问题
② 准确率：回答正确的比例（事实核验）
③ 时效性：信息是否为最新版本
④ 结构性：分块是否合理，语义是否完整
⑤ 冗余度：重复内容的比例（越低越好）
⑥ 噪音率：与业务无关内容的比例

【测试集构建】
从目标用户视角出发，构建100-500条「黄金问题集」：
• 业务核心问题（必须能答）
• 边界问题（知识库外，必须拒答）
• 变体问题（同一问题的不同说法）

【迭代优化循环】
测试集评估 → 失败案例分析 → 定位原因（文档缺失/分块问题/Embedding质量）→ 针对性修复 → 再评估`),
  ],[
    R("国内资源",true,[
      {label:"PaddleOCR（百度开源，中文OCR最佳）",url:"https://github.com/PaddlePaddle/PaddleOCR",desc:"中文文字识别准确率业界最高"},
      {label:"智谱AI Embedding API",url:"https://open.bigmodel.cn",desc:"中文向量化，RAG知识库必备"},
      {label:"OPENDATALAB（数据集平台）",url:"https://opendatalab.com",desc:"国内最全开源数据集，支持中文"},
      {label:"阿里云百炼（知识库管理）",url:"https://bailian.console.aliyun.com",desc:"企业级知识库管理和检索"},
      {label:"飞书知识库（团队知识沉淀）",url:"https://www.feishu.cn/hc/zh-CN",desc:"适合团队协同整理知识文档"},
      {label:"B站：RAG知识库构建完整教程",url:"https://www.bilibili.com/search?keyword=RAG+知识库+构建+教程",desc:"数据整理到RAG应用的完整流程"},
    ]),
    R("国际资源",false,[
      {label:"Unstructured.io（文档解析）",url:"https://unstructured.io",desc:"PDF/Word/HTML文档结构化解析"},
      {label:"LlamaIndex文档",url:"https://docs.llamaindex.ai",desc:"知识库索引主流框架"},
    ]),
  ],[
    E(1,"文档解析管线搭建","找一份50页以上的PDF文档（如产品手册/政策文件），使用PyMuPDF或Unstructured解析文本，清洗格式（去页眉页脚/处理表格），输出结构化JSON（含段落/标题层级/页码元数据），记录解析准确率。"),
    E(2,"分块策略对比实验","对同一文档分别用：① 固定512字分块 ② 按段落自然分块 ③ 递归语义分块（LangChain RecursiveTextSplitter）三种策略处理，各提10个测试问题做RAG检索，对比Top-3召回准确率，分析差异原因。"),
    E(3,"AI辅助批量标注","编写一个Prompt，让GPT-4o/通义批量为20段业务文档生成结构化标注（主题标签/摘要/QA对/重要性评分），运行后对50%的输出进行人工核验，计算AI标注准确率，优化Prompt使准确率达到85%+。"),
    E(4,"企业知识库标注规范设计","为一家虚拟科技公司设计知识库标注规范文档，包含：① 文档分类体系（3级，至少15个叶节点）② 必填元数据字段定义（≥8个字段）③ 质量评分标准（含具体示例）④ 更新维护SOP。输出Word文档。"),
    E(5,"RAG质量基准测试","为你已建立的RAG系统（扣子或Dify的知识库）构建「黄金问题集」（30条：核心问题15条+边界问题10条+变体问题5条），完整评估6维质量指标，输出「知识库质量报告」，给出TOP3优化建议。"),
  ]),

  buildDay(12,2,"NocoBase低代码平台","🗃️",["NocoBase","低代码","数据管理"],[
    T("低代码平台的技术架构",`低代码平台的底层是「元数据驱动」架构：

【元数据驱动原理】
用户通过可视化界面定义：数据模型/字段类型/页面布局/业务规则/工作流
→ 所有定义以元数据形式存储（JSON/XML格式）
→ 运行时引擎解析元数据，动态生成：数据库表+REST API+前端UI+业务逻辑

NocoBase的特殊性：插件架构，核心功能100%由插件实现，企业可按需加载，避免功能臃肿。

【与全代码开发的本质区别】
低代码：适合「已知需求模式」的快速实现（CRUD/审批/看板）
全代码：适合「复杂算法/高性能/高度定制化」需求

最佳实践：用低代码快速验证业务逻辑，复杂需求用代码插件扩展。`),
    T("数据模型设计原则",`数据模型设计质量决定70%的低代码项目成败：

【关系类型与使用场景】
一对多（hasMany）：客户→订单，员工→请假记录
多对一（belongsTo）：订单→客户（外键）
多对多（belongsToMany）：课程↔学生（中间表）
一对一（hasOne）：用户→用户详情（垂直分表）

【字段设计原则】
• 主键：UUID优于自增ID（分布式友好）
• 时间戳：必须加 created_at / updated_at
• 软删除：deleted_at（保留数据审计链路）
• 状态字段：用枚举而非布尔值（更易扩展）

【范式与反范式的平衡】
核心数据：严格遵循3NF（减少冗余）
报表/看板：允许适度反范式（预计算/冗余字段）以提升查询性能`),
    T("工作流引擎设计",`NocoBase内置工作流引擎基于事件驱动架构：

【触发器类型】
• 数据变更触发：记录创建/修改/删除时触发
• 定时触发：Cron表达式，如每天09:00
• Webhook触发：接收HTTP请求启动流程
• 手动触发：用户点击按钮启动

【常用动作节点】
查询节点：从数据库查询数据
创建/修改/删除节点：操作数据库记录
条件判断：IF/ELSE分支
循环：遍历数组数据
HTTP请求：调用外部API
发送通知：邮件/企微/飞书推送
赋值：修改变量值

【真实业务工作流设计案例】
员工提交报销申请（触发器）
→ 金额>5000元（条件）
→ 是：发送给CFO审批
→ 否：直属领导审批
→ 审批通过：财务部门收到待付款通知
→ 驳回：申请人收到驳回原因`),
    T("低代码 vs 全代码决策框架",`什么时候用低代码，什么时候必须写代码？

【用低代码的信号】
✅ 需求是标准CRUD（增删改查）
✅ 需要快速原型验证（2周内MVP）
✅ 业务规则用条件判断能覆盖
✅ 使用者是业务团队，非技术人员主导
✅ 预算有限，快速交付优先

【必须用全代码的信号】
❌ 毫秒级响应的高性能系统
❌ 复杂算法（机器学习/推荐系统/加密）
❌ 与旧系统深度集成且无API
❌ 高度定制化的交互体验
❌ 需要绕过低代码平台的数据权限

【NocoBase的独特优势】
API优先：所有数据操作自动生成REST API
→ 可以作为后端服务，前端用任意框架开发
→ 避免被低代码平台锁定，随时切换前端`),
  ],[
    R("国内资源",true,[
      {label:"NocoBase官方文档（中文）",url:"https://docs.nocobase.com/zh-CN",desc:"最权威中文文档，建议收藏"},
      {label:"NocoBase社区论坛",url:"https://forum.nocobase.com",desc:"社区答疑，中文帖子较多"},
      {label:"宜搭（阿里低代码平台）",url:"https://yida.alibaba-inc.com",desc:"阿里生态，钉钉集成，企业推荐"},
      {label:"轻流（国产低代码）",url:"https://qingflow.com",desc:"国产低代码SaaS，易上手"},
      {label:"B站：NocoBase从零搭建教程",url:"https://www.bilibili.com/search?keyword=NocoBase+教程",desc:"中文实战视频"},
      {label:"明道云（低代码+AI）",url:"https://www.mingdao.com",desc:"企业数字化低代码平台，含AI模块"},
    ]),
    R("国际资源",false,[
      {label:"NocoBase GitHub",url:"https://github.com/nocobase/nocobase",desc:"源码 + Issue讨论"},
      {label:"Retool（低代码内部工具）",url:"https://retool.com",desc:"国际主流低代码工具"},
    ]),
  ],[
    E(1,"CRM系统从零搭建","在NocoBase中设计并实现一个迷你CRM：① 数据模型：客户表/联系人表/商机表/跟进记录表（设计正确关联关系）② 视图：列表视图+看板视图（按商机阶段）③ 表单：快速录入表单 ④ 权限：销售只能看自己的客户。"),
    E(2,"审批工作流设计","创建「费用报销审批流」工作流：员工提交报销单→金额≤500元：直属领导一级审批；500-5000元：两级审批；>5000元：财务总监特批。包含：审批通知推送/驳回说明/自动生成月度报销汇总。"),
    E(3,"数据看板搭建","基于CRM数据创建管理层看板：① KPI卡片（本月新增客户/商机转化率/平均销售周期）② 折线图（近6个月商机趋势）③ 漏斗图（销售漏斗各阶段）④ 排行榜（销售员业绩排名）。实现数据实时刷新。"),
    E(4,"Webhook外部集成","配置NocoBase Webhook：当新建商机状态变为「成交」时，自动向企业微信/飞书发送恭喜通知（含客户名称/合同金额/销售员姓名），测试通知的准确性和实时性（延迟<5秒）。"),
    E(5,"低代码方案交付文档","整合以上实践，撰写一份「基于NocoBase的小型企业数字化方案」交付文档，包含：系统功能清单/数据库ER图/权限矩阵/工作流说明/部署成本估算/培训计划。模拟向客户交付的格式（Word/PDF）。"),
  ]),

  buildDay(13,2,"AI营销文案高级技巧","✍️",["文案","AIDA","A/B测试"],[
    T("营销心理学基础与转化机制",`高转化文案基于人类决策的认知偏差：

【七大心理触发器】
① 稀缺性（Scarcity）：限时/限量触发损失厌恶，研究显示可提升转化25-40%
② 社会认同（Social Proof）：「10万人选择」比「我们很好」有效3-5倍
③ 权威性（Authority）：专家背书/认证/数据降低决策风险
④ 互惠（Reciprocity）：先给价值（免费内容/样品），降低戒备心理
⑤ 锚定（Anchoring）：先展示高价再给折扣，感知价值比直接报折扣价高30%
⑥ 承诺一致（Consistency）：「您是否也认为效率很重要？」→「是」→「那这款AI工具正是为您设计的」
⑦ 情感共鸣（Empathy）：描述痛苦场景比描述产品功能更能打动人

【AI文案的核心价值】
AI不替代创意，AI擅长：快速生成大量变体→数据驱动选择最优`),
    T("AIDA到PASTOR框架演进",`经典框架进化史：

【AIDA框架（广告文案）】
Attention → Interest → Desire → Action
适合：简短广告、产品介绍、销售信

【PASTOR框架（内容营销/B2B）】
Problem（描述痛点）→ Amplify（放大痛苦）→ Story/Solution（解决方案叙事）→ Testimonial（社会证明）→ Offer（明确报价）→ Response（行动召唤）
适合：长文案、销售页、电子邮件序列

【Hook框架（短视频/社交媒体）】
Hook（前3秒抓注意力）→ Problem（确认用户痛点）→ Solution（呈现解决方案）→ Proof（证明有效）→ CTA（行动指引）
适合：抖音、小红书、视频号

【AI Prompt应用】
在Prompt中明确指定框架名称+目标受众+核心卖点+竞争优势，AI生成的文案框架完整性提升60%+`),
    T("多平台文案差异化策略",`同一产品，不同平台的内容逻辑根本不同：

【小红书】
风格：真实感/生活化/种草而非广告
结构：场景代入→亲身体验→推荐理由→避坑提示
禁忌：广告感强/硬推销/过多参数
黄金公式：「我在[场景]发现了[产品]，解决了[痛点]，效果是[结果]」

【抖音/视频号】
风格：强钩子/快节奏/情绪化
结构：0-3秒钩子→3-10秒问题→10-25秒解决→25-30秒CTA
钩子类型：反常识/悬念/痛点/数字冲击

【微信公众号】
风格：深度/信任感/故事性
结构：场景引入（200字）→核心洞察→解决方案→案例→行动
最佳长度：1500-2500字

【淘宝/京东详情页】
风格：功能全面/解决所有疑虑/规格清晰
结构：核心卖点→场景图→参数表→对比→用户评价→FAQ→售后保障`),
    T("A/B测试设计与数据驱动迭代",`文案A/B测试是将「感觉好」变为「数据好」的关键：

【标准A/B测试设计】
测试变量：一次只测试一个变量（标题/正文/CTA/图片）
样本量：每个版本至少500次曝光（或统计显著性p<0.05）
测试周期：至少7天（覆盖工作日+周末的流量差异）
核心指标：CTR（点击率）/ CVR（转化率）/ CAC（获客成本）

【AI文案A/B测试矩阵】
生成5种不同角度的标题：
① 数字型：「23天减掉8斤，我用了这个方法」
② 痛点型：「每天外卖费800元？这个习惯正在吞噬你的财富」
③ 好奇型：「99%的人不知道这个功能可以这样用」
④ 权威型：「哈佛医学院研究：这3个习惯让你少生10年病」
⑤ 场景型：「周一早晨9点，我完成了这件别人需要3天的工作」

【数据解读陷阱】
CTR高但CVR低 = 标题有吸引力但内容不一致
CVR高但CTR低 = 内容转化力好但曝光不足`),
  ],[
    R("国内资源",true,[
      {label:"5118关键词工具（SEO文案必备）",url:"https://www.5118.com",desc:"国内最强关键词挖掘，文案选题利器"},
      {label:"新榜（内容数据分析）",url:"https://www.newrank.cn",desc:"公众号/短视频内容趋势数据"},
      {label:"飞瓜数据（抖音内容分析）",url:"https://www.feigua.io",desc:"抖音爆款视频分析，竞品监控"},
      {label:"写作猫（AI文案润色）",url:"https://xiezuocat.com",desc:"AI文本润色+错误检查，适合国内文案"},
      {label:"创客贴（AI营销设计）",url:"https://www.chuangkit.com",desc:"文案+设计一体，一键生成营销海报"},
      {label:"B站：AI营销文案写作实战",url:"https://www.bilibili.com/search?keyword=AI营销文案+爆款标题+小红书",desc:"营销文案AI实战视频"},
    ]),
    R("国际资源",false,[
      {label:"Copy.ai（英文文案AI）",url:"https://copy.ai",desc:"英文市场文案参考"},
      {label:"Swipe File（文案案例库）",url:"https://swipefile.com",desc:"世界顶级营销文案案例收集"},
    ]),
  ],[
    E(1,"心理触发器文案实验","为同一产品（自选）创作7条文案，每条主打一个心理触发器（稀缺/认同/权威/互惠/锚定/一致性/共鸣），请10位朋友盲测选出最打动人的3条，分析心理机制和受众特征的关系。"),
    E(2,"PASTOR长文案创作","用PASTOR框架为一款AI效率工具撰写一篇1000字的销售页文案，包含：虚构但真实感强的用户案例/具体数据（节省时间比例等）/明确报价和保障。每个框架模块用标题标注，输出后自评各模块的说服力（1-10分）。"),
    E(3,"一稿多平台改编","创作一篇核心内容（产品深度介绍，800字），通过AI改编为5个平台版本：① 小红书种草文300字 ② 抖音视频脚本15秒 ③ 微博话题140字 ④ 微信朋友圈100字 ⑤ LinkedIn英文200字，对比各版本的风格差异。"),
    E(4,"风格学习与模仿训练","选3个你喜欢的品牌（如：苹果/三顿半/元气森林），分别分析其官方文案的：用词特点/句式风格/情感温度/价值主张，然后用AI为同一个新产品生成3种不同品牌风格的文案，评估相似度。"),
    E(5,"A/B测试完整方案","为一个实际的营销场景（自选：公众号推送/小红书帖子/落地页标题），设计一套完整的A/B测试方案：① 5个测试版本（覆盖5种标题框架）② 测试目标和成功指标 ③ 样本量计算 ④ 测试周期 ⑤ 数据分析方法 ⑥ 预测各版本表现。"),
  ]),

  buildDay(14,2,"第2周综合实战","🏅",["综合实战","工具整合","作品集"],[
    T("AI工具链集成思维",`第2周学习回顾与核心思维升华：

本周掌握的工具和能力：
✅ 扣子Bot：零代码搭建AI对话应用
✅ Dify工作流：复杂多步骤AI任务编排  
✅ ComfyUI：专业图像生成工作流（基础+LoRA+ControlNet）
✅ 知识库工程：文档处理/向量检索/质量评估
✅ NocoBase：低代码数据管理系统
✅ 电商客服话术：意图识别/RAG/高难场景处理
✅ 营销文案：AIDA/PASTOR/多平台改编/A/B测试

【工具链集成的核心价值】
单工具能力有限，真正的生产力来自工具链协同：
Dify工作流（任务编排）+ ComfyUI（图像）+ 扣子（对话）+ NocoBase（数据管理）
= 完整的AI驱动内容生产和运营系统`),
    T("MVP思维与快速商业验证",`掌握工具后，最重要的是验证商业价值：

【MVP（最小可行产品）核心原则】
不要一开始就追求完美，先用最简单的工具组合验证业务价值

判断AI应用的3个关键问题：
① 能节省多少人工时间？（效率价值）
② 输出质量是否达到可用门槛？（质量门槛）
③ 是否比现有方案更快/更便宜？（竞争优势）

【第2周综合实战目标】
交付一个能解决真实业务问题的AI解决方案原型，而非技术Demo`),
    T("作品集构建策略",`AI技能的证明依赖可见的作品，而非证书：

【高价值作品集要素】
• 有明确业务场景（解决什么问题）
• 有工具链展示（使用了哪些AI工具）
• 有量化效果（节省多少时间/提升多少转化）
• 有可复现的流程（方法论可以教给别人）

【本周作品清单建议】
① 完整客服Bot（扣子/Dify）→ 录屏展示
② ComfyUI产品图工作流 → 前后对比图
③ NocoBase系统截图 → 数据看板
④ 营销文案A/B测试报告 → 数据说话`),
    T("第3周预告与学习路径",`接下来的重点方向：

【第3周核心内容】
Day 15：短视频分镜脚本设计（AI辅助短视频创作）
Day 16：剪映AI功能深度（字幕/配音/剪辑自动化）
Day 17：Dify工作流进阶（多节点复杂任务）
Day 18：n8n自动化工作流（跨系统集成）
Day 19：AI产品图完整工作流（ComfyUI高级）
Day 20：大模型API调用基础（Python入门）
Day 21：第3周综合实战

【核心能力跃迁】
从「会用工具」→「能设计工作流」→「能集成系统」
这是AI工程师与AI用户的本质区别`),
  ],[
    R("国内资源",true,[
      {label:"即刻App（AI创业者社区）",url:"https://web.okjike.com",desc:"AI从业者聚集地，最新资讯"},
      {label:"飞书妙记（录屏+会议纪要）",url:"https://www.feishu.cn/product/minutes",desc:"AI自动生成会议纪要和任务清单"},
      {label:"Gamma（AI生成PPT）",url:"https://gamma.app",desc:"输入主题自动生成美观幻灯片"},
      {label:"ProcessOn（项目流程图）",url:"https://www.processon.com",desc:"在线画流程图和思维导图"},
      {label:"石墨文档（在线协作）",url:"https://shimo.im",desc:"国内协作文档，支持多人实时编辑"},
    ]),
    R("国际资源",false,[
      {label:"Notion（知识管理+作品集）",url:"https://notion.so",desc:"搭建个人作品集和项目管理"},
      {label:"Product Hunt（AI新工具发现）",url:"https://producthunt.com",desc:"每日发现最新AI工具"},
    ]),
  ],[
    E(1,"综合项目：AI内容工厂","整合本周所学，为一个虚拟品牌搭建完整「AI内容工厂」：① ComfyUI+LoRA生成品牌风格图库（≥8张）② Dify工作流自动生成多平台文案套件 ③ 扣子搭建品牌知识库问答Bot ④ NocoBase建立内容管理数据库 ⑤ 设计自动化发布工作流。整理为可交付的品牌内容包。"),
    E(2,"系统集成方案设计","设计一个「AI驱动的电商品牌全链路运营系统」架构图，包含：内容生产（ComfyUI/Dify）→客户服务（扣子Bot）→数据管理（NocoBase）→自动化流程（工作流）的完整集成关系，标注每个节点的工具和数据流向。"),
    E(3,"效率量化报告","对本周完成的所有AI辅助任务进行效率测算：每项任务的传统方式耗时 vs AI辅助耗时，计算效率提升倍数，计算如果一个人全职从事该类工作，每月可节省多少小时/创造多少额外价值。"),
    E(4,"第2周复盘文档","撰写第2周学习复盘文档（800字以上）：① 工具掌握度自评（7个工具，各1-10分）② 3个最大收获 ③ 2个待解决问题 ④ 第3周重点学习计划 ⑤ 你认为哪个工具的商业价值最高，为什么。"),
    E(5,"作品集展示页制作","用Notion或Gamma.app制作你的「第2周AI学习作品集页面」，包含：本周产出内容的截图/链接/说明，用可视化方式展示你的能力成长，分享给1位朋友收集反馈并记录反馈内容。"),
  ]),

  // ─── WEEK 3 ─────────────────────────────────────────────────────────────────
  buildDay(15,3,"AI短视频分镜脚本设计","🎞️",["分镜","脚本","影像语言"],[
    T("注意力经济与短视频叙事原理",`短视频是当前注意力竞争最激烈的内容形态：

【人类注意力曲线】
0-3秒：决定是否继续看（钩子阶段，决定70%留存率）
3-15秒：确认是否值得继续（价值确认阶段）
15-30秒：深度投入阶段（完播率关键区间）
30秒+：忠实用户阶段

【AI辅助脚本的核心价值】
传统脚本创作：创意→文字→分镜→拍摄，完全线性
AI辅助创作：输入主题→AI生成10个方向→人工选优→AI细化分镜→批量输出，效率提升5-10倍

【爆款短视频的结构规律】
钩子（悬念/反常识/痛点）→ 展开（价值内容）→ 高潮（核心信息） → 行动（CTA）`),
    T("分镜脚本的专业要素",`专业分镜脚本包含以下维度：

【镜头语言基础】
景别：远景（建立环境）/ 全景（交代全貌）/ 中景（叙事主体）/ 近景（情感传达）/ 特写（细节冲击）

运镜：固定镜头（稳定/权威）/ 推进（紧张/强调）/ 拉远（宏观/总结）/ 跟拍（临场感）

切换节奏：快剪（节奏感）/ 慢切（情感沉淀）/ J-cut/L-cut（音画错位）

【AI生成分镜的Prompt格式】
"你是专业短视频导演。请为以下主题创作15秒分镜脚本：[主题]
格式要求：
| 时间 | 景别 | 画面内容 | 台词/文字 | 音效/BGM |
每个分镜控制在2-3秒，共5-7个分镜。风格：[风格]，目标平台：[平台]"`),
    T("不同平台的脚本差异化策略",`各平台算法偏好决定脚本结构：

【抖音（完播率为王）】
• 前3秒必须有钩子（悬念/数字冲击/反常识）
• 节奏快，建议每2-3秒一个切换
• 结尾要有「下集预告」提升关注率

【小红书（价值感知优先）】
• 前3秒可以稍慢，但必须展示「干货量」
• 真实感比精良制作更重要
• 文字+旁白双轨，方便静音浏览

【视频号（情感共鸣优先）】
• 前5秒建立情感连接（共情钩子）
• 适合故事性叙事，完整起承转合
• 长度可到60-90秒，不必强行压缩`),
    T("数字人与AI配音在脚本中的应用",`无需出镜的短视频制作技术路线：

【全AI流程】
文字脚本 → AI配音（豆包/剪映TTS）→ 数字人口型匹配（HeyGen/硅基流动）→ AI生成背景视频（可灵/即梦）→ 剪映合成

【AI配音工具对比】
豆包AI配音：中文自然度最高，免费额度大
剪映TTS：与剪映工作流无缝集成，音色丰富
ElevenLabs：英文最强，情感表达出色（需梯子）

【数字人制作流程】
上传主播形象照 → HeyGen/硅基流动 → 输入文字脚本 → 生成口型同步视频 → 导入剪映后期
成本对比：真人出镜拍摄≈200-2000元/条 vs 数字人制作≈5-50元/条`),
  ],[
    R("国内资源",true,[
      {label:"剪映专业版（脚本+剪辑一体）",url:"https://www.capcut.cn",desc:"文案转分镜，AI自动配音+字幕"},
      {label:"飞瓜数据（爆款视频分析）",url:"https://www.feigua.io",desc:"分析爆款短视频脚本结构"},
      {label:"硅基流动（AI数字人配音）",url:"https://cloud.siliconflow.cn",desc:"文字转语音，多种声线选择"},
      {label:"腾讯智影（AI分镜+视频）",url:"https://zenvideo.qq.com",desc:"脚本到视频一键生成"},
      {label:"新榜（内容数据）",url:"https://www.newrank.cn",desc:"短视频热门话题趋势"},
      {label:"B站：短视频AI脚本设计",url:"https://www.bilibili.com/search?keyword=AI短视频脚本+分镜设计",desc:"分镜脚本实战教程"},
    ]),
    R("国际资源",false,[
      {label:"HeyGen（数字人视频）",url:"https://heygen.com",desc:"AI数字人，国际版"},
      {label:"Runway（AI视频素材）",url:"https://runwayml.com",desc:"AI视频生成与特效"},
    ]),
  ],[
    E(1,"爆款脚本结构拆解","在抖音/小红书各找5个同类目的爆款视频（完播率高/点赞多），分析每个视频的：开头钩子类型/内容结构/节奏/结尾CTA，制作「爆款规律提炼表」，总结该赛道的高转化脚本公式。"),
    E(2,"AI批量脚本生成实验","用AI为同一主题（如：学习AI的好处）生成5种不同钩子风格的15秒脚本（悬念/数字/痛点/反常识/场景），对每个脚本进行「预估完播率」评分（1-10分），分析哪种钩子最适合你的目标受众。"),
    E(3,"分镜脚本转视觉素材","选出最优脚本，将每个分镜转化为AI生成的视觉素材：用即梦/可灵生成对应画面，制作「脚本-素材对照表」，评估AI生成素材与脚本要求的匹配度，记录需要人工调整的部分。"),
    E(4,"数字人视频制作","使用HeyGen或硅基流动制作一条30秒以内的数字人讲解视频：准备文字脚本→生成数字人→导入剪映添加字幕和背景→导出成品。对比数字人视频与真人出镜的视觉效果，分析各自适用场景。"),
    E(5,"完整短视频内容包交付","为一个真实或虚拟品牌制作一套完整短视频内容包：① 爆款脚本×3条（3种不同风格）② 每条脚本对应的AI视觉素材 ③ 配音文案 ④ 字幕文案 ⑤ 发布时间和话题标签建议。模拟向客户交付格式。"),
  ]),

  buildDay(16,3,"剪映AI功能深度使用","✂️",["剪映","AI剪辑","字幕配音"],[
    T("剪映AI功能技术架构",`剪映的AI能力基于多个专有模型：

【语音识别（ASR）】
底层：字节自研的火山语音识别引擎
准确率：中文普通话>95%，方言80%+
支持：中英文混合识别，自动断句
功能：「自动字幕」一键识别视频中的人声，生成逐字字幕

【智能剪辑算法】
「图文成片」：输入文章 → AI自动匹配素材+剪辑+配音+字幕
底层：文本语义分析→素材检索→时间轴自动排布
「智能踩点」：分析音乐节拍 → 自动在鼓点处切换镜头

【文字转语音（TTS）】
基于神经网络TTS，支持100+音色，情感控制
「克隆声音」：上传3分钟音频 → AI复刻你的声音（个人创作专用）`),
    T("自动字幕与多语言处理",`字幕是短视频的第二内容层，用户60%以上静音浏览：

【字幕工作流程】
导入视频 → 文字 → 自动字幕 → AI识别 → 人工校对 → 调整样式 → 导出

【字幕优化技巧】
• 每行字幕不超过16个字（手机屏幕最佳阅读量）
• 关键词单独成行，视觉节奏感更强
• 字幕与画面内容形成「对比」效果（画面展示A，字幕说B）
• 花字效果：关键词使用大号+颜色强调

【多语言字幕生成】
中文视频 → AI翻译 → 英文字幕：适合出海内容
英文视频 → AI翻译 → 中文字幕：适合学习外文素材
注意：AI翻译准确率约85-90%，专业词汇需人工校对`),
    T("图文成片与AI创作模式",`剪映「图文成片」是效率最高的内容生产工具之一：

【使用流程】
输入文章/脚本文字 → 选择视频风格（科技/生活/商务等）→ AI自动完成：
  ① 文本断句和分段
  ② 每段匹配素材视频（从素材库搜索）
  ③ 配置TTS配音
  ④ 生成字幕
  ⑤ 添加背景音乐

【适用场景】
知识科普类：文章→直接生成视频版本
产品介绍：产品文案→快速生成展示视频
新闻资讯：文章摘要→配合素材生成信息流视频

【局限性】
• 素材匹配度有时不准确，需人工替换
• 生成风格较为统一，个性化程度有限
• 专业品牌内容建议只用作初稿`),
    T("剪辑效率工作流设计",`专业剪辑效率提升的系统方法：

【批量处理技巧】
• 「智能字幕」批量处理：一次上传多个视频，批量生成字幕
• 「模板创作」：制作标准模板，批量套用不同素材（适合日更账号）
• 「草稿复用」：将成功作品结构另存为模板

【素材管理规范】
文件夹分类：原始素材/AI生成素材/已剪辑片段/成品导出
命名规范：日期_项目名_版本号（如：20240315_护肤品开箱_v2）
素材快捷标记：绿色=可用/黄色=备选/红色=废弃

【导出规范】
抖音：1080×1920（9:16），码率10Mbps，H.264
小红书：1080×1440（3:4），同上
横版平台：1920×1080（16:9）
HDR内容注意：Dolby Vision仅在特定设备显示效果`),
  ],[
    R("国内资源",true,[
      {label:"剪映官方教程中心",url:"https://www.capcut.cn/resource",desc:"官方出品，最权威的剪映使用指南"},
      {label:"剪映创作学院（B站）",url:"https://www.bilibili.com/search?keyword=剪映创作学院+AI+教程",desc:"官方认证教程，AI功能专项"},
      {label:"必剪（B站官方剪辑工具）",url:"https://bcut.bilibili.cn",desc:"B站官方剪辑软件，上传友好"},
      {label:"一帧秒创（AI视频创作）",url:"https://aigc.yizhentv.com",desc:"文章自动转短视频，运营效率工具"},
      {label:"腾讯智影（AI字幕+配音）",url:"https://zenvideo.qq.com",desc:"AI字幕+声音克隆，企业视频利器"},
    ]),
    R("国际资源",false,[
      {label:"CapCut国际版",url:"https://www.capcut.com",desc:"剪映海外版，同等功能"},
      {label:"Adobe Premiere Pro AI功能",url:"https://www.adobe.com/products/premiere.html",desc:"专业级AI视频剪辑"},
    ]),
  ],[
    E(1,"自动字幕全流程实战","找一段10分钟以上的视频（讲座/采访/Vlog），用剪映自动字幕功能生成字幕，人工校对并统计：识别准确率（错误字数/总字数）、需要人工修正的平均时间，对比手动打字幕的效率提升倍数。"),
    E(2,"图文成片批量测试","准备5篇不同风格的文章（科技/美食/情感/教育/商业），分别用「图文成片」生成视频，评估每篇文章的：素材匹配度（1-10）/配音自然度/整体质量，总结哪类文章最适合图文成片。"),
    E(3,"模板化生产工作流","为一个日更账号设计标准化生产模板：① 设计固定片头3秒 ② 固定片尾5秒含订阅引导 ③ 设计字幕样式规范 ④ 选定3套BGM（不同情感基调）。将模板保存，测试套用新素材所需时间（目标<10分钟/条）。"),
    E(4,"AI配音克隆实验","用剪映「克隆声音」功能录制3分钟音频，克隆自己的声音，然后用克隆声音为一段脚本配音。对比克隆声音与真实声音的相似度，测试克隆声音在不同情感表达（平静/激动/友善）下的表现。"),
    E(5,"完整短视频生产计时赛","挑战：从拿到脚本到完成一条30秒短视频（含字幕/配音/背景音乐/片头片尾），全程使用剪映AI功能，记录每个环节的耗时，目标总时间<30分钟。输出「AI辅助剪辑SOP」文档，供未来复用。"),
  ]),

  buildDay(17,3,"Dify工作流进阶：多节点组合","🔄",["Dify","工作流","自动化"],[
    T("DAG有向无环图执行模型",`Dify工作流基于DAG（Directed Acyclic Graph，有向无环图）执行引擎：

【DAG核心特性】
• 有向：数据从上游节点流向下游节点，方向明确
• 无环：不允许形成循环依赖（避免无限循环）
• 并行执行：无依赖关系的节点可以同时运行，提升效率

【执行调度原理】
拓扑排序 → 确定节点执行顺序 → 并行执行无依赖节点 → 等待最慢节点完成 → 继续下游

【变量传递机制】
每个节点的输出以变量形式存储：{{节点名称.输出字段名}}
例：LLM节点输出 → {{llm_1.text}} → 可在后续节点的Prompt中引用
变量类型：文本/数字/数组/对象/文件

【工作流调试技巧】
使用「单步调试」模式：逐节点执行，查看每步的输入输出
使用「日志」功能：生产环境中查看历史执行记录和错误信息`),
    T("迭代节点与批处理设计",`迭代节点（Iterator）是处理列表数据的核心能力：

【使用场景】
• 批量处理：对列表中每个元素执行相同操作
• 多轮生成：生成N个候选结果
• 结构化提取：从多段文本中逐一提取信息

【设计模式】
输入：数组变量（如：文章列表/产品SKU列表）
迭代体：对每个元素执行的子工作流
输出：处理结果数组

【实际案例：批量SEO优化】
输入：10篇文章标题列表
迭代体：① 分析标题关键词密度 ② LLM生成3个优化版本 ③ 评分选最优
输出：10个优化后标题

【并行 vs 迭代的选择】
并行节点：任务数量固定（如：同时调用3种LLM对比结果）
迭代节点：任务数量动态（如：处理用户上传的N个文件）`),
    T("ReAct Agent架构设计",`ReAct（Reasoning + Acting）是Dify Agent的核心架构：

【ReAct循环】
Thought（思考：下一步做什么？）
→ Action（执行：调用哪个工具？输入什么？）
→ Observation（观察：工具返回了什么？）
→ Thought（再思考：结果是否满足需求？）
→ 循环直到完成任务

【工具调用设计原则】
• 每个工具功能单一：搜索工具只搜索，不处理结果
• 工具描述要精准：LLM根据描述判断何时调用
• 设置最大迭代次数：防止无限循环（建议5-10次）
• 错误处理：工具调用失败时的降级策略

【Agent vs 工作流的选择】
工作流：步骤固定、逻辑可预测、需要稳定性
Agent：步骤灵活、需要自主决策、允许不确定性`),
    T("复杂工作流的测试与监控",`生产级工作流必须有完善的测试和监控体系：

【分层测试策略】
单元测试：每个节点单独测试（输入→预期输出）
集成测试：关键路径端到端测试
压力测试：并发10/50/100个请求，检查稳定性
边界测试：极端输入（超长文本/空输入/特殊字符）

【关键监控指标】
响应时间：P50/P95/P99延迟（99%请求的延迟上限）
成功率：非错误完成的比例（目标>99%）
Token消耗：每次执行的模型调用成本
节点失败率：定位哪个节点最容易出错

【成本优化策略】
• 将昂贵的LLM节点（GPT-4o）替换为高性价比模型（通义/豆包）用于非核心步骤
• 添加缓存层：相同输入命中缓存，不重复调用LLM
• 流式输出：启用Streaming模式，降低用户等待感知`),
  ],[
    R("国内资源",true,[
      {label:"Dify官方文档（工作流）",url:"https://docs.dify.ai/zh-hans/guides/workflow",desc:"官方工作流设计指南"},
      {label:"Dify官方模板库",url:"https://cloud.dify.ai/explore/apps",desc:"现成工作流模板，直接复用"},
      {label:"阿里云百炼（工作流编排）",url:"https://bailian.console.aliyun.com",desc:"阿里云企业级工作流编排平台"},
      {label:"FastGPT工作流编排",url:"https://fastgpt.in",desc:"国产RAG+工作流，开源可私有化"},
      {label:"B站：Dify工作流进阶案例",url:"https://www.bilibili.com/search?keyword=Dify+工作流+进阶+案例",desc:"国内最新Dify实战视频"},
    ]),
    R("国际资源",false,[
      {label:"LangGraph（复杂Agent框架）",url:"https://github.com/langchain-ai/langgraph",desc:"状态机式Agent工作流"},
      {label:"Flowise（开源低代码工作流）",url:"https://flowiseai.com",desc:"拖拽式LLM应用构建工具"},
    ]),
  ],[
    E(1,"并行节点性能实验","设计一个并行工作流：同时向豆包/通义千问/Kimi三个LLM发送同一个问题，分别收集回答，再用第四个LLM节点综合评估三个回答并选出最优。测试并行执行比串行执行节省多少时间。"),
    E(2,"迭代批处理实战","构建批量内容优化工作流：输入一个包含10条产品描述的列表 → 迭代节点逐条处理 → 每条：关键词提取+SEO评分+优化建议 → 汇总输出优化报告。测试处理10条 vs 50条的稳定性。"),
    E(3,"ReAct Agent搭建","在Dify中搭建一个具备工具调用能力的Agent：配置工具：联网搜索+计算器+知识库检索，让Agent自主完成：「帮我分析2024年AI行业的投资趋势，给出数据支撑的结论」，观察ReAct推理过程。"),
    E(4,"错误处理与降级策略","为你的关键工作流添加完善的错误处理：① 每个HTTP节点添加失败分支（返回默认值）② LLM节点添加备用模型（主模型失败→切换备用）③ 设置全局错误通知（失败时发送飞书/企微消息）。模拟3种故障场景测试有效性。"),
    E(5,"工作流成本分析报告","记录你搭建的所有Dify工作流在一周内的运行数据：执行次数/平均响应时间/Token消耗/成功率，计算每次执行的实际成本（API费用），预测规模化到1000次/天的月成本，提出成本优化方案。"),
  ]),

  buildDay(18,3,"n8n自动化工作流入门","⚙️",["n8n","自动化","API连接"],[
    T("n8n的Webhook机制与事件驱动",`n8n基于事件驱动架构，Webhook是最核心的触发方式：

【Webhook工作原理】
外部服务（如：飞书/微信/表单）→ 发送HTTP POST请求到n8n的Webhook URL → n8n接收并触发工作流

【Webhook vs 轮询的区别】
轮询：n8n每隔N秒主动去查询数据变化（低效/有延迟）
Webhook：外部服务主动通知n8n（实时/高效）
类比：轮询=每5分钟查一次快递到了没；Webhook=快递员主动发消息给你

【常用触发节点】
Webhook触发：接收HTTP请求
定时触发（Cron）：按时间表执行（如：每天早9点）
邮件触发：新邮件到达时触发
表单触发：表单提交时触发（内置表单或Typeform）

【n8n vs Dify的分工】
n8n：跨系统集成（连接100+第三方服务），数据路由和格式转换
Dify：AI推理和生成，知识库检索
最佳实践：n8n捕获事件→传给Dify处理→n8n分发结果`),
    T("节点类型与数据转换",`n8n的节点生态系统包含400+集成：

【核心节点分类】
触发器：Webhook/定时/邮件/数据库变化/API轮询
动作：创建/读取/更新/删除各平台数据
转换：数据格式转换/过滤/聚合/合并
逻辑：IF/Switch/Merge/Split
代码：Function节点（JavaScript）/Execute Command（Shell）

【数据流格式（JSON）】
n8n内部数据以JSON数组传递，每个元素是一个「item」
{ "json": { "field1": "value", "field2": 123 } }

【常用数据转换技巧】
提取字段：{{$json["fieldName"]}}
字符串处理：{{$json.text.toLowerCase()}}
日期格式化：{{DateTime.now().toFormat('yyyy-MM-dd')}}
条件表达式：{{$json.status === 'active' ? '启用' : '禁用'}}`),
    T("AI+自动化的融合设计",`n8n与AI工具的集成是当前最有价值的应用方向：

【典型集成场景】
场景1：智能邮件处理
新邮件到达 → n8n读取内容 → 发送到GPT-4o分析意图 → IF重要：标记+转发；IF垃圾：自动归档

场景2：内容自动发布
触发：每天9:00 → n8n读取内容数据库 → 发送到Dify生成当天文案 → 自动发布到小红书/微博

场景3：客服升级自动化
扣子Bot无法回答 → 触发Webhook → n8n记录问题 → 分类发给对应部门 → 通知客服人员

【n8n内置AI节点】
OpenAI节点：直接调用GPT系列API
Anthropic节点：调用Claude API
AI Agent节点：内置ReAct Agent
文档摘要：自动处理长文档`),
    T("n8n部署与安全配置",`生产环境使用n8n的关键配置：

【部署选项】
n8n Cloud（官方托管）：最简单，月费$20起，适合个人和小团队
Docker自部署：完全控制，数据私有，适合企业
   docker run -it --rm --name n8n -p 5678:5678 n8nio/n8n

【关键安全设置】
身份认证：必须开启Basic Auth或OAuth（默认无认证！）
Webhook URL保密：不要在公开场合分享Webhook URL
敏感数据加密：API Key等存入n8n Credentials，不要硬编码
访问日志：开启执行历史记录，便于审计

【性能优化】
数据库：生产环境用PostgreSQL（默认SQLite不适合高并发）
队列模式：高负载场景开启队列处理（Redis+Worker）
执行超时：设置合理的超时时间（建议30-120秒）`),
  ],[
    R("国内资源",true,[
      {label:"n8n中文社区",url:"https://n8n.io/community",desc:"搜索中文节点和工作流分享"},
      {label:"阿里云ECS（自部署n8n）",url:"https://www.aliyun.com/product/ecs",desc:"最低20元/月，搭建私有n8n"},
      {label:"腾讯云轻量服务器",url:"https://cloud.tencent.com/product/lighthouse",desc:"国内节点，部署n8n低延迟"},
      {label:"飞书开放平台（n8n集成）",url:"https://open.feishu.cn",desc:"n8n接入飞书机器人/审批流程"},
      {label:"B站：n8n自动化工作流教程",url:"https://www.bilibili.com/search?keyword=n8n+自动化+工作流+教程",desc:"中文n8n从零搭建视频"},
    ]),
    R("国际资源",false,[
      {label:"n8n官方文档",url:"https://docs.n8n.io",desc:"节点文档，必看"},
      {label:"Make（n8n竞品，更易上手）",url:"https://make.com",desc:"拖拽式自动化，适合非开发者"},
    ]),
  ],[
    E(1,"Webhook接收与处理","创建一个n8n Webhook触发工作流：配置接收端点 → 用Postman/Apifox发送测试数据 → n8n接收并解析JSON → 条件判断（字段值）→ 分别输出处理结果。测试5种不同的输入数据，验证处理逻辑正确性。"),
    E(2,"跨平台数据同步","构建双向数据同步工作流：当飞书多维表格新增一行时 → 自动同步到NocoBase数据库；当NocoBase数据更新时 → 自动更新飞书表格。测试数据一致性，记录同步延迟（目标<5秒）。"),
    E(3,"AI邮件助手","搭建智能邮件处理工作流：定时检查新邮件（每30分钟）→ 用AI分析邮件类型（咨询/投诉/合同/垃圾）→ 生成回复草稿 → 重要邮件发企微提醒 → 普通咨询自动回复 → 所有处理记录到数据库。"),
    E(4,"内容自动发布流水线","设计内容自动发布工作流：每天8:00触发 → 从Airtable/飞书读取「待发布内容」→ 调用Dify生成平台适配版本 → 自动发布到对应平台（模拟，不真实发布）→ 更新发布状态 → 发送日报汇总。"),
    E(5,"n8n商业方案设计","为一家中小型电商企业（100人规模）设计完整的n8n自动化方案：识别5个最高价值的自动化场景，每个场景描述：触发条件/工作流步骤/预期收益/实施难度。输出「自动化升级路线图」PPT（用Gamma.app生成）。"),
  ]),

  buildDay(19,3,"AI产品图完整工作流","🛍️",["ComfyUI","产品图","Inpaint"],[
    T("产品图生产的工业化流程",`专业电商产品图有严格的质量标准和生产流程：

【电商平台规格要求】
淘宝/天猫主图：800×800或1000×1000像素，<3MB，白底或场景图
京东：800×800，纯白底（RGB 255,255,255）
小红书封面：1080×1440，可有生活场景
抖音商品图：9:16竖版，更注重视觉冲击

【传统摄影 vs AI生成成本对比】
传统摄影：摄影棚租金+摄影师+后期 = 500-5000元/SKU
AI生成：模型训练+批量生成 = 10-100元/SKU，效率提升10-50倍
混合方案（最优）：真实产品拍摄→AI背景替换+场景合成

【产品图AI生产五步法】
① 产品主图提取（去背景）→ ② 场景素材生成 → ③ 合成与光影融合 → ④ 细节修复（Inpaint）→ ⑤ 批量导出规格化`),
    T("Inpainting局部重绘技术",`Inpainting是产品图后期处理的核心技术：

【技术原理】
用户绘制遮罩（Mask）→ 标记需要修改的区域 → 扩散模型只在遮罩区域重新生成 → 非遮罩区域保持不变

【关键参数】
Denoise Strength（0.0-1.0）：
  0.3-0.5：轻微修改（补光/修瑕疵/去水印）
  0.5-0.7：中等修改（替换背景局部/添加元素）
  0.7-0.9：大幅修改（完全重绘遮罩区域）

Inpaint Area：
  Only Masked：只处理遮罩区域（速度快/推荐）
  Whole Picture：处理整张图（保证全局一致性）

【常见应用场景】
• 产品瑕疵修复：划痕/灰尘/反光去除
• 背景替换：白底→场景图，无需重拍
• 标签/水印去除：AI无缝填充
• 元素添加：为产品添加阴影/反光/配件`),
    T("产品LoRA训练基础",`训练专属产品LoRA，实现「在任意场景中精确复现产品外观」：

【为什么要训练产品LoRA】
问题：用Prompt描述产品往往生成「类似的」产品，而非「这个」产品
解决：训练包含该产品特征的LoRA，用特殊Token触发精确外观

【训练数据准备】
数量：10-30张（多角度/多光线/同一产品）
质量：清晰度>512px，产品占画面60%以上
多样性：正面/侧面/俯视/细节特写

【推荐工具】
kohya-ss：功能最全，支持所有LoRA训练配置
AutoTrain（HuggingFace）：一键训练，无需配置
训练时长：消费级GPU（4090）约20-60分钟

【激活词设计】
使用独特的激活词（Trigger Word），如：myproduct_v1
在Prompt中加入激活词即可触发产品外观`),
    T("场景合成与光影一致性",`AI合成最难的挑战是光影一致性：

【光影不一致的表现】
• 产品阴影方向与背景光源不符
• 产品色温与背景色温不匹配
• 产品边缘有明显的「抠图感」

【ComfyUI光影融合工作流】
产品图（已去背）→ IC-Light节点（重新打光）→ 背景合成 → Inpaint边缘处理

IC-Light：Lvmin Zhang开发的光照条件控制模型
• 可以根据背景环境自动调整产品光照
• 生成符合场景的阴影和反光
• 显著提升合成自然度

【实用技巧】
• 背景生成时保留光源描述：「右上方45度暖光照射的木质桌面」
• 产品抠图时保留原始阴影，在新背景上叠加
• 添加「地面阴影」：用椭圆渐变软化处理`),
  ],[
    R("国内资源",true,[
      {label:"LiblibAI产品图工作流",url:"https://www.liblib.art",desc:"下载现成电商产品图工作流"},
      {label:"即梦AI（产品场景图）",url:"https://jimeng.jianying.com",desc:"输入产品描述自动生成场景"},
      {label:"稿定AI（商品图批量生成）",url:"https://www.gaoding.com",desc:"电商主图批量生成，运营必备"},
      {label:"创客贴（产品图设计）",url:"https://www.chuangkit.com",desc:"AI+模板快速出图，适合小卖家"},
      {label:"B站：ComfyUI产品图完整教程",url:"https://www.bilibili.com/search?keyword=ComfyUI+产品图+电商+教程",desc:"产品图从拍摄到AI精修完整流程"},
    ]),
    R("国际资源",false,[
      {label:"IC-Light（光照控制）",url:"https://github.com/lllyasviel/IC-Light",desc:"产品图补光/打光AI工具"},
      {label:"Segment Anything（精准抠图）",url:"https://segment-anything.com",desc:"Meta出品，精准主体识别"},
    ]),
  ],[
    E(1,"产品抠图+白底图批量生产","为5个不同类别的产品（服装/电子/食品/家居/美妆各1个）制作白底图工作流：使用ComfyUI的SAM抠图节点精确提取产品 → 白底合成 → 质量检查。统计成功率和平均处理时间，对比人工Photoshop处理的效率。"),
    E(2,"Inpaint产品修复实战","收集5张有明显瑕疵的产品图（反光/划痕/标签/背景杂物），用ComfyUI Inpaint工作流修复每一处瑕疵。记录每处修复的：遮罩大小/Denoise强度/成功率，输出「修复前后对比图」。"),
    E(3,"多场景产品图生产","选一款产品（建议日用品），设计5种不同的使用场景（家庭/办公/户外/咖啡馆/极简白），用ComfyUI+场景LoRA批量生成，确保每个场景图的产品外观保持一致。评估光影融合自然度。"),
    E(4,"产品LoRA数据集准备","为一款真实产品（或自制产品模型）准备LoRA训练数据集：① 拍摄/搜集15-20张多角度图片 ② 用ComfyUI批量裁剪为512×512 ③ 清洗质量不达标的图片 ④ 编写对应的文字描述文件（caption）。输出标准训练数据集。"),
    E(5,"电商品牌视觉方案交付","为一个虚拟电商品牌完整交付AI产品图方案：① 主图×5张（白底+4场景）② 详情页Banner×3张 ③ 社交媒体方图×3张。整理为品牌视觉规范文档，包含：色彩规范/光影风格/场景选择逻辑。模拟真实交付格式。"),
  ]),

  buildDay(20,3,"大模型API调用基础","🐍",["Python","API","LLM调用"],[
    T("HTTP请求与REST API基础",`调用大模型API的底层是HTTP请求：

【HTTP请求结构】
方法：POST（向服务器发送数据）
URL：https://api.openai.com/v1/chat/completions
Headers：
  Content-Type: application/json
  Authorization: Bearer {API_KEY}
Body（JSON格式）：
  {
    "model": "gpt-4o",
    "messages": [
      {"role": "system", "content": "你是助手"},
      {"role": "user", "content": "你好"}
    ],
    "temperature": 0.7,
    "max_tokens": 1000
  }

【Python调用示例】
import openai
client = openai.OpenAI(api_key="your_key")
response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role":"user", "content":"你好"}]
)
print(response.choices[0].message.content)`),
    T("国产API接入（通义/豆包/Kimi）",`国内大模型API具有价格和合规优势：

【通义千问API（性价比最高）】
from openai import OpenAI
client = OpenAI(
    api_key="your_dashscope_key",
    base_url="https://dashscope.aliyuncs.com/compatible-mode/v1"
)
# 通义兼容OpenAI格式，直接替换base_url即可

【价格对比（2024年参考）】
GPT-4o：$5/百万input tokens
通义千问-Long：¥0.5/百万tokens（约1/80价格）
豆包Pro-32K：¥0.8/百万tokens
Kimi moonshot-v1-8k：¥12/百万tokens

【推荐策略】
开发/测试阶段：通义千问（便宜）
生产环境核心功能：按质量需求选择
非核心任务（摘要/分类）：最便宜的模型`),
    T("Prompt工程与输出结构化",`生产级API调用的关键是结构化输出：

【JSON Mode强制结构化输出】
messages: [
  {"role":"system", "content":"你必须以JSON格式输出，格式为：{\"result\":string, \"confidence\":number, \"tags\":[]}"},
  {"role":"user", "content":"分析这段文字的情感：..."}
]
response_format: {"type": "json_object"}  // OpenAI特有参数

【Function Calling（工具调用）】
定义工具函数结构，让LLM决定调用哪个工具并生成参数：
functions = [{
  "name": "search_product",
  "description": "搜索商品信息",
  "parameters": {
    "type": "object",
    "properties": {
      "keyword": {"type": "string"},
      "category": {"type": "string", "enum": ["服装","电子","家居"]}
    }
  }
}]

【流式输出（Streaming）】
stream=True：逐token返回，改善用户体验（不需要等完整回复）
适合：对话界面/长文生成，不适合：批处理/结构化提取`),
    T("异步调用与批量处理",`生产环境中的高效API调用模式：

【同步 vs 异步调用】
同步：一个请求等响应后再发下一个（慢但简单）
异步：同时发出N个请求，并发处理（快但需要管理）

【Python asyncio异步示例】
import asyncio
import openai

async def call_llm(prompt):
    client = openai.AsyncOpenAI()
    resp = await client.chat.completions.create(
        model="gpt-4o-mini",
        messages=[{"role":"user","content":prompt}]
    )
    return resp.choices[0].message.content

async def batch_process(prompts):
    tasks = [call_llm(p) for p in prompts]
    results = await asyncio.gather(*tasks)
    return results

# 10个请求并发，时间从10×3秒→约3-5秒

【速率限制处理】
大多数API有RPM（每分钟请求数）和TPM（每分钟Token数）限制
实现指数退避重试：首次失败等1秒，再失败等2秒、4秒...`),
  ],[
    R("国内资源",true,[
      {label:"阿里云DashScope（通义API）",url:"https://dashscope.aliyun.com",desc:"通义千问API，价格最优惠之一"},
      {label:"智谱AI开放平台（GLM API）",url:"https://open.bigmodel.cn",desc:"GLM-4-Flash免费，适合练手"},
      {label:"月之暗面Kimi API",url:"https://platform.moonshot.cn",desc:"超长上下文API，文档处理首选"},
      {label:"火山引擎（豆包API）",url:"https://www.volcengine.com/product/ark",desc:"字节豆包API，性价比高"},
      {label:"百度千帆大模型平台",url:"https://qianfan.cloud.baidu.com",desc:"文心一言API，国内最早商用"},
      {label:"B站：大模型API调用实战",url:"https://www.bilibili.com/search?keyword=大模型+API+Python+调用+教程",desc:"从零开始API调用视频"},
    ]),
    R("国际资源",false,[
      {label:"OpenAI API文档",url:"https://platform.openai.com/docs",desc:"行业标准API参考"},
      {label:"Anthropic Claude API",url:"https://docs.anthropic.com",desc:"长上下文+强推理能力"},
    ]),
  ],[
    E(1,"第一个API调用","用Python调用通义千问API（DashScope），发送10条不同类型的消息（翻译/总结/创作/分类/提取），记录每次调用的响应时间和Token消耗，计算平均成本，感受API调用的基本模式。"),
    E(2,"结构化JSON输出","设计一个「产品评论分析」API调用：输入用户评论 → 要求LLM以JSON格式输出：{sentiment, score, keywords, main_issues, suggestions}。测试20条真实评论，统计JSON解析成功率，处理解析失败的异常情况。"),
    E(3,"批量处理对比实验","准备50条需要翻译的中文句子：① 同步串行调用（一条接一条）② Python asyncio并发调用（同时发10个）。对比两种方式的总耗时，计算并发效率提升倍数，分析不同并发数（5/10/20）的最优配置。"),
    E(4,"多模型路由系统","构建一个简单的多模型路由系统：根据任务类型自动选择最合适的模型：长文档分析→Kimi/通义Long；简单问答→豆包/通义Turbo；代码生成→通义Coder。实现cost_optimizer函数，计算每类任务的月度成本对比。"),
    E(5,"API集成自动化工具","用Python开发一个实用工具：批量处理Excel/CSV中的文本数据（如：批量翻译产品描述/批量生成SEO标题/批量情感分类），支持配置不同模型和Prompt模板，输出结果保存到新文件。这是你第一个可以交付给客户的AI工具。"),
  ]),

  buildDay(21,3,"第3周综合实战","🏅",["综合实战","多系统集成","作品集"],[
    T("系统集成思维",`第3周核心能力回顾：

本周你掌握的技术栈：
✅ 短视频脚本设计：钩子框架/分镜语言/平台差异
✅ 剪映AI全套功能：字幕/配音/图文成片/批量处理
✅ Dify工作流进阶：DAG/迭代节点/ReAct Agent
✅ n8n自动化：Webhook/跨系统集成/AI+自动化融合
✅ AI产品图工作流：Inpaint/场景合成/光影融合
✅ LLM API调用：Python调用/结构化输出/并发处理

【系统集成的核心价值】
各个工具孤立使用，效果是1+1=2
工具链集成使用，效果是1+1+1=10`),
    T("AI原生内容工作室的运营模式",`基于本周技术栈，可以运营一个AI原生内容工作室：

【标准交付物清单】
短视频内容包：脚本+视觉素材+配音+字幕+成品视频
电商视觉包：主图+场景图+详情页Banner+方图
文案内容包：多平台版本+A/B测试变体+SEO版本

【报价参考（行业参考，仅供学习）】
单条短视频（30秒，含脚本+制作）：500-2000元
产品主图×5张（含场景合成）：300-800元
品牌月度内容套餐：5000-20000元/月

【与客户合作的SOP】
需求沟通（1天）→ 提案确认（1天）→ AI批量生产（2-3天）→ 人工精修（1天）→ 客户审核（1天）→ 修改交付（1天）
总周期：约7天，相比传统方式缩短60-70%`),
    T("技术能力向商业价值的转化",`学会工具≠能赚钱，还需要：

【商业化路径】
路径1：内部应用（节省所在公司成本）→ 证明价值 → 加薪/晋升
路径2：自由职业（接外包项目）→ 建立口碑 → 提高单价
路径3：产品化（将工作流做成SaaS服务）→ 规模化收入

【定价心理学】
基于「成本定价」：计算AI工具费+人工时间×时薪 → 往往定价太低
基于「价值定价」：计算你帮客户节省了多少钱/赚了多少钱 → 更合理
例：帮一个100人的企业节省50%的内容创作成本（原来200万/年）→ 你应该收多少？

【作品集的关键要素】
展示「Before vs After」（效果对比）
展示「效率数据」（节省了多少时间）
展示「商业结果」（帮客户带来了什么ROI）`),
    T("第4周预告与能力升级计划",`接下来的重点方向：

【第4周核心内容（商业应用）】
Day 22：电商平台机器人配置（天猫/京东真实场景）
Day 23：AI数字人制作（HeyGen深度使用）
Day 24：AI图像精修进阶（多模型工作流）
Day 25：数据分析与可视化（Superset/Python）
Day 26：AI Agent设计原理（深度理解Agent）
Day 27：数据采集工具（Spider-flow）
Day 28：第一阶段综合项目准备

【能力跃迁目标】
第3周结束时：能独立完成完整的AI内容生产流程
第4周结束时：能为企业客户提供完整的AI解决方案`),
  ],[
    R("国内资源",true,[
      {label:"即刻App（AI创业社区）",url:"https://web.okjike.com",desc:"AI创业者聚集地，项目灵感来源"},
      {label:"虎嗅（AI商业报道）",url:"https://www.huxiu.com",desc:"AI商业化案例深度分析"},
      {label:"36氪（AI创投报道）",url:"https://36kr.com",desc:"AI创业融资动态，市场趋势"},
      {label:"少数派（效率工具社区）",url:"https://sspai.com",desc:"AI工具应用经验分享"},
      {label:"B站：AI实战项目展示",url:"https://www.bilibili.com/search?keyword=AI实战项目+综合案例",desc:"参考他人综合项目结构"},
    ]),
    R("国际资源",false,[
      {label:"IndieHackers（独立开发者）",url:"https://indiehackers.com",desc:"独立开发者经验分享社区"},
      {label:"ProductHunt",url:"https://producthunt.com",desc:"最新AI产品发现"},
    ]),
  ],[
    E(1,"AI内容工作室综合项目","为一个真实企业（可以是你认识的任何小商家）提供免费的AI内容服务：完整交付：短视频脚本×2条+AI产品图×5张+多平台文案×3套。全程使用本周工具，记录完整制作时间，收集客户真实反馈。"),
    E(2,"n8n+Dify集成工作流","搭建「内容自动生产流水线」：n8n定时触发（每天8:00）→ 读取产品信息数据库 → 发给Dify生成5个平台的文案 → 将文案保存到飞书多维表格 → 发送「今日内容就绪」通知。测试稳定运行3天。"),
    E(3,"API成本优化报告","对本周所有API调用（LLM/图像/视频生成）进行成本分析：① 按任务类型统计消耗 ② 识别最贵的3个成本点 ③ 为每个成本点提出优化方案（模型降级/缓存/批处理）④ 预测优化后的成本降低比例。"),
    E(4,"第3周能力自测","完成以下自测题（限时2小时）：① 从头设计一个5节点Dify工作流并实现 ② 用Python写一个调用通义API的批处理脚本（处理CSV）③ 在ComfyUI中用Inpaint修复一张有瑕疵的产品图 ④ 用剪映图文成片生成一条60秒视频。"),
    E(5,"阶段作品集整理","整理第1-3周的所有作品，制作完整的「AI技能作品集」（Notion页面或PDF文档）：① 分类展示15+件作品 ② 每件作品写100字说明（解决什么问题/用了什么工具/达到什么效果）③ 制作「技能雷达图」可视化你的能力分布。"),
  ]),

  // ─── WEEK 4 ─────────────────────────────────────────────────────────────────
  buildDay(22,4,"电商平台机器人配置实战","🏪",["天猫","京东","电商机器人"],[
    T("电商平台AI客服生态","各大电商平台有官方的AI客服解决方案，与第三方工具定位不同。平台官方机器人直接集成订单/库存/物流数据，能自动查单、触发退款流程，是标配。理解各平台的机器人能力边界和配置方法，是电商AI从业者的核心技能。"),
    T("意图识别与槽位填充","NLU（自然语言理解）在电商客服中的应用：意图识别（用户想做什么）+ 槽位填充（提取关键信息如订单号/商品名）构成完整的语义理解链路。了解如何通过关键词配置和示例语料提升机器人的理解准确率。"),
    T("人机协作与升级策略","最优的电商客服模式不是「全自动」而是「人机协作」：机器人处理80%标准问题，人工聚焦20%高价值/高难度问题。设计好升级触发条件（情绪词/超规请求/投诉）和无缝交接机制，是用户体验的关键。"),
    T("客服数据分析与持续优化","客服系统产生的对话数据是宝贵的业务洞察：未答率/满意度/高频问题TOP20/投诉原因分布。用这些数据驱动知识库更新和话术优化，形成「运营→数据→优化」的闭环。"),
  ],[
    R("国内资源",true,[
      {label:"阿里云智能客服（电商专项）",url:"https://www.aliyun.com/product/ai/chatbot",desc:"天猫/淘宝卖家首选AI客服方案"},
      {label:"京东云智能客服",url:"https://www.jdcloud.com/cn/products/chatbot",desc:"京东生态，适合京东商家"},
      {label:"美洽（SaaS客服平台）",url:"https://meiqia.com",desc:"国产客服SaaS，内置AI回复"},
      {label:"扣子（Coze）电商Bot",url:"https://www.coze.cn",desc:"快速搭建平台客服问答Bot"},
      {label:"微信客服API",url:"https://developers.weixin.qq.com/doc/offiaccount/Customer_Service/Customer_Service_Management.html",desc:"微信公众号客服消息接入"},
      {label:"B站：电商AI客服实战",url:"https://www.bilibili.com/search?keyword=电商AI客服+配置+实战",desc:"平台机器人配置完整教程"},
    ]),
    R("国际资源",false,[
      {label:"Zendesk AI",url:"https://zendesk.com",desc:"国际主流客服平台参考"},
      {label:"Intercom（AI客服）",url:"https://intercom.com",desc:"AI客服对话设计参考"},
    ]),
  ],[
    E(1,"平台机器人配置实战","在淘宝卖家中心/千牛配置AI客服机器人（或用扣子模拟天猫客服场景），设置20条常见问题的自动回复，配置3个意图类别，测试20个用户问题的识别准确率。"),
    E(2,"槽位提取Prompt设计","设计提取电商关键信息的Prompt：从用户消息中提取{订单号/商品名/问题类型/情绪值}，用JSON格式输出。测试50条真实客服对话（可从网络搜集），统计提取准确率。"),
    E(3,"人机协作升级规则设计","为一个电商品牌设计完整的升级规则矩阵：10个自动升级触发条件+对应的升级路由（发给哪个客服组）+交接话术模板。用n8n或Dify实现自动化升级通知。"),
    E(4,"客服数据分析仪表盘","收集100条模拟/真实客服对话，用Python或AI分析：TOP10高频问题/平均解决时长/满意度分布/升级率，用Superset或飞书表格制作可视化仪表盘。"),
    E(5,"电商客服AI解决方案提案","为一家年销售额500万的服装品牌制作完整的AI客服升级提案：现状分析+解决方案+工具选型+实施路径+ROI测算+风险提示。输出8-10页PPT。"),
  ]),

  buildDay(23,4,"AI数字人与HeyGen深度使用","👤",["HeyGen","数字人","AI配音"],[
    T("数字人技术路线对比","数字人技术主要分为：驱动式（用真人视频驱动虚拟形象，如HeyGen）和生成式（完全AI生成，如Sora+ControlNet）。驱动式更稳定可控，生成式更自由。了解各种技术的能力边界和商业适用场景。"),
    T("声音克隆技术原理","声音克隆基于TTS+音色迁移技术：提取说话人的音色特征（音调/语速/节奏/音色）→ 迁移到TTS模型→ 用克隆声音合成任意文字。了解声音克隆的数据要求（时长/质量）和伦理边界（仅用于本人授权场景）。"),
    T("数字人商业应用场景","数字人的核心商业价值：① 降低内容生产成本（不需要真人每次出镜）② 实现7×24小时直播 ③ 多语言版本批量生产（同一脚本生成10种语言版本）④ 品牌IP形象一致性。分析哪些场景适合数字人，哪些仍需真人。"),
    T("数字人直播技术栈","数字人直播的完整技术栈：数字人软件（硅基流动/万兴播爆）+ 直播推流（OBS）+ 互动管理（弹幕识别+自动回复）+ 商品推送。了解如何配置一套稳定运行的数字人直播系统，以及常见故障处理。"),
  ],[
    R("国内资源",true,[
      {label:"硅基流动（国内最强数字人）",url:"https://cloud.siliconflow.cn",desc:"声音克隆+数字人，API可调用"},
      {label:"腾讯智影（数字人视频）",url:"https://zenvideo.qq.com",desc:"企业数字人视频，支持批量生产"},
      {label:"万兴播爆（AI数字人）",url:"https://virbo.wondershare.cn",desc:"国产数字人+口播视频生成"},
      {label:"D-ID（数字人API）",url:"https://studio.d-id.com",desc:"照片驱动数字人，有中文支持"},
      {label:"B站：数字人从零到上线",url:"https://www.bilibili.com/search?keyword=AI数字人+制作+教程+2024",desc:"数字人完整制作流程视频"},
    ]),
    R("国际资源",false,[
      {label:"HeyGen（数字人国际版）",url:"https://heygen.com",desc:"效果最好的AI数字人平台"},
      {label:"ElevenLabs（声音克隆）",url:"https://elevenlabs.io",desc:"声音克隆+多语言配音"},
    ]),
  ],[
    E(1,"HeyGen/硅基流动数字人制作","制作一条60秒的数字人介绍视频：上传形象→克隆声音→输入脚本→生成→后期处理。评估口型同步准确率（1-10分）、声音自然度、整体商业可用性。"),
    E(2,"多语言版本批量生产","用同一个数字人形象，将一段60秒的产品介绍脚本翻译并生成3种语言版本（中/英/日或其他），对比不同语言的口型匹配质量，分析多语言数字人的商业应用价值。"),
    E(3,"数字人vs真人出镜对比测试","请10位朋友在不告知的情况下观看数字人视频，让他们判断是否为真人，统计辨识率；再请他们评估数字人内容的可信度（1-10分）。分析数字人在信任感方面的优劣势。"),
    E(4,"数字人直播技术方案","设计一套完整的数字人直播技术方案：软件选型+推流配置+弹幕互动逻辑+商品推送时机+异常处理预案。在本地测试推流稳定性（持续30分钟），记录CPU/GPU占用率。"),
    E(5,"数字人商业提案","为一家有固定讲师内容需求的教育机构制作数字人方案提案：讲师数字人制作+课程视频批量生成+多语言版本+成本对比（数字人vs真人录制）+实施时间表。"),
  ]),

  buildDay(24,4,"AI图像精修进阶","🖌️",["Inpaint","图像精修","多模型工作流"],[
    T("局部重绘进阶技巧","Inpainting在商业图像后期中的高级应用：分层Inpaint（多次局部重绘逐步精修）、Outpainting（扩展画布/图像延伸）、细节增强（ControlNet Tile模型放大细节）。掌握这些技巧可以处理80%的商业图像修改需求。"),
    T("超分辨率与图像放大","AI图像放大技术：Real-ESRGAN（通用放大）/ ESRGAN+ / Ultimate SD Upscale（SD专用，放大同时增加细节）。了解不同放大模型在：人像/产品/风景/文字 场景下的适用性，以及4x放大的质量损失控制方法。"),
    T("多模型串联工作流设计","高质量商业图像往往需要多个模型协同：基础生成（SD）→ 细节放大（ESRGAN）→ 局部修复（Inpaint）→ 背景融合（IC-Light）→ 色彩调整（后处理节点）。理解每个模型的职责分工和数据传递方式。"),
    T("图像质量评估体系","建立系统的图像质量评估标准：技术维度（分辨率/清晰度/色彩准确性）+ 商业维度（品牌一致性/平台规格符合度/目标受众匹配度）。引入CLIP Score等自动化质量评估工具，实现批量质量筛选。"),
  ],[
    R("国内资源",true,[
      {label:"LiblibAI精修工作流",url:"https://www.liblib.art",desc:"下载现成高质量精修ComfyUI工作流"},
      {label:"即梦AI（图像精修）",url:"https://jimeng.jianying.com",desc:"一键画质增强，细节补充"},
      {label:"吐司AI（在线超分）",url:"https://tusi.cn",desc:"在线图片放大/修复，免GPU"},
      {label:"B站：ComfyUI图像精修放大教程",url:"https://www.bilibili.com/search?keyword=ComfyUI+图像精修+超分+放大",desc:"完整精修工作流视频教程"},
      {label:"无界AI（AI修图）",url:"https://www.wujieai.com",desc:"免费额度，图像增强功能完整"},
    ]),
    R("国际资源",false,[
      {label:"Real-ESRGAN（超分模型）",url:"https://github.com/xinntao/Real-ESRGAN",desc:"开源图像超分辨率"},
      {label:"Topaz Gigapixel AI",url:"https://topazlabs.com/gigapixel",desc:"商业图像放大，效果顶级"},
    ]),
  ],[
    E(1,"复杂遮罩精细Inpaint","选一张有复杂背景的人物/产品图，用ComfyUI精细绘制遮罩，分别测试：背景完全替换/局部元素添加/文字去除/颜色区域修改，评估每种操作的成功率和自然度。"),
    E(2,"超分辨率放大对比","对同一低分辨率图像（512×512）分别用3种放大模型放大4x：Real-ESRGAN/Ultimate-SD-Upscale/Topaz（或ESRGAN+），从：清晰度/细节/噪点/文字可读性 四个维度对比，确定最优方案。"),
    E(3,"Outpainting画布扩展","找3张构图较紧的图片（人物/产品/风景），用ComfyUI的Outpainting功能扩展画布（向四周各扩展20-30%），评估AI填充内容的自然度，分析哪类图像最适合Outpainting处理。"),
    E(4,"多模型串联精修流水线","搭建完整精修工作流：输入低质量图 → 1x Inpaint修复瑕疵 → 4x超分放大 → 色彩后处理 → 质量评分过滤 → 输出。测试10张不同类型图片（人像/产品/场景），统计可用率。"),
    E(5,"商业图像精修服务定价","整合本周图像精修能力，设计一套「AI图像精修服务」的服务套餐和定价：① 基础款（去瑕疵+白底）② 标准款（+场景合成）③ 高级款（+精细Inpaint+超分放大）。制作服务说明书，在5188等平台调研竞争对手报价。"),
  ]),

  buildDay(25,4,"数据分析与可视化","📊",["Superset","数据分析","BI报表"],[
    T("数据分析思维框架","AI辅助数据分析的核心不是工具，而是分析思维：明确分析目标→选择合适指标→数据清洗→建立假设→验证假设→得出洞察→形成建议。AI工具在每个步骤都能提速，但分析框架需要人来设定。"),
    T("Pandas+AI辅助分析","Python Pandas是数据分析的核心库，结合AI的工作方式：描述你的分析需求 → AI生成Pandas代码 → 执行验证 → 修正迭代。掌握Pandas核心操作（groupby/merge/pivot/resample），AI可以大幅降低学习门槛。"),
    T("Apache Superset数据看板","Superset是开源BI工具，可连接各类数据库，创建交互式看板。了解核心概念：数据集（Dataset）/图表（Chart）/看板（Dashboard）/过滤器，掌握从数据库连接到看板发布的完整流程。"),
    T("AI辅助数据洞察","GPT的Advanced Data Analysis（原Code Interpreter）可直接上传CSV/Excel，用自然语言提问：「帮我找出销量下降的根本原因」「预测下季度趋势」。了解这类AI分析工具的能力边界：擅长探索性分析，不擅长因果推断。"),
  ],[
    R("国内资源",true,[
      {label:"腾讯云图（BI可视化）",url:"https://cloud.tencent.com/product/bi",desc:"国内BI平台，拖拽做数据大屏"},
      {label:"百度Sugar（数据可视化）",url:"https://sugar.baidu.com",desc:"百度出品，数据大屏在线工具"},
      {label:"DataV（阿里数据大屏）",url:"https://dataV.aliyun.com",desc:"阿里云数据可视化平台"},
      {label:"Pandas中文文档",url:"https://pandas.pydata.org/docs/",desc:"数据处理核心库，中文资料多"},
      {label:"B站：Python数据分析教程",url:"https://www.bilibili.com/search?keyword=Python数据分析+Pandas+可视化",desc:"从数据处理到可视化完整视频"},
      {label:"Jupyter中文社区",url:"https://www.bilibili.com/search?keyword=Jupyter+notebook+数据分析",desc:"Jupyter实战教程"},
    ]),
    R("国际资源",false,[
      {label:"Apache Superset",url:"https://superset.apache.org",desc:"开源BI可视化平台"},
      {label:"Observable（数据可视化）",url:"https://observablehq.com",desc:"在线D3可视化实验平台"},
    ]),
  ],[
    E(1,"AI辅助数据探索","下载一个Kaggle公开数据集（电商/用户行为/销售数据），上传到ChatGPT Advanced Analysis，用自然语言提10个分析问题，评估AI回答的准确率和洞察价值，总结AI数据分析的能力边界。"),
    E(2,"Pandas数据清洗实战","找一个「脏数据」CSV（含缺失值/重复行/格式错误/异常值），用AI辅助编写Pandas清洗脚本，处理所有数据质量问题，记录：原始数据问题类型/处理方法/清洗后质量评估。"),
    E(3,"Superset看板搭建","在Superset中连接SQLite数据库，导入电商订单数据，创建5种图表类型（折线图/柱图/饼图/数字卡片/地图），组合成一个完整的「电商运营看板」，支持日期范围过滤。"),
    E(4,"数据洞察转业务建议","对一组电商销售数据进行完整分析：① 用Pandas计算核心指标（GMV/转化率/客单价/复购率）② 识别2-3个异常趋势 ③ 用AI生成可能的原因假设 ④ 设计验证方法 ⑤ 输出「数据分析报告」（500字+图表）。"),
    E(5,"数据驱动决策方案","选一个真实业务场景（可以是你熟悉的行业），设计「数据驱动运营」方案：① 定义5个核心KPI ② 设计数据收集方案 ③ 制定预警阈值（哪些数据变化需要立即行动）④ 建立周/月报告模板。输出完整方案文档。"),
  ]),

  buildDay(26,4,"AI Agent设计原理","🤖",["Agent","工具调用","多步推理"],[
    T("Agent与工作流的本质区别","工作流（Workflow）：步骤预先定义，执行路径可预测，适合稳定业务流程。Agent：自主决定下一步行动，路径动态生成，适合复杂/未知任务。两者不是替代关系：工作流适合生产，Agent适合探索。"),
    T("工具调用（Function Calling）机制","LLM通过Function Calling实现与外部世界交互：定义工具（函数名+描述+参数）→ 用户提问 → LLM决定调用哪个工具+生成参数 → 执行工具 → 将结果返回LLM → LLM整合生成最终回答。工具设计的质量决定Agent能力的上限。"),
    T("多Agent协作系统","复杂任务需要多个专业Agent协作：规划Agent（分解任务）→ 执行Agent（各司其职）→ 验证Agent（质量检查）→ 汇总Agent（整合输出）。理解如何设计Agent角色、通信协议和任务分配机制。"),
    T("Agent的安全边界与人工监督","开放的Agent能力需要严格的安全约束：限制可调用工具的范围/设置操作确认机制（重要操作需用户确认）/日志记录所有操作/设置成本上限/异常检测。「Human in the Loop」是生产环境Agent的必要设计。"),
  ],[
    R("国内资源",true,[
      {label:"扣子Agent（国内首选）",url:"https://www.coze.cn",desc:"可视化多Agent编排，零代码"},
      {label:"Dify Agent模式",url:"https://docs.dify.ai/zh-hans/guides/agent",desc:"工具调用+多步骤推理Agent"},
      {label:"阿里百炼（Agent编排）",url:"https://bailian.console.aliyun.com",desc:"企业级Agent工作流编排"},
      {label:"字节扣子工作流",url:"https://www.coze.cn/docs/guides/workflow",desc:"扣子工作流官方文档"},
      {label:"B站：AI Agent设计教程",url:"https://www.bilibili.com/search?keyword=AI+Agent+设计+教程+2024",desc:"国内最新Agent实战视频"},
    ]),
    R("国际资源",false,[
      {label:"AutoGen（微软开源）",url:"https://github.com/microsoft/autogen",desc:"多Agent对话框架"},
      {label:"CrewAI（角色扮演Agent）",url:"https://github.com/joaomdmoura/crewAI",desc:"多角色协作Agent框架"},
    ]),
  ],[
    E(1,"工具调用设计实验","为一个电商助手Agent设计工具集：定义5个工具（查订单/查库存/计算折扣/查物流/生成文案），编写每个工具的描述（要能让LLM正确判断何时调用），在扣子或Dify中实现，测试20个场景下的工具选择准确率。"),
    E(2,"ReAct推理链分析","让一个Agent完成复杂任务（如：「帮我分析竞品定价策略，并给出我们的调价建议」），开启Chain-of-Thought模式，记录完整的Thought→Action→Observation链条，分析Agent推理的合理性和潜在错误。"),
    E(3,"多Agent分工系统","用扣子工作流搭建三Agent协作系统：规划Agent（分解任务为子任务列表）→ 执行Agent（逐项完成）→ 审核Agent（检查质量，发现问题退回执行Agent重做），测试：「帮我制作一个产品发布会的完整策划方案」。"),
    E(4,"Agent安全测试","对你搭建的Agent进行10种越权测试：让Agent做超出工具范围的事/诱导Agent绕过限制/注入恶意指令等，记录每种攻击的成功率，针对3种主要漏洞设计防护策略。"),
    E(5,"Agent商业价值评估","调研3-5个已商业化的AI Agent产品（如：Perplexity/Cursor/Devin/各垂直Agent），分析其：核心工具集/推理策略/安全机制/商业模式，输出「AI Agent商业化路线图」（1000字报告+思维导图）。"),
  ]),

  buildDay(27,4,"数据采集与爬虫工具","🕷️",["爬虫","数据采集","Spider-flow"],[
    T("数据采集的法律边界","数据采集前必须了解的法律边界：robots.txt协议（应当遵守）/ 服务条款（禁止爬虫的平台不能爬）/ 数据用途（训练AI/商业分析需要注意版权）/ 个人信息保护法（不能采集个人敏感信息）。合法合规是数据采集的前提。"),
    T("HTTP请求与反爬技术","网页数据采集的技术基础：HTTP请求结构（Headers/Cookie/Session）→ HTML解析（BeautifulSoup/lxml）→ 动态网页处理（Playwright/Selenium）→ 反爬应对（随机延迟/UA轮换/代理IP）。了解常见反爬机制的工作原理，选择合法合规的应对策略。"),
    T("Spider-flow可视化爬虫","Spider-flow是开源的可视化爬虫平台，无需编程即可配置数据采集任务：定义采集URL → 配置提取规则（CSS Selector/XPath）→ 设置翻页逻辑 → 数据存储（数据库/文件）。适合不会编程的业务人员采集公开数据。"),
    T("AI辅助数据清洗与结构化","采集到的原始数据往往需要清洗：用LLM对非结构化文本进行结构化提取（从商品描述中提取规格参数）/ 用AI进行数据去重（语义相似度判断）/ 用AI进行数据分类和标注。AI大幅降低数据治理的人工成本。"),
  ],[
    R("国内资源",true,[
      {label:"八爪鱼采集器（国内最流行）",url:"https://www.bazhuayu.com",desc:"可视化爬虫，无需代码，适合运营"},
      {label:"火车采集器",url:"http://www.locoy.com",desc:"老牌数据采集工具，稳定可靠"},
      {label:"Spider-flow（开源可视化爬虫）",url:"https://github.com/ssssssss-team/spider-flow",desc:"开源免费，支持JS渲染"},
      {label:"集搜客（GooSeeker）",url:"https://www.gooseeker.com",desc:"浏览器插件式爬虫，易上手"},
      {label:"B站：Python爬虫实战教程",url:"https://www.bilibili.com/search?keyword=Python爬虫+Scrapy+实战教程",desc:"从HTTP基础到框架实战"},
    ]),
    R("国际资源",false,[
      {label:"Scrapy（Python爬虫框架）",url:"https://scrapy.org",desc:"主流爬虫框架，生产级"},
      {label:"Playwright（浏览器自动化）",url:"https://playwright.dev",desc:"JS渲染页面爬取利器"},
    ]),
  ],[
    E(1,"合规数据采集实战","选一个允许采集的公开平台（如：Github Trending/Product Hunt/公开新闻网站），用Spider-flow或Python Requests+BeautifulSoup采集100条结构化数据，输出整洁的CSV文件，记录采集成功率和遇到的反爬问题。"),
    E(2,"竞品价格监控系统","搭建竞品价格监控工作流：定时采集3-5个竞品的公开价格信息 → 存入数据库 → 价格变动超过5%时触发n8n通知（企微/飞书）→ 每周生成价格趋势报告。注意仅采集公开价格，合法合规。"),
    E(3,"AI辅助数据结构化","采集100条产品评论文本，用LLM批量提取结构化字段：{评分/情感/主要优点/主要缺点/提及功能/用户画像}，输出结构化Excel，分析提取准确率，计算人工标注 vs AI标注的效率差异。"),
    E(4,"Spider-flow自定义工作流","在Spider-flow中搭建一个多步骤采集任务：列表页采集URL列表 → 进入详情页采集内容 → 下载图片 → 数据去重 → 存入MySQL数据库。测试整个流程的稳定性（运行1小时不中断）。"),
    E(5,"数据产品方案设计","设计一个基于数据采集的数据产品方案：选择一个垂直领域（如：AI工具排行/国内餐饮品牌数据/电商类目趋势），定义数据采集范围、更新频率、数据结构、分析维度、商业化模式（订阅/API/报告）。输出产品需求文档。"),
  ]),

  buildDay(28,4,"第一阶段综合项目准备","📋",["项目规划","需求分析","综合实战"],[
    T("需求分析与项目规划","综合项目的成败始于清晰的需求分析：目标用户是谁？核心痛点是什么？AI能解决什么？人工仍需负责什么？用「问题树」拆解需求，用「用户故事」定义功能边界，用「成功指标」定义验收标准。"),
    T("技术选型决策框架","选择技术工具的决策维度：① 功能匹配度（能否满足核心需求）② 团队技术能力（学习曲线）③ 可靠性（生产环境稳定性）④ 成本（API费用/订阅费）⑤ 扩展性（未来能否升级）。避免「新工具偏见」和「旧工具惰性」。"),
    T("项目执行SOP设计","标准化项目执行流程：需求确认（含验收标准）→ 技术方案评审 → 分工和时间节点 → 开发测试 → UAT（用户验收测试）→ 上线部署 → 监控与维护。AI项目特有挑战：幻觉处理/质量不稳定/成本控制。"),
    T("作品集与个人品牌","30天学习的终极成果不是证书，而是作品集和个人品牌：GitHub/Notion展示技术项目/小红书分享学习历程/LinkedIn更新AI技能/参与AI社区贡献内容。个人品牌是最好的求职/接单敲门砖。"),
  ],[
    R("国内资源",true,[
      {label:"飞书项目（项目管理）",url:"https://www.feishu.cn/product/project",desc:"任务拆解+进度追踪，团队协作"},
      {label:"ProcessOn（思维导图/流程图）",url:"https://www.processon.com",desc:"在线画项目结构图和需求分析"},
      {label:"石墨文档（项目PRD）",url:"https://shimo.im",desc:"在线协作文档，产品方案必备"},
      {label:"Notion（项目作品集）",url:"https://notion.so",desc:"搭建个人项目展示页面"},
      {label:"B站：AI产品项目规划方法",url:"https://www.bilibili.com/search?keyword=AI产品+项目规划+需求分析",desc:"项目拆解和规划视频教程"},
    ]),
    R("国际资源",false,[
      {label:"Miro（在线白板）",url:"https://miro.com",desc:"头脑风暴+流程梳理"},
      {label:"Linear（现代项目管理）",url:"https://linear.app",desc:"简洁高效的项目追踪工具"},
    ]),
  ],[
    E(1,"项目选题与需求分析","从以下方向选择一个综合项目（或自定义）：A.AI内容创作工作室系统 B.垂直领域智能客服 C.数据采集与分析平台 D.AI教育内容平台。完成需求分析文档：用户画像/核心功能/技术架构/成功指标/风险清单。"),
    E(2,"技术架构设计","为选定项目绘制完整技术架构图：标注每个模块使用的AI工具+数据库+自动化平台，以及模块间的数据流向和集成方式。用draw.io或Miro制作，评估架构的可行性和潜在瓶颈。"),
    E(3,"项目原型搭建","在2小时内用低代码/AI工具快速搭建项目MVP原型（不追求完美，能跑通主流程即可），记录哪些功能AI工具能直接支持，哪些需要额外开发，哪些暂时不可行。"),
    E(4,"风险评估与应对方案","识别项目的5大风险：技术风险/成本风险/质量风险/时间风险/商业风险，为每个风险设计具体的应对策略（预防措施+紧急预案），评估每个风险的发生概率（高/中/低）和影响程度。"),
    E(5,"项目启动汇报","制作项目启动汇报文档（PPT或Notion），包含：项目背景与目标/需求分析摘要/技术架构/里程碑计划/资源需求/预期成果。模拟向投资人/老板汇报的场景，练习10分钟讲清楚核心要点。"),
  ]),

  // ─── WEEK 5 / FINAL SPRINT ──────────────────────────────────────────────────
  buildDay(29,5,"第一阶段综合项目制作","🔨",["综合项目","全流程实战","MVP交付"],[
    T("MVP交付的核心原则","最小可行产品（MVP）不是「功能最少」，而是「能验证核心价值假设的最简版本」。先做能用的，再做好用的，最后做想用的。每次迭代都要有可测量的用户反馈。避免「完美主义陷阱」——AI工具让快速迭代成为可能。"),
    T("AI工具协同的节奏管理","多工具协同项目的时间管理：并行执行（图像生成+文案写作可以同时进行）/ 依赖管理（视频需要等图像完成）/ 异步处理（批量AI任务提交后做其他工作）。合理安排工具协同节奏，最大化生产效率。"),
    T("质量门禁与验收标准","每个交付物应有明确的质量门禁：技术指标（分辨率/响应时间/准确率）+业务指标（可用性/一致性/品牌符合度）+用户反馈（至少3人以上的真实评价）。没有质量门禁的项目往往以「差不多能用」收场。"),
    T("项目复盘方法论","项目结束后必须做完整复盘：做对了什么（继续保持）/ 做错了什么（改进方向）/ 缺少了什么（能力缺口）/ 下次如何更好（行动计划）。AI项目特有复盘点：哪些工具超出预期/哪些工具低于预期/成本是否在控。"),
  ],[
    R("国内资源",true,[
      {label:"Vercel（快速部署前端）",url:"https://vercel.com",desc:"React/Next.js应用零配置部署"},
      {label:"腾讯云Serverless（国内访问）",url:"https://cloud.tencent.com/product/scf",desc:"国内可访问，低成本部署"},
      {label:"阿里云OSS（静态网站托管）",url:"https://www.aliyun.com/product/oss",desc:"便宜可靠的国内静态资源托管"},
      {label:"ProcessOn（展示流程梳理）",url:"https://www.processon.com",desc:"项目架构图和演示文稿"},
      {label:"飞书文档（项目汇报）",url:"https://www.feishu.cn",desc:"在线多维文档，适合项目展示"},
      {label:"B站：项目部署上线教程",url:"https://www.bilibili.com/search?keyword=前端项目+部署+Vercel+阿里云",desc:"项目发布全流程视频"},
    ]),
    R("国际资源",false,[
      {label:"Hugging Face Spaces",url:"https://huggingface.co/spaces",desc:"免费托管AI Demo应用"},
      {label:"Render（免费部署后端）",url:"https://render.com",desc:"免费层后端+数据库部署"},
    ]),
  ],[
    E(1,"项目全力冲刺（Day 1）","完成项目核心功能的70%：搭建主要工作流/知识库/自动化链路，确保主流程可以跑通（即使有bug）。每2小时记录一次进度（完成了什么/遇到了什么问题/下一步计划）。"),
    E(2,"项目全力冲刺（Day 2）","完成剩余30%功能，进行全面测试：用50个测试用例验证系统各功能，记录所有bug，按优先级（P0/P1/P2）分类，修复所有P0和P1问题，P2问题标注为「待优化」。"),
    E(3,"用户测试与反馈收集","邀请3-5位非技术背景的用户测试你的项目（最好是目标用户群体），记录他们的使用过程（屏幕录制），收集反馈问卷，进行30分钟用户访谈，整理「用户反馈报告」（优点/痛点/改进建议）。"),
    E(4,"成本与效益分析","计算完整项目的实际成本：AI API费用/工具订阅费/开发时间成本，对比预期收益（节省人工成本/增加收入/提升效率），计算ROI，预测规模化到10倍用户量的月度成本和盈亏平衡点。"),
    E(5,"项目演示视频制作","制作一个3-5分钟的项目演示视频：① 30秒项目背景和解决的问题 ② 2-3分钟核心功能演示 ③ 30秒成果数据展示 ④ 30秒下一步计划。用剪映/屏幕录制+AI配音完成，发布到个人作品集页面。"),
  ]),

  buildDay(30,5,"阶段总结与作品整理","🎯",["阶段总结","作品集","成长规划"],[
    T("30天学习成果盘点","完整30天的知识和技能地图：LLM应用（Prompt/RAG/Agent/API调用）/ AI图像（SD/MJ/ComfyUI/LoRA/ControlNet）/ AI视频（可灵/即梦/剪映/数字人）/ 自动化（扣子/Dify/n8n/NocoBase）/ 数据（采集/分析/可视化）。你已经掌握了一个完整的AI工具生态。"),
    T("个人AI工具体系建设","基于30天经验，建立你的个人AI工具体系：常用工具清单（按使用频率排序）/ 工具选择决策树（什么场景用什么工具）/ 成本控制方案（哪些工具值得付费）/ 学习更新机制（如何持续跟上AI进化节奏）。"),
    T("职业发展路径规划","基于已掌握技能，三条职业发展路径：路径A：AI训练师/提示工程师（门槛低，入门快，适合短期目标）路径B：AI应用开发工程师（需要补充Python/前端基础，薪资更高）路径C：AI内容创业者（最自由，风险最高）。根据自身情况选择并制定90天行动计划。"),
    T("持续学习机制设计","AI行业迭代极快，30天只是起点：① 信息源管理（关注哪些账号/平台）② 每周固定学习时间（建议4-6小时/周）③ 实战项目驱动（有真实项目才能深度掌握）④ 社区参与（输出和分享是最好的学习方式）⑤ 工具评测习惯（新工具第一时间体验评测）"),
  ],[
    R("国内资源",true,[
      {label:"即刻App（AI圈子）",url:"https://web.okjike.com",desc:"AI从业者，分享成果最佳平台"},
      {label:"小红书AI创作者",url:"https://www.xiaohongshu.com",desc:"发布AI作品，积累受众"},
      {label:"36氪（AI创业报道）",url:"https://36kr.com",desc:"AI行业趋势，寻找商业机会"},
      {label:"虎嗅（深度分析）",url:"https://www.huxiu.com",desc:"AI商业化案例，对标学习"},
      {label:"B站AI区（发布作品）",url:"https://www.bilibili.com",desc:"发布AI作品视频，建立个人IP"},
      {label:"少数派（AI工具测评）",url:"https://sspai.com",desc:"写AI工具测评，建立专业口碑"},
    ]),
    R("国际资源",false,[
      {label:"Hugging Face（模型前沿）",url:"https://huggingface.co",desc:"关注最新AI模型动态"},
      {label:"Papers with Code",url:"https://paperswithcode.com",desc:"最新AI论文+代码"},
    ]),
  ],[
    E(1,"30天技能自测","完成最终技能自测（2小时）：① 从零设计一个5步Dify工作流并实现 ② 用ComfyUI生成+Inpaint修复一张商业级产品图 ③ 用Python写一个调用LLM API的批处理脚本 ④ 在剪映中用图文成片生成一条60秒视频 ⑤ 在扣子中搭建一个有知识库的客服Bot。记录每项完成时间。"),
    E(2,"完整作品集发布","整理并发布你的AI学习作品集：选取30天中最能体现你能力的10件作品（每类至少1件），写清楚每件作品的背景/工具/成果/数据，发布到Notion/个人网站/小红书，分享链接给3个朋友收集反馈。"),
    E(3,"90天成长计划制定","制定接下来90天的成长计划：① 确定一个主攻方向（就业/接单/创业）② 识别3个核心能力缺口 ③ 制定每周学习计划（含具体工具和项目）④ 设定3个里程碑目标（1月/2月/3月各1个）⑤ 建立学习伙伴/导师/社区资源清单。"),
    E(4,"第一个付费项目规划","规划你的第一个付费项目：在自由职业平台（猪八戒/飞猪/Fiverr）搜索AI相关服务，找到3个你能提供的服务类别，制作服务说明页面（含价格/交付物/案例），发布并记录咨询数量。"),
    E(5,"毕业复盘与经验分享","写一篇《我的30天AI学习复盘》长文（1500字以上）：① 学习前vs学习后的能力对比 ② 最难忘的3个学习突破 ③ 走过的3个弯路和教训 ④ 给后来者的3条建议 ⑤ 下一阶段的目标。发布到公众号/小红书/知乎，这将是你个人品牌的第一篇内容。"),
  ]),
];

// ─── STORAGE ──────────────────────────────────────────────────────────────────
const SK = "ai_plan_v3";
async function load() {
  try {
    const data = localStorage.getItem(SK);
    return data ? JSON.parse(data) : {};
  } catch { return {}; }
}
async function save(d) {
  try { localStorage.setItem(SK, JSON.stringify(d)); } catch {}
}

// ─── APP ──────────────────────────────────────────────────────────────────────
// ── PASSWORD SECURITY ──────────────────────────────────────────────────────
// Passwords are stored as SHA-256 hashes. Plaintext never appears in code.
// Each hash = one user slot. To add a user: hash their password and add to array.
// Hash tool: https://emn178.github.io/online-tools/sha256.html
// Current slots: 50 user slots pre-configured
// ════════════════════════════════════════════════════
// 【密令管理区】只需修改这里，其他代码不用动
//
// 修改到期日：把 "2026-12-31" 改成你想要的日期
// 永不过期：  把日期改成 null
// 新增密令：  在末尾加一行 { hash: "64位哈希", expires: "2026-12-31" },
//            哈希生成工具：https://emn178.github.io/online-tools/sha256.html
// ════════════════════════════════════════════════════
const VALID_PW_HASHES = [
  // ── AI学习 专属密令（50个）──
  { hash: "41ec7cd91e34e789c24bf36b9298398a68b5af0f0e94a52ff1d97cb2438a8163", expires: "2026-12-31" }, // learn_01: 0bbsyA
  { hash: "abc2bdf70d1d2cbcabd14da14a5d33c0789d6951cbc554189bee1a0e18a2cae2", expires: "2026-12-31" }, // learn_02: YtSWMo
  { hash: "ac043b915bc735e31628916afcb2e036d890fd48590297e89dacaf27c12011f0", expires: "2026-12-31" }, // learn_03: YyIKimEZ
  { hash: "6536516e6662ffcb06ef0d59c6ac0e390bb2a45a78ba17e0bd827de55bad36cf", expires: "2026-12-31" }, // learn_04: AlvUAjZY
  { hash: "822a5df0598d5fae3f575b3f8d6349d0add9f8d396d075b1270d85454635c78f", expires: "2026-12-31" }, // learn_05: J3haTa
  { hash: "b6a1528486b7adfada804543c51f7febb88974d73956252642f2fc34c1c2eb7c", expires: "2026-12-31" }, // learn_06: h5XMT5
  { hash: "d5fafff2562c490a344373fb6ff32d9031a32bf0c37b42ccbd35597c128f078c", expires: "2026-12-31" }, // learn_07: aMQGgt0
  { hash: "7ddc11542a31a8f96952e461ee099ab9b7c3d3d8a4ab295fa256796ded5c71b9", expires: "2026-12-31" }, // learn_08: hdsKwmw
  { hash: "03bb5050e1156fa7562c3db105dc694a1e909c00be8a6cd54317f07f913f650a", expires: "2026-12-31" }, // learn_09: Lokrhp
  { hash: "250f2821e1b95bb7bf4ca037359e400bf50101187db0e5f50fb3dcdae951d129", expires: "2026-12-31" }, // learn_10: yVAC83a
  { hash: "aa87e708db0fc8afac5ee10f49ff44e8a768d7cf42ac8352c2eb2e903455fc35", expires: "2026-12-31" }, // learn_11: P8SdpK5
  { hash: "3e9c0902e10f3c76d4bbf780460022d2f4b2b8945c9aa21c801f06d2a908b3bc", expires: "2026-12-31" }, // learn_12: TwH0bM
  { hash: "8eb6c2fe97dd9c579f0492140c2a54b6a9126360f1779b38eea4f1807c22d891", expires: "2026-12-31" }, // learn_13: 96iAnMO
  { hash: "a506ccbb991e5aef9f3a02fbfc33476268500883ad4205373deaf1b0b3ca50c4", expires: "2026-12-31" }, // learn_14: PIediEN
  { hash: "32628b9ccca88cab1958ae1aaed0b3c5bf85e3db98bce814e803a74a1bab3973", expires: "2026-12-31" }, // learn_15: L3Pqd1X
  { hash: "9b293d287d24ceba5887179c82b8eabc5acd9f279f5725821e3ece48c3283fb2", expires: "2026-12-31" }, // learn_16: dDKhvsER
  { hash: "e3d950667ea31faa041240948d7f50f0cfa7c286884c28a90a10ae04d700dec2", expires: "2026-12-31" }, // learn_17: qBpReVc
  { hash: "21a8c8cc8e7c4cb8185d20bc7b37a8ebdaff4ffe90b170eab30d7deb327dfab0", expires: "2026-12-31" }, // learn_18: 4Yiqy5
  { hash: "9af4b52fc0b2885ffe1032f57270842a584dbed7beb7dffa39b3ba9dccf502e7", expires: "2026-12-31" }, // learn_19: 2z38VpI
  { hash: "ffcf27cf2009ae3599f60695d1f59d0e95816ef90ff2956a9cbb04fbeb1ce3de", expires: "2026-12-31" }, // learn_20: kdSNr6
  { hash: "836bf9fdecc89c96c9db91f00059a426c4d499c645be1d0c60c5b7ed318db72c", expires: "2026-12-31" }, // learn_21: I9Ts7pw
  { hash: "730bb4ad9e925c020096ffcfa770f4f5e808b2cd5d462d253bdf5e5e09d1e379", expires: "2026-12-31" }, // learn_22: VLICAS
  { hash: "948810f556870e08d96b61b0baa797471dd2e332e6f2b72253e5bad9470948b6", expires: "2026-12-31" }, // learn_23: 52gxMYUr
  { hash: "0ac93d36a0b8bc8b70109cb387a9a74daaec251b456c4e9b87f3d535b471d251", expires: "2026-12-31" }, // learn_24: V1TGdVl
  { hash: "e45633010dd329dec5d4b56c98a708780839cbd2c86ec218a7603d06c1b7b616", expires: "2026-12-31" }, // learn_25: 9XSYw2
  { hash: "9fc59986535205a21dd056f0bfc9c42f408e16ec83173d702894c326b876a382", expires: "2026-12-31" }, // learn_26: QKcBgKr
  { hash: "2a4bfa9233f7b0ff0fd0f17319994f2404a3f7c06d76ef909c81f16e6c124cbe", expires: "2026-12-31" }, // learn_27: RrYYWNT
  { hash: "79d260e51fbf01b53fc7fc0d2a48789e6224c7b27c87a6d7f7a985e5c041fe59", expires: "2026-12-31" }, // learn_28: uJkbcn12
  { hash: "426006096c28acd22a0883a4dfdb01cabccf2494931bdc42ea0d067c664ad0e0", expires: "2026-12-31" }, // learn_29: SgB262z
  { hash: "853d6913b21367f5367ac85984a8f7524e2e7a59b36938278ff7749d76ffb1e2", expires: "2026-12-31" }, // learn_30: uIZj7rT
  { hash: "214b7b1fbfb68704fcdfb90ceb7d9170511501dc97a83dcdce3f8c6aa6445f24", expires: "2026-12-31" }, // learn_31: xqDyFlA
  { hash: "8863c1b1bc52403207337cb36af2b6d48661fedd40c986b6817002dac7b66b36", expires: "2026-12-31" }, // learn_32: JdxjS1eu
  { hash: "8b0ade00da8582130a49be855b2f414b57b75a879020efc3fcdbf21dfc3cabb6", expires: "2026-12-31" }, // learn_33: Wt4gIe
  { hash: "7d62993f43f3e108d8b5a6b16aea678a0fa3bc232db8917c9dc4ca463be7d97d", expires: "2026-12-31" }, // learn_34: K5jYXO3
  { hash: "21504116effce1ef5d9ee47f0656bd13091742927ae30b43cef1c05e25087588", expires: "2026-12-31" }, // learn_35: egB7uRze
  { hash: "6dca239bceb6a10c674e69560c76c41fbe0d1f329646182dc3226ceae290f807", expires: "2026-12-31" }, // learn_36: qW17i7h
  { hash: "6b9c1918b0d69b7d4925ab1e64da63e16f1e5f8311af9bac73e525a73da473d5", expires: "2026-12-31" }, // learn_37: 03Ef0JtC
  { hash: "4cacc66bf2ef1bcb3888dbcb3142b94f47a0d8a46f3bebe1039bce86caf43649", expires: "2026-12-31" }, // learn_38: HiRvm4o
  { hash: "aab8158cf32814dff54b7879cf13156873887f83b189021bbd37c273dd5be47d", expires: "2026-12-31" }, // learn_39: oYrTOJZ
  { hash: "64992bd091b55bf769296e545fa148349b11b4c09669ebce8b294ff74d57b303", expires: "2026-12-31" }, // learn_40: RnPbnQ
  { hash: "cd7861fffd6c3da2f0852a108c20e15db2fa729ac5ea7bbf7f2bd8bc9ccce1ad", expires: "2026-12-31" }, // learn_41: vlu7K1GQ
  { hash: "6e6d538af1c737da4c5f6ba513590d9cd15d929d7c6eb18da82d75985762c6c7", expires: "2026-12-31" }, // learn_42: 8XG73lZ
  { hash: "63b9dd7aeb7f7a9ba3e338950b43e34e29438d229f37c759f548510c055ec63f", expires: "2026-12-31" }, // learn_43: 28Dqh4W
  { hash: "9464802d693497bc96a80307e0ac8a298afad4dc34d317873984bb645979a7de", expires: "2026-12-31" }, // learn_44: LF2AWQ
  { hash: "8758ac092203c9496187754a5dbafc89996d2aacfa3485cb30846a68cdbd138e", expires: "2026-12-31" }, // learn_45: LUEhu4d
  { hash: "f490037b0fb2d4ccf5d1f77c2c2c48737a39e4d3548e61744956eab2dfbcac0d", expires: "2026-12-31" }, // learn_46: IkZmM1i
  { hash: "24bb12378e31e643e50078d65a05fd24538024cdfad436fc9b23bc9dbf4f40d3", expires: "2026-12-31" }, // learn_47: vrLt5boD
  { hash: "77f63fa53c47ce000d4684318c75560d8c30a899b716a2a17228eb49e2773a3c", expires: "2026-12-31" }, // learn_48: bH47HDS8
  { hash: "080ca91f75882e7f731d4da03629fe51d6112b43e2ebca88b5a740e9eda16a6c", expires: "2026-12-31" }, // learn_49: kpJd7f
  { hash: "c2dbedf834d3a02a0764a5bc920160d3e61e690e62fa653ade48a0936fb83b74", expires: "2026-12-31" }, // learn_50: 45B1f8
  // ── 三合一套餐密令（50个）── 三个工具都能用
  { hash: "58852f6a56b025b0cd9ac272a07d2b5d7c00d91f7d3468e5d01efd334ff152f2", expires: "2026-12-31" }, // bundle_01: 0cEDhk
  { hash: "a532d5100bd2221eb99b1d8798463eaf027f4480b2f52201468072374236ec4d", expires: "2026-12-31" }, // bundle_02: rHgEqLNs
  { hash: "2a3ee50e7856ca852a541591fc1fa765e71a277f41f80842ed24cc5ca7ea6d82", expires: "2026-12-31" }, // bundle_03: heIdo2c
  { hash: "7320595c93c7afb29ff3fa73da39ac0a410e425b8011b3c0e293923c235caea3", expires: "2026-12-31" }, // bundle_04: drHId8ia
  { hash: "4260d0510ce65a89f3e0007a06df3ef81fa1c55ebb51a465c1a708d06b584764", expires: "2026-12-31" }, // bundle_05: 0Xikx3o
  { hash: "52b7a3d82cd50dd846285e0a96c3d62361a0c8a76ce535924c5c1ce3af6ab133", expires: "2026-12-31" }, // bundle_06: S3vSbTl
  { hash: "d1ba3f041d5fe410f47a812aea85b5d1dbfab31ecfb5efc9ee541e4e4fba2c3f", expires: "2026-12-31" }, // bundle_07: WqvXp2pG
  { hash: "6cdd8745823b2497e9e8613f85e17518d989d2a2c4f789384c03c18111f8aa32", expires: "2026-12-31" }, // bundle_08: VFjxqn
  { hash: "01781a622e636a1da0d5d43fc7009afc822cf49e7efd30d1ebc00a5e129f4652", expires: "2026-12-31" }, // bundle_09: LkP1JD5F
  { hash: "264b5c83d72e1be9259f5c2ed6c678f4beb3aa66476a7eb816e4f5af1fcf71e1", expires: "2026-12-31" }, // bundle_10: xZYACdP
  { hash: "13a2f4212cf79eeb0da218cf8e7a051334e7151e421d92f6f92b1c1371f42063", expires: "2026-12-31" }, // bundle_11: b6milAy
  { hash: "5c1d3c357474db6ca1f6ab83057b029476e5319ee7c2f5dfa3f619c8fd9b97fa", expires: "2026-12-31" }, // bundle_12: IsScV7
  { hash: "1b2cfa5c1a319af4415b81a04056ba90c4c4097daeb91b51f7d26d10982a600e", expires: "2026-12-31" }, // bundle_13: RbWljos4
  { hash: "665bcc8cbf827c6cb8219c5290dfd6699c5b03074f86192096603339ea04d325", expires: "2026-12-31" }, // bundle_14: 94cQpJ
  { hash: "cdaf5b35eca921486558e522056a44e0f87b98d5b91eac25a272c76bd808a2d7", expires: "2026-12-31" }, // bundle_15: IMWNWi
  { hash: "848db25e07ac778bc9a6348a322a4278ef253472cb46724ddfc7e2c37aa415e8", expires: "2026-12-31" }, // bundle_16: l6ycMTR
  { hash: "315a9bfd7a49fc1b60887e84fff7a17224b75001ebb1937895b3b74aafd21d58", expires: "2026-12-31" }, // bundle_17: D0QR7NZ
  { hash: "826fcada952d869788335119b62c99fa5b78729af1fffd020e8f2073853dba01", expires: "2026-12-31" }, // bundle_18: ClhNAEE
  { hash: "67be03865906827bfbcfe289f71ba5c42baa5fc5fe9578ff202c066eb57e5631", expires: "2026-12-31" }, // bundle_19: UTWUUgx
  { hash: "9a86db34888fddd08a97a1d9afd2991aae7e0aabece009432543a479fcf5da1c", expires: "2026-12-31" }, // bundle_20: rZ92vfvg
  { hash: "6c9902fff46080cef3de43ef37a7711b0a0a70c8c724c807b7570787f685cc7d", expires: "2026-12-31" }, // bundle_21: RBfHwE
  { hash: "6657152d15b88a211587cc3615190d1f71853810b44c67697f70660550000e36", expires: "2026-12-31" }, // bundle_22: Z4DxlGX2
  { hash: "d106a985922fbcd3471f958cf1402575095ff0e95767b6c7028ff10fa573c38e", expires: "2026-12-31" }, // bundle_23: pYJ6lW
  { hash: "c49f4981e4a074eede4298d38be2f450ffebc13a1010cc46175b01a21189df63", expires: "2026-12-31" }, // bundle_24: 795v4Eu
  { hash: "006cec3f3db67ff0e3fe9461ca1ced69dea62fc8215bc7cba920c62a9d2b5a97", expires: "2026-12-31" }, // bundle_25: ImSleiC
  { hash: "792a29e23d04e7d712fa393697f68ed877c077084abbc63729a948946aae3293", expires: "2026-12-31" }, // bundle_26: 2frKYJFR
  { hash: "e2555dfe8f855bde18b76e542d1d63f64119a7f042547ef6278e4cc30489e9b6", expires: "2026-12-31" }, // bundle_27: 5CgEZxQ
  { hash: "019ff2d0e515afe70754618f3f4ad87f6050daba55e2bc7c26bc354f10dda6d1", expires: "2026-12-31" }, // bundle_28: 1udglz
  { hash: "ef58498ab5c679d71c94550456d5911a8984d84940a71b32c373e5f6754729d4", expires: "2026-12-31" }, // bundle_29: SgpZDG
  { hash: "049c56dc2c2abfb14d651fa14f96a02c77d1d5847caba5316eeb7c1077d26161", expires: "2026-12-31" }, // bundle_30: G8II2s
  { hash: "76d3be96592da36ad462a4c5cab6231351af66a68dce11edd248680a254bf604", expires: "2026-12-31" }, // bundle_31: 1luiY72
  { hash: "e8fde2b707cf66a31d886c34f9b324a5acc77d8af26f93e27be23279ae8c8004", expires: "2026-12-31" }, // bundle_32: tsq7gas
  { hash: "7dcd1d15897734e1777633a4b7180454f8d898ddf5eeeb5f1db5e5e4262e3cc3", expires: "2026-12-31" }, // bundle_33: oM980t3I
  { hash: "7c20a5951bfba07c8d1b28f06df624ef5a87354edfdd1c13140650b1fcbb41a0", expires: "2026-12-31" }, // bundle_34: dKN94k
  { hash: "1169547ff4ed0cd9115c782152f62752b4a20a8ad5a7ccee49161f155139be76", expires: "2026-12-31" }, // bundle_35: mo5IJyFu
  { hash: "14d143b5802041d0ffd7b116d349a4b4f3047c92dd89b1e57cbbbf0a1bd0e75d", expires: "2026-12-31" }, // bundle_36: o8xPBX
  { hash: "b6c9aed6243a31d2aa044a89b8a77c4ad8b00b5d56136b31d569a49e86bcac43", expires: "2026-12-31" }, // bundle_37: Fp925V
  { hash: "fb526263de18fd09ac557f1e05b618476c5d6525af1a1ec60c69e6629c951d90", expires: "2026-12-31" }, // bundle_38: c2RSAd
  { hash: "5ca66339f6bab8377cd71aa930552765df3484076dee77b7ca96dc4ecd5268f6", expires: "2026-12-31" }, // bundle_39: PUudzd
  { hash: "d68ab55d88e56e8f08ac0de3d42c29783fc1cbd764e8e0a60777dfae874c9f15", expires: "2026-12-31" }, // bundle_40: ayqx9mJ
  { hash: "43ee433eb2258fdc141d9eaf22b86c5deaa3ca6560aa8fb09429fb846c4aa0ee", expires: "2026-12-31" }, // bundle_41: ClNG4oVe
  { hash: "1163b6f653c2d98e48346197c0e3bb0a76e2dd15a1c1480618a1cc93feb5d7e9", expires: "2026-12-31" }, // bundle_42: j5Wqd4
  { hash: "8ab8a9556b62a8d8904ab20d181d4aee03a670ec2cc4041f527c85260ad452f6", expires: "2026-12-31" }, // bundle_43: PiAIKP
  { hash: "d7bdad3f7ce8705de9e6ddd5f213099f2695be92ea60b67dba589150b5fa37be", expires: "2026-12-31" }, // bundle_44: ica2rOpx
  { hash: "632a3da7c4d537b87240354c9a7941731a7a1dc1e6136493519ed3722bbaf173", expires: "2026-12-31" }, // bundle_45: QyaDAVZs
  { hash: "5907ed8aa403cd105d7093506cfe88dbc4bea92b8ce82070f1b6714899d1cb52", expires: "2026-12-31" }, // bundle_46: PfzVnz
  { hash: "62f4b0596d1183f04e2ce29e6005da8f22bbfdf87ec165bed2c3cb6ab7b8e727", expires: "2026-12-31" }, // bundle_47: xtcvz7VW
  { hash: "8a1544a64535241d60de9edd415286c9c335498d58a55699e82450b64258aaf3", expires: "2026-12-31" }, // bundle_48: nCSDku
  { hash: "ec3116cfb0847a6e146b1d2317640d4011647de3ef12be924b7a40796a15e2b4", expires: "2026-12-31" }, // bundle_49: uL2FtEv
  { hash: "22dc92a9ec1ae254d929178cc20d570c93a061afe94bf528cab6a46a1fac181b", expires: "2026-12-31" }, // bundle_50: 32POVnq
];
// 到期验证函数（不需要修改）
function checkValidSlot(hash) {
  const slot = VALID_PW_HASHES.find(s => s.hash === hash);
  if (!slot) return false;
  if (!slot.expires) return true;
  const expiry = new Date(slot.expires);
  expiry.setHours(23, 59, 59, 999);
  return new Date() <= expiry;
}
const CONTACT_ID = "X79Y99";
const FREE_DAYS = 7;

// Browser-native SHA-256 (no library needed)
async function sha256(str) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,"0")).join("");
}

export default function AILearning() {
  const [store, setStore] = useState({ checkins: {}, scores: {}, answers: {}, mcq: {}, exSubmit: {}, unlocked: false });
  const [day, setDay] = useState(1);
  const [tab, setTab] = useState("theory");
  const [open, setOpen] = useState({});
  const [checks, setChecks] = useState({});
  const [texts, setTexts] = useState({});
  const [mcqSel, setMcqSel] = useState({});
  const [mcqDone, setMcqDone] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [sidebar, setSidebar] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pwShake, setPwShake] = useState(false);
  const [unlockAnim, setUnlockAnim] = useState(false);
  const [exStepOpen, setExStepOpen] = useState({});
  const [exSubmitText, setExSubmitText] = useState({});
  const contentRef = useRef(null);

  useEffect(() => { load().then(d => { setStore(d); setLoading(false); }); }, []);
  useEffect(() => { if (contentRef.current) contentRef.current.scrollTop = 0; }, [day, tab]);

  const lesson = CURRICULUM.find(c => c.day === day) || CURRICULUM[0];
  const totalDays = 30;
  const checkedCount = Object.keys(store.checkins || {}).length;
  const pct = Math.round((checkedCount / totalDays) * 100);
  const weekPct = (w) => {
    const wDays = CURRICULUM.filter(c => c.week === w);
    return Math.round(wDays.filter(c => store.checkins?.[c.day]).length / wDays.length * 100);
  };
  const isChecked = d => !!(store.checkins || {})[d];
  const isUnlocked = store.unlocked === true;

  const persist = async (newStore) => {
    setStore(newStore);
    await save(newStore);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const trySelectDay = (d) => {
    if (d <= FREE_DAYS || isUnlocked) { setDay(d); setTab("theory"); }
    else { setShowPaywall(true); }
  };

  const submitPassword = async () => {
    const inputHash = await sha256(pwInput.trim());
    if (checkValidSlot(inputHash)) {
      setUnlockAnim(true);
      await persist({ ...store, unlocked: true });
      setTimeout(() => { setShowPaywall(false); setUnlockAnim(false); setPwInput(""); }, 1200);
    } else {
      setPwError(true); setPwShake(true);
      setTimeout(() => setPwShake(false), 500);
      setTimeout(() => setPwError(false), 2000);
    }
  };

  const doCheckin = async () => {
    if (isChecked(day)) return;
    await persist({ ...store, checkins: { ...store.checkins, [day]: new Date().toLocaleDateString("zh-CN") } });
  };

  const toggleAcc = (id) => setOpen(o => ({ ...o, [id]: !o[id] }));
  const toggleStepGuide = (key) => setExStepOpen(s => ({ ...s, [key]: !s[key] }));

  const submitExercise = async (exNo) => {
    const key = `${day}_${exNo}`;
    const text = exSubmitText[key] || "";
    if (!text.trim()) return;
    const newSubmit = { ...(store.exSubmit || {}), [key]: { text, date: new Date().toLocaleDateString("zh-CN"), time: new Date().toLocaleTimeString("zh-CN",{hour:"2-digit",minute:"2-digit"}) } };
    const newChecks = { ...checks, [key]: true };
    setChecks(newChecks);
    const dayChecks = lesson.exercises.filter((_,i) => newChecks[`${day}_${i+1}`]).length;
    const ns = { ...store, exSubmit: newSubmit, scores: { ...store.scores, [day]: { ...(store.scores?.[day]||{}), exercises: dayChecks } } };
    if (dayChecks >= lesson.exercises.length && !isChecked(day)) {
      ns.checkins = { ...store.checkins, [day]: new Date().toLocaleDateString("zh-CN") };
    }
    await persist(ns);
  };

  const resetExercise = async (exNo) => {
    const key = `${day}_${exNo}`;
    const newSubmit = { ...(store.exSubmit || {}) };
    delete newSubmit[key];
    const newChecks = { ...checks, [key]: false };
    setChecks(newChecks);
    const dayChecks = lesson.exercises.filter((_,i) => newChecks[`${day}_${i+1}`]).length;
    await persist({ ...store, exSubmit: newSubmit, scores: { ...store.scores, [day]: { ...(store.scores?.[day]||{}), exercises: dayChecks } } });
  };

  const toggleCheck = async (exNo) => {
    const key = `${day}_${exNo}`;
    const newChecks = { ...checks, [key]: !checks[key] };
    setChecks(newChecks);
    const dayChecks = lesson.exercises.filter((_,i) => newChecks[`${day}_${i+1}`]).length;
    await persist({ ...store, scores: { ...store.scores, [day]: { ...(store.scores?.[day]||{}), exercises: dayChecks } } });
  };

  const exDone = (n) => !!checks[`${day}_${n}`];
  const getExSubmit = (n) => (store.exSubmit || {})[`${day}_${n}`];
  const exCount = lesson.exercises.filter((_,i) => exDone(i+1)).length;

  const weeks = [...new Set(CURRICULUM.map(c => c.week))];
  const WEEK_NAMES = {1:"第1周 · 基础入门", 2:"第2周 · 专业工具", 3:"第3周 · 进阶整合", 4:"第4周 · 商业应用", 5:"结业冲刺"};
  const WEEK_COLORS = {1:"#4f9cf9", 2:"#a78bfa", 3:"#34d399", 4:"#fb923c", 5:"#f472b6"};

  if (loading) return (
    <>
      <FontStyle />
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:"#080c14",color:"#fff",fontFamily:"Sora,sans-serif"}}>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:52,marginBottom:16}}>🤖</div>
          <div style={{fontSize:16,color:"#4a5a7a",letterSpacing:1}}>初始化学习系统...</div>
        </div>
      </div>
    </>
  );

  const accentColor = WEEK_COLORS[lesson.week] || "#4f9cf9";

  return (
    <>
      <FontStyle />
      <div style={{display:"flex",height:"100vh",fontFamily:"'Sora','PingFang SC',sans-serif",background:"#080c14",overflow:"hidden",color:"#d4dff0"}}>

        {/* ─── SIDEBAR ─────────────────────────────────────────────── */}
        <div style={{
          width: sidebar ? 272 : 0, minWidth: sidebar ? 272 : 0,
          background:"#0b1020",
          borderRight:"1px solid #121d35",
          display:"flex",flexDirection:"column",
          transition:"all 0.28s cubic-bezier(0.4,0,0.2,1)",
          overflow:"hidden",
        }}>
          {/* Brand */}
          <div style={{padding:"22px 18px 14px",borderBottom:"1px solid #121d35"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:34,height:34,borderRadius:10,background:"linear-gradient(135deg,#1a3a6a,#2a5298)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🤖</div>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:"#e8f0fe",letterSpacing:-0.3}}>AI 学习计划</div>
                <div style={{fontSize:10,color:"#3a4a6a",marginTop:1}}>30天 · 系统成长路径</div>
              </div>
            </div>
            {/* Overall progress */}
            <div style={{background:"#0f1629",borderRadius:10,padding:"10px 12px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{fontSize:11,color:"#4a5a7a"}}>总进度</span>
                <span style={{fontSize:12,fontWeight:700,color:"#4f9cf9"}}>{checkedCount}/{totalDays}</span>
              </div>
              <div style={{height:5,background:"#121d35",borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${pct}%`,background:"linear-gradient(90deg,#2a5298,#4f9cf9)",borderRadius:3,transition:"width 0.5s"}} />
              </div>
              <div style={{fontSize:10,color:"#2a3a5a",marginTop:4}}>{pct}% 完成</div>
            </div>
          </div>

          {/* Day list */}
          <div style={{flex:1,overflowY:"auto",padding:"6px 0 12px"}}>
            {weeks.map(w => {
              const wDays = CURRICULUM.filter(c => c.week === w);
              const wp = weekPct(w);
              return (
                <div key={w}>
                  <div style={{padding:"12px 18px 6px",display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:10,fontWeight:700,color:WEEK_COLORS[w],letterSpacing:0.8}}>{WEEK_NAMES[w]||`第${w}周`}</span>
                    <div style={{flex:1,height:2,background:"#121d35",borderRadius:1}}>
                      <div style={{height:"100%",width:`${wp}%`,background:WEEK_COLORS[w],borderRadius:1,opacity:0.6,transition:"width 0.4s"}} />
                    </div>
                    <span style={{fontSize:10,color:"#2a3a5a"}}>{wp}%</span>
                  </div>
                  {wDays.map(c => {
                    const isActive = c.day === day;
                    const done = isChecked(c.day);
                    return (
                      <div key={c.day} onClick={() => trySelectDay(c.day)}
                        style={{
                          display:"flex",alignItems:"center",gap:9,padding:"8px 18px",cursor:"pointer",
                          background: isActive ? `rgba(${w===1?'79,156,249':w===2?'167,139,250':w===3?'52,211,153':w===4?'251,146,60':'244,114,182'},0.1)` : "transparent",
                          borderLeft: isActive ? `2px solid ${WEEK_COLORS[w]}` : "2px solid transparent",
                          transition:"all 0.15s",
                          opacity: (c.day > FREE_DAYS && !isUnlocked) ? 0.55 : 1,
                        }}>
                        <span style={{fontSize:15,flexShrink:0}}>
                          {done ? "✅" : (c.day > FREE_DAYS && !isUnlocked) ? "🔒" : c.emoji}
                        </span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:isActive?700:400,color:isActive?"#e8f0fe":"#5a6a8a",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>
                            Day {c.day} · {c.title}
                          </div>
                        </div>
                        {(c.day > FREE_DAYS && !isUnlocked) && (
                          <span style={{fontSize:9,color:"#fb923c",fontWeight:700,background:"#1a0f00",border:"1px solid #3a2000",borderRadius:4,padding:"1px 5px",flexShrink:0}}>付费</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── MAIN ────────────────────────────────────────────────── */}
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Header */}
          <div style={{background:"#0b1020",borderBottom:"1px solid #121d35",padding:"14px 24px",flexShrink:0}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
              <button onClick={() => setSidebar(s=>!s)} style={{background:"none",border:"none",color:"#3a4a6a",cursor:"pointer",fontSize:20,padding:"2px 4px",marginTop:2,flexShrink:0}}>☰</button>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:26}}>{lesson.emoji}</span>
                  <div>
                    <div style={{fontSize:17,fontWeight:800,color:"#e8f0fe",lineHeight:1.3}}>Day {lesson.day} · {lesson.title}</div>
                    <div style={{display:"flex",gap:6,marginTop:5,flexWrap:"wrap"}}>
                      {lesson.tags.map(t => (
                        <span key={t} className="tag-chip" style={{background:`${accentColor}18`,color:accentColor,border:`1px solid ${accentColor}30`}}>{t}</span>
                      ))}
                      {isChecked(day) && <span className="tag-chip" style={{background:"#0f2a1a",color:"#22c55e",border:"1px solid #1a4a2a"}}>✓ 已打卡 {store.checkins[day]}</span>}
                    </div>
                  </div>
                </div>
              </div>
              {/* Exercise counter */}
              <div style={{textAlign:"center",background:"#0f1629",border:"1px solid #1a2444",borderRadius:10,padding:"8px 14px",flexShrink:0}}>
                <div style={{fontSize:20,fontWeight:800,color:exCount===5?"#22c55e":accentColor}}>{exCount}/5</div>
                <div style={{fontSize:10,color:"#3a4a6a",marginTop:2}}>实操完成</div>
              </div>
              {/* Checkin */}
              <button className="btn-primary" onClick={doCheckin} disabled={isChecked(day)} style={{flexShrink:0,background:isChecked(day)?"#0f2a1a":undefined,color:isChecked(day)?"#22c55e":undefined}}>
                {isChecked(day) ? "✅ 已打卡" : "📅 今日打卡"}
              </button>
            </div>
            {/* Tabs */}
            <div style={{display:"flex",gap:2,marginTop:14,borderBottom:"1px solid #121d35",paddingBottom:0}}>
              {[{k:"theory",l:"📖 理论学习"},{k:"resources",l:"🔗 资源推荐"},{k:"practice",l:"✏️ 实操练习"}].map(tb => (
                <button key={tb.k} onClick={() => setTab(tb.k)} style={{
                  background:"none",border:"none",borderBottom: tab===tb.k ? `2px solid ${accentColor}` : "2px solid transparent",
                  color: tab===tb.k ? accentColor : "#3a4a6a",
                  padding:"8px 18px",cursor:"pointer",fontSize:13,fontWeight:tab===tb.k?700:500,
                  transition:"all 0.15s",fontFamily:"inherit",
                }}>{tb.l}</button>
              ))}
              {saved && <span style={{marginLeft:"auto",alignSelf:"center",color:"#22c55e",fontSize:12,fontFamily:"'JetBrains Mono',monospace"}}>✓ saved</span>}
            </div>
          </div>

          {/* Content */}
          <div ref={contentRef} style={{flex:1,overflowY:"auto",padding:"24px",background:"#080c14"}}>
            <div style={{maxWidth:820,margin:"0 auto"}} className="fade-in">

              {/* ── THEORY ── */}
              {tab === "theory" && (
                <div>
                  {lesson.theory.map((sec, si) => {
                    const isOpen = !!open[sec.id];
                    return (
                      <div key={sec.id} style={{marginBottom:12,borderRadius:14,background:"#0b1020",border:`1px solid ${isOpen ? accentColor+'40' : '#121d35'}`,overflow:"hidden",transition:"border-color 0.2s"}}>
                        <div onClick={() => toggleAcc(sec.id)} style={{padding:"16px 20px",display:"flex",alignItems:"center",gap:12,cursor:"pointer",userSelect:"none"}}>
                          <div style={{width:26,height:26,borderRadius:7,background:`${accentColor}18`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            <span style={{fontSize:11,fontWeight:800,color:accentColor,fontFamily:"'JetBrains Mono',monospace"}}>§{si+1}</span>
                          </div>
                          <span style={{fontWeight:700,fontSize:14,color:"#c8d8f0",flex:1}}>{sec.title}</span>
                          <span style={{color:"#3a4a6a",fontSize:12,transform:isOpen?"rotate(180deg)":"none",transition:"transform 0.25s"}}>▼</span>
                        </div>
                        <div className={`acc-body ${isOpen?"open":""}`}>
                          <div style={{padding:"0 20px 20px"}}>
                            <div style={{height:1,background:"#121d35",marginBottom:16}} />
                            {sec.content.split("\n").map((line, li) => {
                              if (!line.trim()) return <div key={li} style={{height:8}} />;
                              const isHeader = line.startsWith("【") || line.startsWith("①") || line.startsWith("②") || line.startsWith("③") || line.startsWith("④") || line.startsWith("⑤") || line.startsWith("⑥") || line.startsWith("⑦");
                              const isBullet = line.startsWith("•") || line.startsWith("✅") || line.startsWith("❌") || line.startsWith("→");
                              const isCode = line.startsWith("  ") && (line.includes("→") || line.includes(":"));
                              return (
                                <div key={li} style={{
                                  fontSize:13,lineHeight:1.85,marginBottom:2,
                                  color: isHeader ? accentColor : isBullet ? "#8aa8c8" : "#8090a8",
                                  fontWeight: isHeader ? 700 : 400,
                                  paddingLeft: isBullet ? 4 : 0,
                                  fontFamily: isCode ? "'JetBrains Mono',monospace" : "inherit",
                                  background: isCode ? "#0f1629" : "none",
                                  padding: isCode ? "2px 8px" : (isBullet ? "0 0 0 4px" : 0),
                                  borderRadius: isCode ? 4 : 0,
                                }}>
                                  {line}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        {!isOpen && (
                          <div style={{padding:"0 20px 14px",fontSize:12,color:"#2a3a5a",lineHeight:1.5,overflow:"hidden",maxHeight:36,textOverflow:"ellipsis"}}>
                            {sec.content.replace(/\n/g," ").slice(0,120)}…
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{textAlign:"center",padding:"20px 0",color:"#2a3a5a",fontSize:13}}>
                    全部{lesson.theory.length}个章节 · 学完后前往「实操练习」完成打卡 →
                  </div>
                </div>
              )}

              {/* ── RESOURCES ── */}
              {tab === "resources" && (
                <div>
                  {lesson.resources.map((rg, ri) => (
                    <div key={ri} style={{marginBottom:20}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                        <div style={{height:1,flex:1,background:"#121d35"}} />
                        <span style={{fontSize:11,fontWeight:700,color:"#3a4a6a",letterSpacing:0.8}}>
                          {rg.cn === true ? "🇨🇳 国内资源" : "🌐 国际资源"}
                        </span>
                        <div style={{height:1,flex:1,background:"#121d35"}} />
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:10}}>
                        {(rg.items||[]).map((item,ii) => (
                          <a key={ii} href={item.url} target="_blank" rel="noreferrer" style={{textDecoration:"none"}}>
                            <div className="card-hover" style={{background:"#0b1020",borderRadius:12,border:"1px solid #121d35",padding:"13px 16px",display:"flex",gap:12,alignItems:"flex-start",cursor:"pointer"}}>
                              <div style={{width:8,height:8,borderRadius:"50%",background:rg.cn===true?"#4f9cf9":"#a78bfa",flexShrink:0,marginTop:5}} />
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,fontWeight:600,color:"#c8d8f0",marginBottom:3}}>{item.label}</div>
                                <div style={{fontSize:11,color:"#3a4a6a",lineHeight:1.5}}>{item.desc}</div>
                              </div>
                              <span style={{color:"#2a3a5a",fontSize:14,flexShrink:0}}>→</span>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── PRACTICE ── */}
              {tab === "practice" && (
                <div>
                  {/* Progress */}
                  <div style={{background:"#0b1020",borderRadius:12,border:"1px solid #121d35",padding:"14px 18px",marginBottom:18}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <span style={{fontSize:13,fontWeight:700,color:"#8090a8"}}>今日实操进度</span>
                      <span style={{fontSize:13,fontWeight:800,color:exCount===lesson.exercises.length?"#22c55e":accentColor}}>{exCount} / {lesson.exercises.length} 完成</span>
                    </div>
                    <div style={{height:6,background:"#121d35",borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${(exCount/lesson.exercises.length)*100}%`,background:`linear-gradient(90deg,${accentColor}80,${accentColor})`,borderRadius:3,transition:"width 0.4s"}} />
                    </div>
                    <div style={{display:"flex",gap:6,marginTop:10}}>
                      {lesson.exercises.map((_,i) => (
                        <div key={i} style={{flex:1,height:3,borderRadius:2,background:exDone(i+1)?accentColor:"#121d35",transition:"background 0.3s"}} />
                      ))}
                    </div>
                    <div style={{fontSize:11,color:"#2a3a5a",marginTop:8}}>💡 完成并提交作品后，系统自动打卡记录学习进度</div>
                  </div>

                  {/* Exercise cards */}
                  {lesson.exercises.map((ex) => {
                    const done = exDone(ex.no);
                    const stepKey = `${day}_${ex.no}`;
                    const isStepOpen = !!exStepOpen[stepKey];
                    const submitted = getExSubmit(ex.no);
                    const draftText = exSubmitText[stepKey] || "";
                    return (
                      <div key={ex.no} style={{
                        background:"#0b1020", borderRadius:16,
                        border:`1px solid ${done ? accentColor+"50" : "#121d35"}`,
                        marginBottom:14, overflow:"hidden",
                        boxShadow: done ? `0 0 18px ${accentColor}06` : "none",
                        transition:"border-color 0.25s",
                      }}>
                        {/* Header */}
                        <div style={{padding:"16px 20px",display:"flex",gap:14,alignItems:"flex-start"}}>
                          <div style={{
                            width:34,height:34,borderRadius:10,flexShrink:0,marginTop:1,
                            background: done ? "#0a2018" : "#0f1629",
                            border:`2px solid ${done ? "#22c55e40" : "#1a2444"}`,
                            display:"flex",alignItems:"center",justifyContent:"center",
                          }}>
                            {done
                              ? <span style={{fontSize:16}}>✅</span>
                              : <span style={{fontSize:12,fontWeight:800,color:"#3a4a6a",fontFamily:"'JetBrains Mono',monospace"}}>{String(ex.no).padStart(2,"0")}</span>
                            }
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                              <span style={{fontSize:11,fontWeight:800,color:done?"#22c55e":accentColor,fontFamily:"'JetBrains Mono',monospace"}}>练习 {ex.no}</span>
                              <span style={{fontSize:14,fontWeight:700,color:done?"#a8e8c8":"#c8d8f0"}}>{ex.title}</span>
                              {done && <span className="tag-chip" style={{background:"#0f2a1a",color:"#22c55e",border:"1px solid #1a4a2a",marginLeft:"auto"}}>✓ 已提交</span>}
                            </div>
                            <div style={{fontSize:13,color:"#5a6a8a",lineHeight:1.85}}>{ex.desc}</div>
                            {ex.steps && ex.steps.length > 0 && (
                              <button onClick={() => toggleStepGuide(stepKey)} style={{
                                marginTop:12,background:"none",
                                border:`1px solid ${isStepOpen ? accentColor+"50" : "#1a2a44"}`,
                                color:isStepOpen?accentColor:"#4a5a7a",borderRadius:8,padding:"7px 16px",
                                fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
                                transition:"all 0.2s",display:"inline-flex",alignItems:"center",gap:6,
                              }}>
                                <span style={{transform:isStepOpen?"rotate(90deg)":"none",transition:"transform 0.2s",display:"inline-block"}}>▶</span>
                                {isStepOpen ? "收起操作指南" : `📋 展开操作指南（${ex.steps.length}步）`}
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Step guide */}
                        {ex.steps && ex.steps.length > 0 && (
                          <div className={`step-guide${isStepOpen?" open":""}`}>
                            <div style={{padding:"0 20px 16px",borderTop:"1px solid #0f1629"}}>
                              <div style={{fontSize:11,fontWeight:700,color:"#3a4a6a",letterSpacing:1,padding:"12px 0 8px"}}>── 详细操作步骤</div>
                              {ex.steps.map((step, si) => (
                                <div key={si} className="step-item">
                                  <div style={{
                                    width:28,height:28,borderRadius:8,flexShrink:0,
                                    background:`${accentColor}15`,border:`1px solid ${accentColor}30`,
                                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,
                                  }}>{step.s}</div>
                                  <div style={{flex:1}}>
                                    <div style={{fontSize:11,fontWeight:700,color:"#3a5a7a",marginBottom:3}}>步骤 {si+1}</div>
                                    <div style={{fontSize:13,color:"#8090a8",lineHeight:1.8}}>{step.t}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Submit area */}
                        <div style={{padding:"0 20px 18px"}}>
                          {done && submitted ? (
                            <div style={{background:"#0a1a10",border:"1px solid #1a3a22",borderRadius:12,padding:"14px 16px"}}>
                              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                                <div style={{fontSize:12,fontWeight:700,color:"#22c55e"}}>✅ 已提交 · {submitted.date} {submitted.time}</div>
                                <button onClick={() => resetExercise(ex.no)} style={{background:"none",border:"1px solid #1a3a22",color:"#3a6a4a",borderRadius:6,padding:"3px 10px",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>重新提交</button>
                              </div>
                              {ex.submitHint && <div style={{fontSize:11,color:"#2a5a3a",marginBottom:6}}>📌 {ex.submitHint}</div>}
                              <div style={{fontSize:12,color:"#5a8a6a",lineHeight:1.75,whiteSpace:"pre-wrap"}}>{submitted.text}</div>
                            </div>
                          ) : (
                            <div style={{background:"#080c14",border:"1px solid #121d35",borderRadius:12,padding:"14px 16px"}}>
                              {ex.submitHint && (
                                <div style={{fontSize:11,color:"#3a5a7a",marginBottom:10,padding:"6px 10px",background:"#0a1020",borderRadius:6,borderLeft:`2px solid ${accentColor}`}}>
                                  📌 提交要求：{ex.submitHint}
                                </div>
                              )}
                              <div style={{fontSize:11,color:"#3a4a6a",marginBottom:6,fontWeight:600}}>📝 描述完成情况 / 贴上作品链接或截图描述</div>
                              <textarea className="submit-area" rows={3}
                                placeholder="例如：我完成了...，收获是...，遇到的问题是...（也可以粘贴链接）"
                                value={draftText}
                                onChange={e => setExSubmitText(t => ({...t,[stepKey]:e.target.value}))}
                              />
                              <div style={{display:"flex",alignItems:"center",gap:10,marginTop:10}}>
                                <button
                                  onClick={() => submitExercise(ex.no)}
                                  disabled={!draftText.trim()}
                                  style={{
                                    background:draftText.trim()?`linear-gradient(135deg,${accentColor}cc,${accentColor})`:"#1a2444",
                                    color:draftText.trim()?"#fff":"#3a4a6a",border:"none",borderRadius:8,
                                    padding:"9px 22px",fontSize:13,fontWeight:700,
                                    cursor:draftText.trim()?"pointer":"not-allowed",
                                    fontFamily:"inherit",transition:"all 0.2s",
                                  }}>
                                  🚀 提交作品
                                </button>
                                {!draftText.trim() && <span style={{fontSize:11,color:"#2a3a5a"}}>填写完成情况后提交</span>}
                                {exCount === lesson.exercises.length - 1 && draftText.trim() && (
                                  <span style={{fontSize:11,color:"#22c55e",fontWeight:700}}>🎉 提交后将自动打卡！</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Checkin status */}
                  <div style={{
                    borderRadius:14,border:`2px dashed ${isChecked(day)?"#22c55e40":"#1a2444"}`,
                    background: isChecked(day) ? "#0a1e10" : "#0b1020",
                    padding:"20px",textAlign:"center",marginTop:8,
                  }}>
                    {isChecked(day) ? (
                      <div>
                        <div style={{fontSize:28,marginBottom:6}}>🎉</div>
                        <div style={{color:"#22c55e",fontSize:15,fontWeight:800,marginBottom:4}}>{store.checkins[day]} · Day {day} 打卡成功！</div>
                        <div style={{fontSize:12,color:"#3a6a4a"}}>完成了 {exCount}/{lesson.exercises.length} 个练习</div>
                      </div>
                    ) : (
                      <div>
                        <div style={{fontSize:28,marginBottom:8}}>📋</div>
                        <div style={{color:"#4a5a7a",fontSize:13,marginBottom:4}}>提交所有练习作品后将自动完成打卡</div>
                        <div style={{fontSize:11,color:"#2a3a5a"}}>还剩 {lesson.exercises.length - exCount} 个练习待提交</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
        {/* ── PAYWALL MODAL ── */}
        {showPaywall && (
          <div
            onClick={e => { if (e.target === e.currentTarget) setShowPaywall(false); }}
            style={{
              position:"fixed",inset:0,background:"rgba(4,8,20,0.92)",zIndex:9999,
              display:"flex",alignItems:"center",justifyContent:"center",
              backdropFilter:"blur(8px)",
            }}>
            <div className={unlockAnim ? "unlock-pop" : ""} style={{
              background:"#0b1020",border:"1px solid #1a2a44",borderRadius:20,
              padding:"36px 32px",maxWidth:420,width:"90%",
              boxShadow:"0 24px 80px rgba(0,0,0,0.6)",
            }}>
              {unlockAnim ? (
                <div style={{textAlign:"center",padding:"20px 0"}}>
                  <div style={{fontSize:52,marginBottom:12}}>🎉</div>
                  <div style={{fontSize:20,fontWeight:800,color:"#22c55e",marginBottom:6}}>解锁成功！</div>
                  <div style={{fontSize:13,color:"#3a6a4a"}}>全部30天课程已为你开放</div>
                </div>
              ) : (
                <div>
                  <div style={{textAlign:"center",marginBottom:20}}>
                    <div style={{
                      width:60,height:60,borderRadius:18,margin:"0 auto 12px",
                      background:"#1a1a0a",border:"1.5px solid #3a3010",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,
                    }}>🔐</div>
                    <div style={{fontSize:18,fontWeight:800,color:"#e8f0fe",marginBottom:8}}>Day 8-30 付费课程</div>
                    {/* Price badge */}
                    <div style={{
                      display:"inline-flex",alignItems:"baseline",gap:4,
                      background:"linear-gradient(135deg,#1a0f00,#2a1800)",
                      border:"1.5px solid #fb923c50",borderRadius:12,
                      padding:"8px 20px",marginBottom:10,
                    }}>
                      <span style={{fontSize:13,color:"#fb923c",fontWeight:600}}>¥</span>
                      <span style={{fontSize:32,fontWeight:900,color:"#fb923c",letterSpacing:-1,lineHeight:1}}>9.9</span>
                      <span style={{fontSize:12,color:"#a06030",marginLeft:2}}>解锁全部</span>
                    </div>
                    <div style={{fontSize:13,color:"#4a5a7a",lineHeight:1.7}}>
                      Day 1-7 免费试学 · Day 8-30 完整内容<br/>
                      付款后联系获取专属解锁密令 🔑
                    </div>
                  </div>

                  <div style={{
                    background:"#0a1020",border:"1px solid #1a2a44",borderRadius:12,
                    padding:"14px 18px",marginBottom:20,
                  }}>
                    <div style={{fontSize:11,color:"#3a4a6a",fontWeight:700,letterSpacing:0.8,marginBottom:8}}>📨 联系方式获取密令</div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{
                        background:"#1a2a4a",border:"1px solid #2a4a8a",
                        borderRadius:10,padding:"10px 16px",flex:1,
                      }}>
                        <div style={{fontSize:11,color:"#4a6aaa",marginBottom:2}}>联系账号</div>
                        <div style={{fontSize:16,fontWeight:800,color:"#4f9cf9",fontFamily:"'JetBrains Mono',monospace",letterSpacing:1}}>
                          {CONTACT_ID}
                        </div>
                      </div>
                      <button
                        onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(CONTACT_ID); }}
                        style={{background:"none",border:"1px solid #1a2a44",color:"#3a5a8a",borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                        复制
                      </button>
                    </div>
                    <div style={{fontSize:11,color:"#2a3a5a",marginTop:8,lineHeight:1.6}}>
                      告知对方你已学完 Day 1-7，索取解锁密令 🔑
                    </div>
                  </div>

                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:11,color:"#3a4a6a",fontWeight:700,marginBottom:8}}>🔑 已有密令？输入解锁</div>
                    <input
                      className={`pw-input${pwError ? " error" : ""}${pwShake ? " lock-shake" : ""}`}
                      type="password"
                      placeholder="输入密令..."
                      value={pwInput}
                      onChange={e => { setPwInput(e.target.value); setPwError(false); }}
                      onKeyDown={e => { if (e.key === "Enter") submitPassword(); }}
                    />
                    {pwError && (
                      <div style={{fontSize:12,color:"#ef4444",marginTop:6}}>✕ 密令不正确，请检查后重试</div>
                    )}
                  </div>

                  <div style={{display:"flex",gap:10}}>
                    <button
                      onClick={submitPassword}
                      style={{
                        flex:1,background:pwInput?"linear-gradient(135deg,#2a5298,#4f9cf9)":"#1a2444",
                        color:pwInput?"#fff":"#3a4a6a",border:"none",borderRadius:10,
                        padding:"12px",fontSize:14,fontWeight:700,
                        cursor:pwInput?"pointer":"not-allowed",
                        fontFamily:"inherit",transition:"all 0.2s",
                      }}>
                      解锁课程
                    </button>
                    <button
                      onClick={() => setShowPaywall(false)}
                      style={{background:"none",border:"1px solid #1a2444",color:"#3a4a6a",borderRadius:10,padding:"12px 18px",fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>
                      关闭
                    </button>
                  </div>

                  <div style={{textAlign:"center",marginTop:14,fontSize:11,color:"#2a3a5a"}}>
                    解锁后可访问 Day 8 - Day 30 全部内容
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  );
}
